"use client";

import { receivePlaybackState } from "@/lib/realtime-playback";

export const dynamic = "force-dynamic";

/* ===== easy-tedu · 컨트롤러 (dark mobile control panel) =====
 * 디자인 핸드오프(claude.ai/design) 구현 — 실제 백엔드(API + socket.io) 배선.
 * 플로우: 도장 선택 → 기기 대시보드 → 제어/미러(미디어·콘솔·이머시브) → 영상 → 재생목록 → 페어링 → 설정
 */
import {
    useCallback,
    useEffect,
    useMemo,
    useRef,
    useState,
    type ReactNode,
} from "react";
import { io, type Socket } from "socket.io-client";
import { startVideoAccessKeeper } from "@/lib/video-access";
import { useLocale, useTranslations } from "next-intl";
import { tkdLabel, tkdForm, tkdPlaylist } from "@/lib/tkd-terms";
import { parseVoiceCommand } from "@/lib/voice-command";
import { VoiceSheet, type VoiceOutcome } from "./voice-sheet";
import { authClient } from "@/lib/auth-client";
import type {
    Command,
    CommandResult,
    DeviceState,
    MasterPlayVideo,
    DrillPlayOpt,
} from "@/lib/realtime-types";
import { callH264Off } from "@/lib/call-frame";
import { RANK_BASICS } from "@/lib/rank-limit";
import { platformFromUA, remoteLaunchCopy, skipsCodecPreference, type DevicePlatform } from "@/lib/device-platform";
import { realtimeUrl } from "@/lib/realtime-url";

import "./tedu.css";
import { I } from "./icons";
import {
    DataProvider,
    Toast,
    TabBar,
    ActionSheet,
    NoticeSheet,
    PromptSheet,
    ConfirmSheet,
    PhoneSheet,
    type TabId,
    type ModalAction,
    type ModalActionSection,
} from "./ui";
import {
    type DojangView,
    type VideoView,
    type PlaylistView,
    type PlaylistRaw,
    type DeviceView,
    type MasterQueue,
    type RecentCast,
    defaultDeviceView,
    mergeState,
    canViewCamera,
    atDojang,
    interpPosition,
    normalizePlaylist,
    masterToVideoView,
    toMasterPayload,
    isReferenceVideo,
    fmt,
    type StudentView,
    type ClassView,
} from "./types";
import { isBasicBreakdownSegment } from "@/lib/ref-segment";
import {
    DojangScreen,
    DashboardScreen,
    HomeworkScreen,
    SettingsScreen,
    type Owner,
} from "./screens";
import { diag, diagOn } from "@/lib/diag";
import { setFrame, clearFrame } from "./frame-store";
import { DiagOverlay } from "@/components/diag-overlay";
import {
    LibraryScreen,
    UploadScreen,
    PlaylistDetail,
    FormDrillScreen,
    PairingScreen,
    LoadSheet,
    RangeSheet,
    PlaylistPickerSheet,
    AddVideoSheet,
    type ActiveUpload,
} from "./media-screens";
import { ControlScreen } from "./control-screen";
import { CameraViewer } from "@/components/camera-viewer";
import { PresenterCast } from "@/components/presenter-cast";
import { type MasterForm } from "./setlist-screen";
import { LocalPlayer } from "./local-player";
import { TrainingHistoryScreen } from "./training-screen";
import { HomeDevicesScreen, lastSeenText } from "./home-devices-screen";
import { ClassesScreen } from "./classes-screen";
import { HomeLinkScreen, homeLinkUrl, copyText, shareLink, canShare } from "./home-link-screen";
import { MembersScreen } from "./members-screen";
import { AccountScreen } from "./account-screen";
import { TourOverlay } from "@/components/tour";
import { CONTROL_TOUR } from "./tour-steps";
import { prefetchSet, type SetItem, type SetPlan } from "@/lib/mirror-cache";
import { DEMO_COURSES, type DemoCourse } from "./demo-courses";
import { perf } from "@/lib/perf";
import { monoNow } from "./mono-clock";

// 관리 앱이 새로 열려도 카메라 창을 이어 붙이는 기록(아래 '관리 앱이 새로 열려도' 주석) — 탭 단위(sessionStorage).
const CAM_RESUME_KEY = "easy-tedu-cam-resume";
const CAM_RESUME_MS = 2 * 60_000;

type DeviceRecord = {
    id: string;
    name: string;
    deviceName?: string;
    student?: { id: string; name: string; homeworkCompleted?: number } | null;
    studentId?: string | null;
    homeworkCompleted?: number;
    lastSeen: string | null;
    touchLocked?: boolean;
    atHome?: boolean;
    rankFormId?: string | null;
    homePlaylistId?: string | null;
    homeworkDoneAt?: string | null;
    hasPushToken?: boolean;
    platform?: DevicePlatform | null;
    cameraOk?: boolean | null;
    fromStore?: boolean | null;
};
/** '(으)로' 조사 — 받침이 없거나 ㄹ 받침이면 '로', 그 밖의 받침이면 '으로'. 영문·숫자로 끝나면 읽는 소리로, 그 밖의 글자는 '(으)로'. */
function josaRo(word: string): string {
    const c = word.trim().charCodeAt(word.trim().length - 1);
    const ch = word.trim().slice(-1).toUpperCase();
    if (/[0-9A-Z]/.test(ch)) return "036MN".includes(ch) ? "으로" : "로"; // 영·삼·육·엠·엔만 ㄹ 아닌 받침('초등 A로'·'3반' 아님 '3으로')
    if (!(c >= 0xac00 && c <= 0xd7a3)) return "(으)로";
    const jong = (c - 0xac00) % 28;
    return jong === 0 || jong === 8 ? "로" : "으로";
}
type Screen =
    | "dojang"
    | "main"
    | "control"
    | "upload"
    | "pairing"
    | "pldetail"
    | "formdrill"
    | "training"
    | "homeDevices"
    | "members"
    | "account"
    | "setlist"
    | "classes" // 반 나누기(설정 > 수련생과 홈 트레이닝 기기, 2026-10-07)
    | "homeLink"; // 수련생 링크 만들기(홈 트레이닝 등록의 링크 방식, 2026-09-10)
type Modal =
    | {
          kind: "action";
          title: string;
          subtitle?: string;
          actions: ModalAction[];
          sections?: ModalActionSection[];
      }
    | {
          kind: "prompt";
          title: string;
          label?: string;
          placeholder?: string;
          initial?: string;
          confirmLabel?: string;
          password?: boolean;
          danger?: boolean;
          onSubmit: (v: string) => void;
      }
    | {
          kind: "confirm";
          title: string;
          message?: string;
          confirmLabel?: string;
          danger?: boolean; // 미지정 시 ConfirmSheet 기본(빨강) — 파괴적이지 않은 확인은 false 로 파랑
          onConfirm: () => void;
      }
    | {
          kind: "notice"; // 고를 게 없는 안내 — 확인 하나로 닫는다
          title: string;
          message?: string;
      }
    | {
          kind: "phone"; // 연락처 등록/변경 — 번호 입력 → 문자 인증 2단계(PhoneSheet 가 자체 처리)
          initial: string | null;
      };

/* 이 **탭**의 이름표 — 재연결·새로고침해도 그대로다(sessionStorage 는 탭 단위·새 탭이면 새 값).
   제어화면 점유(sessionHold)를 서버가 이걸로 알아본다. 없으면 끊겼다 붙은 내 화면이 **직전의 나**
   (서버에 아직 남아 있는 죽은 소켓)한테 막힌다 — 서버가 그 소켓의 죽음을 알아채는 데 최대 45초. */
let clientIdMem = "";
function clientId(): string {
    if (clientIdMem) return clientIdMem;
    const gen = () => `c${Date.now().toString(36)}${Math.random().toString(36).slice(2, 10)}`;
    try {
        const k = "tedu.cid";
        clientIdMem = sessionStorage.getItem(k) ?? "";
        if (!clientIdMem) sessionStorage.setItem(k, (clientIdMem = gen()));
    } catch {
        clientIdMem = gen(); // 저장을 막아둔 브라우저(사생활 보호 모드) — 이 페이지가 살아 있는 동안만 유지
    }
    return clientIdMem;
}

// 기기 상태 스냅샷 — 온라인이면 재생상태(playing/paused/idle), 오프라인이면 "offline".
function snapshotOf(list: DeviceView[]) {
    return list.map((d) => ({
        id: d.id,
        name: d.name,
        status: d.online ? d.status : "offline",
    }));
}
// 서버가 돌려준 진행 중 세션 목록 → { deviceId: 시작시각ms } 맵.
function toActiveMap(open: unknown): Record<string, number> {
    const m: Record<string, number> = {};
    if (Array.isArray(open))
        for (const s of open)
            if (s?.deviceId && s?.startedAt)
                m[s.deviceId] = new Date(s.startedAt).getTime();
    return m;
}
// 서버 동기화 결과를 현재 맵과 병합.
//  · 이미 화면에 표시 중인 세션은 로컬 시작시각을 유지한다. 서버 startedAt 은 sync 디바운스/왕복 지연만큼
//    더 과거일 수 있어 즉시 채택하면 타이머가 7~9초 앞으로 튄다.
//  · 로컬에 없는 세션만 서버 startedAt 으로 복원한다(재진입/다른 컨트롤러가 시작한 세션).
//  · 서버엔 아직 없지만 '지금 재생/일시정지 중'인 기기(keepIds): 낙관적 세션 유지. 서버는 재생 status 를
//    4초 디바운스로 늦게(때론 짧은 클립 연속재생 땐 못) 기록하므로, 그 사이/그동안 타이머가 끊기지 않게 한다.
//  · 위 둘 다 아닌 기기(대기·오프라인이고 서버에도 없음): 끝난 것 → 제거(수업종료·타 컨트롤러 종료·reap 반영).
function reconcileActiveMap(
    prev: Record<string, number>,
    server: Record<string, number>,
    keepIds: Set<string>,
): Record<string, number> {
    const next: Record<string, number> = {};
    for (const id of Object.keys(server))
        next[id] = prev[id] != null ? prev[id] : server[id];
    for (const id of keepIds)
        if (prev[id] != null && next[id] == null) next[id] = prev[id];
    return next;
}
// 낙관적 세션 유지 대상 — '재생 중'인 기기만. 일시정지/정지는 서버 판단을 따른다:
// 서버가 세션을 갖고 있으면 reconcile 이 그대로 유지하고, 30분 비활성으로 서버가 자동마감(reap)하면
// 서버 목록에서 빠져 화면 타이머도 함께 사라진다(라이브-기록 일관성). 재생 중인 기기만 서버 미기록(첫 4초·
// 짧은 클립 연속재생)이어도 낙관적으로 유지한다.
function activeDeviceIds(list: DeviceView[]): Set<string> {
    return new Set(
        list.filter((d) => d.online && d.status === "playing").map((d) => d.id),
    );
}

export default function ControlPage() {
    const { data: session, isPending } = authClient.useSession();
    const tp = useTranslations("control.page");
    const tc = useTranslations("control");
    const tScreen = useTranslations("control.screen");
    const tHome = useTranslations("control.homeDevices"); // 집 화면 고르기(어느 화면?)의 켜짐·마지막 접속 문구
    const tk = useTranslations("control.classes"); // 반 나누기·반 옮기기
    const th = useTranslations("control.homework");
    const tl = useTranslations("control.media.homeLink"); // 수련생 링크(복사·공유·새로 만들기)
    const tMedia = useTranslations("control.media"); // 재생목록 ⋯ 의 '이 목록 받아두기' 문구(offline.*)
    const locale = useLocale();

    // 로그인 안 됐으면 별도 인트로 화면 없이 바로 로그인으로 (인트로 문구는 /login 에 통합).
    // 로그인 후 nextTarget 이 이 경로로 되돌려보냄.
    useEffect(() => {
        if (!isPending && !session) {
            window.location.replace(
                `/login?next=${encodeURIComponent(window.location.pathname)}`,
            );
        }
    }, [isPending, session]);

    const [owner, setOwner] = useState<Owner>({ name: "", email: "", phone: null });
    const [dojangs, setDojangs] = useState<DojangView[]>([]);
    const [dojang, setDojang] = useState<DojangView | null>(null);

    const [screen, setScreen] = useState<Screen>("dojang");

    // 홈워킹 탭의 '집 화면 등록'으로 들어왔나 — 등록 화면이 집 토글을 켠 채 열린다(09-03 사용자).

    const [tab, setTab] = useState<TabId>("devices");
    // 사용법 둘러보기 — 설정에서 누를 때마다 처음 칸부터(박람회 = 방문자마다 새로).
    const [tourOn, setTourOn] = useState(false);
    // 둘러보기를 끝까지 본 사람에게 건네는 마무리 한마디. 아래 알약 토스트로는 부스에서 눈에 안 든다
    // → 화면 위쪽에 크게 띄운다(사용자 결정, 2026-08-06).
    const [tourEnd, setTourEnd] = useState(false);
    // 자동 시연 — 설정에서 코스를 고르면 그 코스가 사람 없이 저 혼자 한 바퀴 돈다(박람회 부스).
    const [demo, setDemo] = useState<DemoCourse | null>(null);
    useEffect(() => {
        if (!tourEnd) return;
        const t = setTimeout(() => setTourEnd(false), 3400);
        return () => clearTimeout(t);
    }, [tourEnd]);
    // 가로 태블릿(폭≥760 & 높이≥500)에서만 2단(기기 그리드 | 제어) 레이아웃 — 폰 가로(높이~390)는 제외
    const [isWide, setIsWide] = useState(false);

    const [deviceRecords, setDeviceRecords] = useState<DeviceRecord[]>([]);
    // 수련생 목록(링크 토큰·기기 수) — 기기 0대인 "링크만 있는" 수련생을 보이려면 기기 목록만으론 모자란다.
    const [students, setStudents] = useState<StudentView[]>([]);
    const [classes, setClasses] = useState<ClassView[]>([]); // 홈 트레이닝 '반' — 비어 있으면 화면은 반을 모른다
    const [classesFrom, setClassesFrom] = useState<"homeDevices" | "main">("homeDevices"); // 반 나누기에서 뒤로 갈 곳(들어온 자리)
    const [pairClassId, setPairClassId] = useState<string | null>(null); // 반을 고른 채 등록을 눌렀을 때 미리 채울 반
    const [devicesLoaded, setDevicesLoaded] = useState(false); // 첫 로드 전 "기기 없음" 플래시 방지
    const [videos, setVideos] = useState<VideoView[]>([]);
    // '기본동작' 세부(품·낱기술) — 재생목록 '영상 추가' 시트에만 쓰는 별도 목록(videos 엔 안 섞는다). ui.tsx DataCtx.breakdown 참고.
    const [breakdown, setBreakdown] = useState<VideoView[]>([]);
    const [playlists, setPlaylists] = useState<PlaylistView[]>([]);
    const [masterForms, setMasterForms] = useState<MasterForm[]>([]); // 표준 품새(마스터) 카탈로그
    // 낱기술 '기술 한 줄'(지도 영상 + 왼쪽·오른쪽) 허용 계정인가 — 서버가 판정해 내려준다(시험 중: 테스트 계정·운영자).
    const [techMoves, setTechMoves] = useState(false);
    const [sbs, setSbs] = useState(false); // 손발손 구령 버전(우선 테스트 계정만)
    const [drillForm, setDrillForm] = useState<MasterForm | null>(null); // 재생목록 탭에서 품새 카드 진입 시 전체화면 드릴 대상
    // 라이브러리 탭 세그/묶음 위치 — 부모가 보유. 태극1장 → 전체화면 드릴(formdrill)로 나가면 LibraryScreen 이
    // 언마운트되므로 내부 useState 로 두면 뒤로 왔을 때 태극 묶음이 사라지고 라이브러리 메인으로 떨어진다 → 여기서 유지.
    const [libSeg, setLibSeg] = useState<"default" | "mine">("mine"); // 기본=내 자료(도장 자체 콘텐츠 우선)
    const [libGroup, setLibGroup] = useState<null | "basic" | "taegeuk">(null);
    // 기기에 보낸 마스터 재생 큐(deviceId→). 마스터는 별도 도장이라 컨트롤러 byId/plById 에 없어, 제어화면 미러/큐가 비었다.
    // 보낸 목록을 여기 담아 두고 byId/plById 에 합성 주입 → 기존 computeNowPlaying·Queue·Mirror 가 그대로 렌더.
    const [masterQueues, setMasterQueues] = useState<
        Record<string, MasterQueue>
    >({});
    // 최근 캐스트(로드시트 상단 칩) — 세션 한정. 도장 전환 시 초기화(enterDojang).
    const [recentCasts, setRecentCasts] = useState<RecentCast[]>([]);
    const [presence, setPresence] = useState<Record<string, boolean>>({});
    // rxAt = 이 컨트롤러가 보고를 '받은' 로컬 시각(서버 타입엔 없는 컨트롤러 전용 필드).
    // 신선도 판정을 기기 시계(ts)에 기대면, 시계가 어긋난 기기는 보고가 계속 와도 '오래됨'으로 읽힌다.
    const [states, setStates] = useState<Record<string, DeviceState & { rxAt: number }>>({});
    // 기기별 마지막 제어자 이름(관장 전용 그리드 배지) — 서버가 관장 룸에만 control:update 를 보낸다.
    const [controlBy, setControlBy] = useState<Record<string, string>>({});
    // 관장(대표자/최고관리자) 여부 — 부관리자 관리·제어자 배지 노출 기준.
    const canManage = dojang?.role === "OWNER" || dojang?.role === "SUPERADMIN";

    const [selId, setSelId] = useState<string | null>(null);
    // 기기 다중선택(즉석) — 그룹 저장 없이 그때그때 고른 기기들에 일괄 명령. selecting=선택 모드.
    const [selecting, setSelecting] = useState(false);
    const [selIds, setSelIds] = useState<Set<string>>(new Set());
    const [batchSheet, setBatchSheet] = useState(false); // 일괄 '같은 영상 틀기' 재생목록 시트(단일 sheet 와 분리)
    const [selPlId, setSelPlId] = useState<string | null>(null);
    // 재생목록 상세를 닫을 때 돌아갈 곳 — "control"이면 제어 화면에서 만든 것: 닫으면서 그 기기에서 바로 재생
    const [plReturn, setPlReturn] = useState<"main" | "control">("main");
    // 재생목록 상세 진입과 동시에 '영상 추가' 시트 자동 열기 (새로 만든 직후 / 제어 화면 큐의 영상 추가).
    // PlaylistDetail 이 마운트 시점에만 읽으므로(useState 초기값), 상세를 벗어나면 꺼서 다음 진입에 새지 않게 한다.
    const [plAutoPick, setPlAutoPick] = useState(false);
    // 재생목록 '영상 추가' 시트에서 업로드로 진입한 경우 그 재생목록 id — 완료 시 자동 추가 + 상세로 복귀
    const [uploadReturnPl, setUploadReturnPl] = useState<string | null>(null);
    const [sheet, setSheet] = useState(false);
    const [modal, setModal] = useState<Modal | null>(null);
    const [activeUpload, setActiveUpload] = useState<ActiveUpload | null>(null); // 진행 중 업로드 — 라이브러리에 "전송 중" 표시
    const [connected, setConnected] = useState(false);
    // 기기 없이 제어화면 룩으로 로컬 재생 — 라이브러리 영상/재생목록 항목 탭 시. null=닫힘.
    // 로컬 미리보기 — ids(byId 조회) 또는 videos(마스터처럼 byId 에 없는 영상 직접). onSend 있으면 '디바이스에 재생' CTA 노출.
    const [localPlay, setLocalPlay] = useState<{
        ids?: string[];
        videos?: VideoView[];
        idx: number;
        title?: string;
        onSend?: (index: number) => void;
        onAddToPlaylist?: (index: number) => void;
    } | null>(null);
    // 라이브러리 미리보기 '＋ 담기' → 이 영상을 담을 재생목록 고르기 시트(videoId). null=닫힘.
    const [addToPlVideo, setAddToPlVideo] = useState<string | null>(null);
    // 제어 화면 '영상 추가' — 상세 화면으로 넘어가지 않고 제어 화면 위 오버레이로 현재 큐에 바로 추가.
    // { plId, items } = 대상 재생목록 + 낙관적 항목(추가 즉시 avail 에서 제외·재생 중 큐 SYNC). null=닫힘.
    const [addQueue, setAddQueue] = useState<{ plId: string; items: string[] } | null>(null);
    const [now, setNow] = useState(0);
    // 수련 세션(기기 단위) — 서버(DB)가 단일 진실원. activeMap=진행 중 세션 캐시(deviceId→시작시각ms).
    // 컨트롤러가 기기 상태 스냅샷을 동기화하면 서버가 기기별로 시작/갱신/마감하고 열린 목록을 돌려준다.
    const [activeMap, setActiveMap] = useState<Record<string, number>>({});
    const devicesRef = useRef<DeviceView[]>([]); // 인터벌에서 최신 기기 상태 접근용
    // 숙제 알림 몰아 읽기 — 한 편 끝날 때마다 오므로, 여러 학생이 동시에 끝내도 목록은 한 번만 다시 읽는다.
    const hwRefreshRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    // 홈워킹 탭에서 학생을 고르는 중인가 — 기기 탭의 selecting 과 같은 일을 한다(탭바를 감춘다).
    const [hwSelecting, setHwSelecting] = useState(false);
    // 숙제 내기 시트(LoadSheet 얼개) — 어느 학생(들)에게 내는 중인지. null=닫힘.
    const [hwPickDevs, setHwPickDevs] = useState<DeviceView[] | null>(null);
    // 자습 범위 시트(RangeSheet·LoadSheet 얼개) — 어느 학생 것을 고치는 중인지. null=닫힘.
    const [rankPickDev, setRankPickDev] = useState<DeviceView | null>(null);
    const [sessionNow, setSessionNow] = useState(0); // 경과 타이머용 현재시각 — 1초마다·세션 갱신 시 갱신
    // 기기별 '얼린(백그라운드) 누적 ms' — 플레이어 탭이 최소화(hidden)된 동안은 실제 재생이 멈추므로
    // 수련 타이머도 멈춰야 한다. 벽시계 경과(now-start)에서 이 누적을 빼 hidden 구간을 제외한다(복귀 시 점프 없음).
    const hiddenAccumRef = useRef<
        Record<string, { ms: number; lastTick: number }>
    >({});
    // 화면 테마(컨트롤러 전용) — localStorage 영속. 기본은 라이트. 플레이어는 항상 다크.
    const [theme, setTheme] = useState<"dark" | "light">("light");
    useEffect(() => {
        const saved = localStorage.getItem("tedu-theme");
        if (saved === "light" || saved === "dark") setTheme(saved);
    }, []);
    const toggleTheme = useCallback(() => {
        setTheme((t) => {
            const next = t === "dark" ? "light" : "dark";
            localStorage.setItem("tedu-theme", next);
            return next;
        });
    }, []);
    const skewRef = useRef<number | null>(null); // 서버↔컨트롤러 시계 차 추정 (위치 보간 보정)
    const statesRef = useRef(states); // 최신 commit 된 states — 소켓 핸들러에서 updater 밖 비교용(diag)
    useEffect(() => {
        statesRef.current = states;
    }, [states]);
    // 손들기 알림음 — 학생이 손을 드는 순간(false→true) 짧은 '딩-동'. WebAudio(에셋 불필요).
    // 비프는 소켓 이벤트(제스처 아님)로 울리므로, AudioContext 가 suspended 면 무음이 된다(첫 비프만 나고
    // 이후 안 나던 원인). 아래 unlock effect 가 사용자 제스처마다 resume 해 running 을 유지한다.
    const audioCtxRef = useRef<AudioContext | null>(null);
    const audioPrimedRef = useRef(false);
    const ensureAudio = useCallback(() => {
        const Ctx =
            window.AudioContext ??
            (window as unknown as { webkitAudioContext?: typeof AudioContext })
                .webkitAudioContext;
        if (!Ctx) return null;
        const ctx = audioCtxRef.current ?? new Ctx();
        audioCtxRef.current = ctx;
        if (ctx.state === "suspended") ctx.resume().catch(() => {});
        return ctx;
    }, []);
    // 사용자 제스처마다 컨텍스트 resume — 한 번 running 이 되면 이후 소켓 비프도 울린다(백그라운드 복귀 후 재차 보장).
    // 첫 제스처엔 무음(gain 0) 노드를 제스처 안에서 1회 재생해 iOS/Safari 까지 완전 unlock 한다.
    useEffect(() => {
        const unlock = () => {
            const ctx = ensureAudio();
            if (ctx && !audioPrimedRef.current) {
                try {
                    const o = ctx.createOscillator();
                    const g = ctx.createGain();
                    g.gain.value = 0;
                    o.connect(g).connect(ctx.destination);
                    o.start();
                    o.stop(ctx.currentTime + 0.02);
                    audioPrimedRef.current = true;
                } catch {
                    /* noop */
                }
            }
        };
        window.addEventListener("pointerdown", unlock, { capture: true });
        window.addEventListener("keydown", unlock, { capture: true });
        return () => {
            window.removeEventListener("pointerdown", unlock, {
                capture: true,
            });
            window.removeEventListener("keydown", unlock, { capture: true });
        };
    }, [ensureAudio]);
    const beepHand = useCallback(() => {
        try {
            const ctx = ensureAudio();
            if (!ctx) return;
            const t0 = ctx.currentTime;
            (
                [
                    [880, 0],
                    [1320, 0.14],
                ] as [number, number][]
            ).forEach(([freq, dt]) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = "sine";
                osc.frequency.value = freq;
                const start = t0 + dt;
                gain.gain.setValueAtTime(0, start);
                gain.gain.linearRampToValueAtTime(0.18, start + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0008, start + 0.13);
                osc.connect(gain).connect(ctx.destination);
                osc.start(start);
                osc.stop(start + 0.14);
            });
            if (typeof navigator !== "undefined" && "vibrate" in navigator)
                navigator.vibrate?.([40, 60, 40]); // 모바일 컨트롤러 햅틱
        } catch {
            /* 오디오 차단/미지원 — 시각 표시로 충분 */
        }
    }, [ensureAudio]);
    // 관장 메시지 알림음 — 손들기('딩동', 밝게 올라가는 880→1320)와 헷갈리지 않게 **낮고 부드러운 내림 두 음**
    // (523→392, triangle·작은 음량). 소리만 듣고도 "손들기냐 메시지냐"가 구분되도록 일부러 반대 성격으로 뽑았다.
    const beepMsg = useCallback(() => {
        try {
            const ctx = ensureAudio();
            if (!ctx) return;
            const t0 = ctx.currentTime;
            (
                [
                    [523.25, 0],
                    [392.0, 0.13],
                ] as [number, number][]
            ).forEach(([freq, dt]) => {
                const osc = ctx.createOscillator();
                const gain = ctx.createGain();
                osc.type = "triangle";
                osc.frequency.value = freq;
                const start = t0 + dt;
                gain.gain.setValueAtTime(0, start);
                gain.gain.linearRampToValueAtTime(0.14, start + 0.02);
                gain.gain.exponentialRampToValueAtTime(0.0008, start + 0.16);
                osc.connect(gain).connect(ctx.destination);
                osc.start(start);
                osc.stop(start + 0.18);
            });
            if (typeof navigator !== "undefined" && "vibrate" in navigator)
                navigator.vibrate?.(60); // 손들기(짧게 3번)와 다른 한 번 진동
        } catch {
            /* 오디오 차단/미지원 — 시각 표시로 충분 */
        }
    }, [ensureAudio]);
    const dojangIdRef = useRef<string | null>(null); // 현재 도장 — 같은 도장 재진입 판별용

    // dojang:sync 응답(현재 온라인 디바이스 상태들)을 presence/states 에 반영 — 재연결·도장 재진입 시 상태 복구.
    const applySync = useCallback((list: DeviceState[]) => {
        if (!Array.isArray(list)) return;
        const now = Date.now();
        if (list.length > 0)
            setStates((m) => {
                const next = { ...m };
                for (const s of list) {
                    const sample = now - s.ts;
                    skewRef.current =
                        skewRef.current == null
                            ? sample
                            : Math.min(sample, skewRef.current + 50);
                    next[s.deviceId] = {
                        ...s,
                        ts: s.ts + (skewRef.current ?? 0),
                        rxAt: now, // 받은 로컬 시각 — 신선도 판정용(기기 시계와 무관)
                    };
                }
                return next;
            });
        // dojang:sync 는 '현재 온라인 디바이스 전체'의 권위 스냅샷 — 목록에 없는(=오프라인) 기기는 정정한다.
        // 컨트롤러가 잠깐 끊긴 사이 기기가 오프라인이 되면 그 presence:update(online:false)를 놓쳐, 이전 true 가
        // 남아 오프라인 기기가 그리드에 '대기'로 잘못 떴다. 재동기화 때 알던 기기 전체를 목록 기준으로 다시 칠한다.
        const onlineIds = new Set(list.map((s) => s.deviceId));
        // 놓친 오프라인 전이 — 그 기기의 스냅샷도 같이 버린다(재접속 때 옛 화면 번쩍임 방지).
        for (const id of Object.keys(statesRef.current))
            if (!onlineIds.has(id)) clearFrame(id);
        setPresence((p) => {
            const next: Record<string, boolean> = {};
            for (const id of Object.keys(p)) next[id] = onlineIds.has(id);
            for (const id of Object.keys(statesRef.current))
                next[id] = onlineIds.has(id);
            for (const id of onlineIds) next[id] = true;
            return next;
        });
    }, []);

    type ToastAction = { label: string; onClick: () => void };
    const [toast, setToast] = useState<{
        msg: string;
        icon?: ReactNode;
        leaving?: boolean;
        action?: ToastAction;
        sticky?: boolean; // 자동으로 안 사라지고 액션(확인)을 눌러야만 닫힘 — 관장 전달사항처럼 꼭 확인해야 하는 알림
        title?: string; // alert(전달사항) 카드 헤더 — 보낸이 라벨(예: '관장 전달사항')
    } | null>(null);
    const toastT = useRef<ReturnType<typeof setTimeout> | null>(null);
    const toastOutT = useRef<ReturnType<typeof setTimeout> | null>(null);
    const stickyToastRef = useRef(false); // 확인 대기 중인 관장 쪽지가 떠 있나 — 일반 안내 토스트가 이걸 덮지 않게 가드
    const sockRef = useRef<Socket | null>(null);
    const buildCheckRef = useRef<() => void>(() => {}); // 새 빌드 확인을 지금 한 번 — 소켓 재연결(=배포로 서버 재시작) 때

    const closeModal = () => setModal(null);
    const dismissToast = useCallback(() => {
        stickyToastRef.current = false;
        if (toastT.current) clearTimeout(toastT.current);
        setToast((t) => (t ? { ...t, leaving: true } : t));
        toastOutT.current = setTimeout(() => setToast(null), 250);
    }, []);
    // action(되돌리기 등)이 있으면 누를 시간을 더 줌(6s), 일반 안내는 2.4s.
    // opts.sticky=true 면 타이머 없이 계속 떠 있고 액션(확인)을 눌러야만 닫힌다(관장 전달사항). opts.title=전달사항 카드 헤더.
    const showToast = useCallback(
        (msg: string, icon?: ReactNode, action?: ToastAction, opts?: { sticky?: boolean; title?: string }) => {
            const sticky = opts?.sticky;
            // 확인 대기 중인 관장 전달사항(sticky)이 떠 있으면 일반 안내 토스트는 덮어쓰지 않는다 — 확인 전까지 유지.
            // (새 전달사항 자체는 sticky 라 통과해 최신으로 교체된다.)
            if (!sticky && stickyToastRef.current) return;
            stickyToastRef.current = !!sticky;
            setToast({ msg, icon, action, sticky, title: opts?.title });
            if (toastT.current) clearTimeout(toastT.current);
            if (toastOutT.current) clearTimeout(toastOutT.current);
            if (sticky) return; // 자동 페이드아웃 없음 — 확인 누를 때까지 유지
            // 표시 시간 후 페이드아웃 클래스 부여 → 애니메이션(0.25s) 끝나면 언마운트
            toastT.current = setTimeout(
                () => {
                    setToast((t) => (t ? { ...t, leaving: true } : t));
                    toastOutT.current = setTimeout(() => setToast(null), 250);
                },
                action ? 6000 : 2400,
            );
        },
        [],
    );

    // 관장 → 부관리자 메시지 전송(실시간). toUserId=null 이면 전체 부관리자. ack 로 성공 여부를 받아
    // 폼이 결과를 표시한다(연결이 끊겼거나 ack 유실 시 4초 후 실패로 확정).
    const sendStaffMessage = useCallback(
        (toUserId: string | null, text: string) =>
            new Promise<boolean>((resolve) => {
                const sock = sockRef.current;
                if (!sock || !sock.connected) return resolve(false);
                let done = false;
                const finish = (ok: boolean) => {
                    if (done) return;
                    done = true;
                    resolve(ok);
                };
                const t = setTimeout(() => finish(false), 4000);
                sock.emit(
                    "notify:send",
                    { toUserId: toUserId ?? undefined, text },
                    (r: { ok?: boolean }) => {
                        clearTimeout(t);
                        finish(!!r?.ok);
                    },
                );
            }),
        [],
    );

    /* ---- data maps ---- */
    const byId = useMemo(
        () =>
            Object.fromEntries(videos.map((v) => [v.id, v])) as Record<
                string,
                VideoView
            >,
        [videos],
    );
    const plById = useMemo(
        () =>
            Object.fromEntries(playlists.map((p) => [p.id, p])) as Record<
                string,
                PlaylistView
            >,
        [playlists],
    );
    // 마스터 재생 큐를 byId/plById 에 합성 주입 — 마스터 재생 중인 기기의 제어화면이 제목·큐·미러를 그리도록.
    // (배열 videos/playlists 는 그대로 두어 라이브러리·재생목록 탭엔 노출 안 됨. 조회 맵에만 얹는다.)
    // 마스터 카탈로그(모든 멤버가 받는다) → 조회용 맵. masterQueues 는 '보낸 브라우저'에만 있어서,
    // 관장이 튼 품새를 사범이 열면(또는 새로고침하면) 미러·제목이 비었다 — 실제 신고된 증상.
    // 카탈로그에서 직접 찾으면 누가 틀었든, 새로고침해도 그려진다.
    const masterCatalogById = useMemo(() => {
        const m: Record<string, VideoView> = {};
        for (const f of masterForms)
            for (const v of f.videos)
                m[v.id] = masterToVideoView({
                    languageTrack: v.languageTrack,
                    id: v.id, mediaKey: v.mediaKey, version: v.version, title: v.title,
                    durationSec: v.durationSec, thumbnail: v.thumbnail,
                    mirror: v.mirror ?? null, mirrorHi: v.mirrorHi ?? null,
                    mirrorTag: v.mirrorTag ?? null, mirrorHiTag: v.mirrorHiTag ?? null, sources: v.sources,
                });
        return m;
    }, [masterForms]);
    const byIdAug = useMemo(() => {
        const qs = Object.values(masterQueues);
        const extra: Record<string, VideoView> = {};
        // 품·낱기술(실제 영상 행 — 참조 메타·mp4 경로가 온전)을 먼저 — 재생목록에 담긴 뒤 상세·미리보기가 이걸로 그려진다.
        for (const v of breakdown) if (!byId[v.id]) extra[v.id] = v;
        // 카탈로그분 먼저 깔고(공용), 그 위에 보낸 큐 정보를 얹는다. 실제 도장 영상(byId)이 최종 우선.
        for (const [id, v] of Object.entries(masterCatalogById)) if (!byId[id] && !extra[id]) extra[id] = v;
        // byId 에 없는 '진짜 마스터(운영자 품새) 영상'만 합성 주입. 개별 영상 캐스팅(LOAD_MASTER)은 실제 도장 영상이라
        // byId 에 이미 있는데, masterToVideoView 는 손실성(mp4Path=""·mirror 스냅샷)이라 덮어쓰면 미리보기·미러가 깨진다
        // → 실제 byId 항목(라이브 조회, 미러 자가치유)이 항상 이기게 한다.
        for (const q of qs)
            for (const mv of q.videos)
                if (!byId[mv.id]) extra[mv.id] = masterToVideoView(mv);
        return { ...byId, ...extra };
    }, [byId, masterQueues, masterCatalogById, breakdown]);
    // ⚠️ states 를 그대로 의존성에 넣으면 안 된다 — 기기는 재생 중 초당 여러 번 보고하므로
    // 아래 맵이 매 보고마다 새 객체가 되고, 그걸 보는 미러가 계속 재설정돼 화면이 멈춘 것처럼 된다
    // (실측 회귀: 두 번째 캐스팅부터 그리드·제어화면이 얼어붙음).
    // 합성 재생목록에 필요한 건 '어떤 합성 id 로 어떤 영상을 트는가' 뿐이라, 그것만 문자열로 굳힌다.
    const synthKey = useMemo(
        () =>
            Object.values(states)
                .filter((st) => st.playlistId?.startsWith("master:") || st.playlistId?.startsWith("masterset:"))
                .map((st) => `${st.playlistId}|${st.videoId ?? ""}`)
                .sort()
                .join(","),
        [states],
    );
    const plByIdAug = useMemo(() => {
        const qs = Object.values(masterQueues);
        if (qs.length === 0) return plById;
        const extra: Record<string, PlaylistView> = {};
        for (const q of qs)
            extra[q.plId] = {
                id: q.plId,
                name: q.title,
                desc: "",
                isDefault: true,
                items: q.videos.map((v) => v.id),
                repeats: {},
                hidden: {},
                added: {},
            };
        // 개별 영상 한 편 캐스팅(LOAD_MASTER)의 합성 재생목록 — id 가 `master:<영상id>` 라
        // 보낸 사람 정보 없이도 복원된다. 이게 없으면 기기가 보고한 playlistId 를 아무도 몰라
        // computeNowPlaying 이 null 을 반환하고, 그리드는 '대기'로, 제어화면 미러는 빈 채로 남는다
        // (보낸 브라우저만 멀쩡해 '관장은 되는데 사범은 안 된다'로 보였다).
        const synth = synthKey ? synthKey.split(",").map((p) => p.split("|")) : [];
        for (const [plid, reportedVid] of synth) {
            if (!plid.startsWith("master:")) continue;
            if (plById[plid] || extra[plid]) continue;
            const vid = plid.slice("master:".length);
            const v = byIdAug[vid];
            if (!v) continue;
            void reportedVid;
            extra[plid] = {
                id: plid, name: v.title, desc: "", isDefault: true,
                items: [vid], repeats: {}, hidden: {}, added: {},
            };
        }
        // 마디별 임시 세트(masterset:<제목>) — 목록 구성은 보낸 쪽만 안다. 다만 기기가 지금 트는
        // 영상(videoId)은 보고되므로, 그것만이라도 1곡짜리로 세워 미러·제목이 뜨게 한다.
        // (큐 목록은 보낸 브라우저에서만 완전하다 — 완전한 복원은 서버가 큐를 들고 있어야 가능)
        for (const [plid, reportedVid] of synth) {
            if (!plid.startsWith("masterset:")) continue;
            if (plById[plid] || extra[plid]) continue;
            const v = reportedVid ? byIdAug[reportedVid] : undefined;
            if (!v) continue;
            extra[plid] = {
                id: plid, name: plid.slice("masterset:".length) || v.title, desc: "", isDefault: true,
                items: [v.id], repeats: {}, hidden: {}, added: {},
            };
        }
        // 마스터 '전체 세트' 재생목록 — 카탈로그에 있는 것이면 누구 브라우저에서든 복원한다.
        for (const f of masterForms)
            if (f.playlistId && !plById[f.playlistId] && !extra[f.playlistId])
                extra[f.playlistId] = {
                    id: f.playlistId, name: f.name, desc: "", isDefault: true,
                    items: f.videos.map((v) => v.id), repeats: {}, hidden: {}, added: {},
                };
        return { ...plById, ...extra };
    }, [plById, masterQueues, masterForms, synthKey, byIdAug]);

    /* ---- fetch helpers ---- */
    const refreshDevices = useCallback(async (id: string) => {
        const [devs, studs, clss] = await Promise.all([
            fetch(`/api/dojangs/${id}/devices`).then((r) => r.json()).catch(() => []),
            // 수련생(링크·기기 수) — 기기와 같은 때 읽어야 '링크만 있는' 줄과 기기 줄이 어긋나지 않는다.
            fetch(`/api/dojangs/${id}/students`).then((r) => (r.ok ? r.json() : [])).catch(() => []),
            // 반 — 수련생과 같은 때 읽어야 칩 수와 줄이 어긋나지 않는다.
            fetch(`/api/dojangs/${id}/classes`).then((r) => (r.ok ? r.json() : [])).catch(() => []),
        ]);
        setStudents(Array.isArray(studs) ? (studs as StudentView[]) : []);
        setClasses(Array.isArray(clss) ? (clss as ClassView[]) : []);
        setDeviceRecords(
            Array.isArray(devs)
                ? devs.map((d: DeviceRecord) => ({
                      id: d.id,
                      name: d.name,
                      deviceName: d.deviceName,
                      studentId: d.student?.id ?? null,
                      homeworkCompleted: d.student?.homeworkCompleted ?? 0,
                      lastSeen: d.lastSeen,
                      touchLocked: d.touchLocked ?? false,
                      atHome: d.atHome ?? false,
                      rankFormId: d.rankFormId ?? null,
                      homePlaylistId: d.homePlaylistId ?? null,
                      homeworkDoneAt: d.homeworkDoneAt ?? null,
                      hasPushToken: d.hasPushToken ?? false,
                      platform: d.platform ?? null,
                      cameraOk: d.cameraOk ?? null,
                      fromStore: d.fromStore ?? null,
                  }))
                : [],
        );
        setDevicesLoaded(true);
    }, []);
    // 내가 목록을 바꿨다(등록·해지·이름·집 화면·숙제·잠금) — 다시 읽고, 같은 도장의 **다른 관리 앱들**에도 알린다.
    // 🔑 2026-09-04 실제 신고: 다른 기기에서 해지해도 아이폰엔 회색 오프라인으로만 남았다. 서버는 이 신호를
    //    다른 관리 앱들에 되돌려 주고(나 자신은 제외), 받은 쪽은 목록을 다시 읽는다(내용은 안 싣는다).
    const devicesChanged = useCallback(async (id: string) => {
        sockRef.current?.emit("devices:changed");
        return refreshDevices(id);
    }, [refreshDevices]);
    const refreshVideos = useCallback(async (id: string) => {
        // breakdown=1 로 품·낱기술까지 받아 **여기서 둘로 가른다** — 화면 목록(videos)은 예전 그대로 전체·품마디,
        // 품·낱기술은 breakdown 으로 따로(영상 추가 시트 전용). 요청 한 번으로 둘 다.
        const vs = await fetch(`/api/dojangs/${id}/videos?breakdown=1`)
            .then((r) => r.json())
            .catch(() => []);
        const all: VideoView[] = Array.isArray(vs) ? vs : [];
        // 손발손 구령 영상(refVariant, 허용 계정에만 옴)도 breakdown 쪽으로 — 라이브러리·대기화면엔 안 섞이고 영상 추가 시트에서만 고른다.
        const isBd = (v: VideoView) => !!v.refFormId && (isBasicBreakdownSegment(v.refSegment ?? null) || !!(v.refVariant ?? "").trim());
        setVideos(all.filter((v) => !isBd(v)));
        setBreakdown(all.filter(isBd));
    }, []);
    const refreshPlaylists = useCallback(async (id: string) => {
        const ps = await fetch(`/api/dojangs/${id}/playlists`)
            .then((r) => r.json())
            .catch(() => []);
        setPlaylists(
            Array.isArray(ps)
                ? ps.map((p: PlaylistRaw) => normalizePlaylist(p))
                : [],
        );
    }, []);
    // 표준 품새(마스터 라이브러리) 카탈로그 — 세트리스트 화면용. 멤버 읽기 전용 엔드포인트.
    const refreshMaster = useCallback(async (id: string) => {
        const r = await fetch(`/api/dojangs/${id}/master`)
            .then((res) => res.json())
            .catch(() => null);
        setMasterForms(
            Array.isArray(r?.forms) ? (r.forms as MasterForm[]) : [],
        );
        setTechMoves(r?.techMoves === true);
        setSbs(r?.sbs === true);
    }, []);
    // 기기 상태 스냅샷을 서버에 동기화 → 서버가 기기별 세션 reconcile, 진행 중 목록을 받아 캐시 갱신.
    const syncTraining = useCallback(async (id: string, list: DeviceView[]) => {
        try {
            const r = await fetch(`/api/dojangs/${id}/training/sync`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ devices: snapshotOf(list) }),
            });
            if (r.ok) {
                const server = toActiveMap(await r.json());
                const keep = activeDeviceIds(list); // 재생/일시정지 중인 기기는 서버 미기록이어도 타이머 유지
                setActiveMap((prev) => reconcileActiveMap(prev, server, keep));
                setSessionNow(monoNow()); // 타이머 시드(초기 0:00 깜빡임 방지)
            }
        } catch {
            /* 기록 동기화 실패는 제어에 영향 없음 */
        }
    }, []);
    // 진입/재진입 시 진행 중 세션 복원(서버가 stale 자동 마감 후 목록 반환).
    const refreshActiveSessions = useCallback(async (id: string) => {
        try {
            const r = await fetch(`/api/dojangs/${id}/training/active`);
            const server = toActiveMap(r.ok ? await r.json() : []);
            setActiveMap((prev) =>
                reconcileActiveMap(
                    prev,
                    server,
                    activeDeviceIds(devicesRef.current),
                ),
            );
            setSessionNow(monoNow()); // 타이머 시드
        } catch {
            /* ignore */
        }
    }, []);
    // '수업 종료' 가 카메라까지 내리게 하는 다리.
    // 🔑 카메라 상태(camView/camSend/camHold)는 이 파일 한참 아래에서 선언된다 — 여기서 곧장 부르면
    //    선언 전이라 죽는다. 그래서 아래에서 실제 함수를 꽂아 두고 여기선 그것만 부른다.
    const closeCamsForRef = useRef<((ids: string[]) => void) | null>(null);
    // 명시적 종료(수업 종료·정지 버튼) — 비동기 상태 관찰을 기다리지 않고 그 기기 세션을 서버에서 '지금' 즉시 마감.
    // 타이머는 낙관적으로 바로 제거(누른 즉시 멈춤·이력 진행중 잔존 방지). 실패해도 다음 sync/이력조회 reap 이 보정.
    const endTrainingFor = useCallback((ids: string[]) => {
        const id = dojangIdRef.current;
        const targets = ids.filter(Boolean);
        if (!id || targets.length === 0) return;
        setActiveMap((m) => {
            const n = { ...m };
            for (const did of targets) delete n[did];
            return n;
        });
        // 마감 POST — 실패하면 서버 세션이 열린 채 남아 재진입/다른 컨트롤러에서 타이머가 되살아난다.
        // 일시적 네트워크 오류 대비 지수 백오프로 몇 번 재시도(최종 실패는 30분 reap 이 보정).
        // 서버 endNow 는 열린 세션이 있을 때만 닫으므로 재시도는 멱등 — 그 사이 재개돼도 다음 sync 가 새 세션을 만든다.
        const postEnd = (attempt: number) => {
            fetch(`/api/dojangs/${id}/training/sync`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    devices: snapshotOf(devicesRef.current),
                    ended: targets,
                }),
            })
                .then(async (r) => {
                    if (!r.ok) throw new Error(String(r.status));
                    const server = toActiveMap(await r.json());
                    // 방금 종료한 기기(targets)는 유지대상에서 제외 — 종료 즉시 제거되도록.
                    const keep = activeDeviceIds(devicesRef.current);
                    for (const did of targets) keep.delete(did);
                    setActiveMap((prev) =>
                        reconcileActiveMap(prev, server, keep),
                    );
                })
                .catch(() => {
                    if (attempt < 3)
                        setTimeout(
                            () => postEnd(attempt + 1),
                            2000 * 2 ** attempt,
                        ); // 2s·4s·8s
                });
        };
        postEnd(0);
    }, []);
    // 낙관적 배지 정리 — '수업 종료'(GO_HOME)·'정지'(STOP)를 보낸 즉시 그 기기를 대기(idle)로 표시한다.
    // 그리드 배지는 states[id].status 기반이라, 여태 플레이어의 idle 보고가 왕복해야만 '재생 중'이 풀렸다.
    // 실기기(WebView 키오스크)에서 백그라운드 스로틀·회선 블립·currentTime 리셋 경합(0으로 못 읽어 paused 보고)이면
    // 그 보고가 늦거나 유실·비-idle 로 와 배지가 '재생 중'으로 잔존한다. 눌렀으면 즉시 대기로 — 수련 타이머(위)와 같은 낙관 원칙.
    // 명령이 실제로 안 닿아 계속 재생 중이면, 기기의 다음 재생 보고가 다시 playing 으로 self-correct 한다.
    // keepPlaylist: 정지는 '잠깐 멈춤'이라 재생목록을 그대로 둔다(종료만 목록까지 내린다).
    const markIdleOptimistic = useCallback((ids: string[], opts?: { keepPlaylist?: boolean }) => {
        setStates((m) => {
            let changed = false;
            const n = { ...m };
            for (const id of ids) {
                const prev = m[id];
                if (prev && prev.status !== "idle") {
                    n[id] = {
                        ...prev,
                        status: "idle",
                        hidden: false,
                        ...(opts?.keepPlaylist
                            ? {}
                            : { playlistId: null, videoId: null }),
                    };
                    changed = true;
                }
            }
            return changed ? n : m;
        });
    }, []);
    // 자동 열기 플래그는 상세를 벗어나면 끈다 — 안 끄면 다음에 기존 재생목록을 열 때 시트가 튀어나온다.
    useEffect(() => {
        if (screen !== "pldetail" && plAutoPick) setPlAutoPick(false);
    }, [screen, plAutoPick]);
    // HLS 변환 진행상황 SSE 구독 — 진행률은 즉시 패치, 완료/실패 시 목록 재조회(hlsPath·version 반영)
    useEffect(() => {
        if (!dojang) return;
        const es = new EventSource(`/api/dojangs/${dojang.id}/hls-events`);
        es.onmessage = (ev) => {
            let p: {
                videoId: string;
                state:
                    | "queued"
                    | "uploading"
                    | "processing"
                    | "mirror"
                    | "done"
                    | "failed";
                progress?: number;
                error?: string;
            };
            try {
                p = JSON.parse(ev.data);
            } catch {
                return;
            }
            // 미러 미리보기 준비됨 → 목록 재조회로 mirror 경로 반영(제어 화면이 즉시 가벼운 미러로 전환). 변환은 계속 진행.
            if (p.state === "mirror") {
                refreshVideos(dojang.id);
                return;
            }
            if (p.state === "done" || p.state === "failed")
                refreshVideos(dojang.id);
            if (p.state === "done") return; // ready 상태는 재조회 결과(hlsPath)로 표시
            const { videoId, ...hls } = p;
            setVideos((vs) =>
                vs.map((v) =>
                    v.id === videoId
                        ? { ...v, hls: hls as VideoView["hls"] }
                        : v,
                ),
            );
        };
        return () => es.close(); // EventSource 는 끊겨도 자동 재접속, 도장 전환 시에만 종료
    }, [dojang, refreshVideos]);

    /* ---- load me (user + dojangs + device counts) ---- */
    const loadMe = useCallback(async () => {
        const me = await fetch("/api/me")
            .then((r) => r.json())
            .catch(() => null);
        if (!me) return;
        setOwner({ name: me.user?.name ?? "", email: me.user?.email ?? "", phone: me.user?.phone ?? null, camera: !!me.user?.camera, tour: !!me.user?.tour, demo: !!me.user?.demo, homeDevice: !!me.user?.homeDevice, appFullscreen: !!me.user?.appFullscreen, practice: !!me.user?.practice, lead: !!me.user?.lead, voice: !!me.user?.voice });
        const list: DojangView[] = Array.isArray(me.dojangs)
            ? me.dojangs.map(
                  (d: {
                      id: string;
                      name: string;
                      role: DojangView["role"];
                      cinemaOverlay?: boolean;
                      captions?: boolean;
                      captionLang?: string;
                      titleStay?: boolean;
                      homeLink?: boolean;
                      timeZone?: string;
                  }) => ({
                      id: d.id,
                      name: d.name,
                      role: d.role,
                      cinemaOverlay: d.cinemaOverlay ?? true,
                      captions: d.captions === true,
                      captionLang: d.captionLang === "de" ? "de" : "en",
                      titleStay: d.titleStay === true,
                      homeLink: d.homeLink === true,
                      timeZone: d.timeZone ?? Intl.DateTimeFormat().resolvedOptions().timeZone,
                  }),
              )
            : [];
        // device counts (parallel, best-effort)
        const counts = await Promise.all(
            list.map((d) =>
                fetch(`/api/dojangs/${d.id}/devices`)
                    .then((r) => r.json())
                    .then((a) => (Array.isArray(a) ? a.length : 0))
                    .catch(() => 0),
            ),
        );
        setDojangs(list.map((d, i) => ({ ...d, deviceCount: counts[i] })));
    }, []);

    useEffect(() => {
        if (!session) return;
        (async () => {
            await loadMe();
        })();
    }, [session, loadMe]);

    /* ---- on dojang select: load its content ---- */
    const enterDojang = useCallback(
        (d: DojangView) => {
            // 같은 도장 재진입이면 소켓이 계속 연결돼 있어 presence/states 가 유효 — 비우면 전부 오프라인으로 떴다가
            // 재동기화될 때까지 깜빡인다. 다른 도장으로 바꿀 때만 비우고(소켓 재연결+connect 시 dojang:sync 로 채움).
            const same = dojangIdRef.current === d.id;
            dojangIdRef.current = d.id;
            try {
                localStorage.setItem("tedu-dojang", d.id);
            } catch {
                /* 새로고침 복귀용 — 저장 실패는 무시 */
            }
            setDojang(d);
            setScreen("main");
            setTab("devices");
            setSelId(null);
            setSelecting(false);
            setSelIds(new Set());
            setBatchSheet(false);
            if (!same) {
                setStates({});
                setPresence({});
                setControlBy({}); // 다른 도장: 이전 도장 제어자 배지 잔존 방지
                setActiveMap({}); // 다른 도장: 이전 도장 타이머 잔존 방지(아래 refreshActiveSessions 가 새 값으로 채움)
                setRecentCasts([]); // 다른 도장: 이전 도장 최근 캐스트 칩 잔존 방지
                setDevicesLoaded(false); // 새 도장 목록 로드 전까지 빈 상태 대신 로딩 표시
            } else {
                sockRef.current?.emit("dojang:sync", applySync); // 같은 도장: 혹시 모를 누락분 즉시 보정
            }
            refreshDevices(d.id);
            refreshVideos(d.id);
            refreshPlaylists(d.id);
            refreshMaster(d.id); // 표준 품새(마스터) 카탈로그
            refreshActiveSessions(d.id); // 진행 중인 수련 세션이 있으면 경과 타이머 복원
        },
        [
            refreshDevices,
            refreshVideos,
            refreshPlaylists,
            refreshMaster,
            refreshActiveSessions,
            applySync,
        ],
    );

    /* ---- 새로고침 복귀 — 마지막 진입 도장을 기억해 도장선택 화면을 건너뛴다 ---- */
    const [bootRestoring, setBootRestoring] = useState(false);
    const restoredRef = useRef(false);
    useEffect(() => {
        let saved = "";
        try {
            saved = localStorage.getItem("tedu-dojang") ?? "";
        } catch {
            /* ignore */
        }
        if (!saved) return;
        setBootRestoring(true); // 도장목록 로드 전까지 도장선택 깜빡임 대신 스피너
        const t = setTimeout(() => setBootRestoring(false), 5000); // 복귀 실패(목록 로드 지연 등)해도 도장선택은 보이게
        return () => clearTimeout(t);
    }, []);
    useEffect(() => {
        if (restoredRef.current || !session || dojangs.length === 0) return; // 세션·도장목록 로드 대기
        restoredRef.current = true;
        let saved = "";
        try {
            saved = localStorage.getItem("tedu-dojang") ?? "";
        } catch {
            /* ignore */
        }
        const match = saved ? dojangs.find((d) => d.id === saved) : null;
        // 도장이 하나뿐이면 선택 화면을 건너뛰고 자동 진입(설정 > 다른 도장 전환으로 언제든 복귀).
        const auto = match ?? (dojangs.length === 1 ? dojangs[0] : null);
        if (auto && !dojang) enterDojang(auto); // 매칭 실패(삭제·탈퇴·계정전환)+다도장이면 그대로 도장선택 노출
        setBootRestoring(false);
    }, [session, dojangs, dojang, enterDojang]);

    /* ---- socket connection (per dojang) ---- */
    useEffect(() => {
        if (!dojang?.id) return;
        // JWT 수명이 15분 — 고정 auth 객체로 두면 재연결(서버 재시작·네트워크 블립) 시
        // 만료 토큰으로 INVALID_JWT 거부 루프에 빠져 "연결된 듯 따로 노는" 상태가 됨.
        // 매 (재)연결 시도마다 신선한 토큰을 받아오는 콜백 방식으로 해결.
        const sock: Socket = io(realtimeUrl(), {
            path: "/socket.io",
            auth: (cb) => {
                authClient
                    .token()
                    .then(({ data }) =>
                        cb({
                            token: data?.token ?? "",
                            dojangId: dojang.id,
                            clientId: clientId(), // 재연결해도 같은 값 — 제어화면 점유를 이어받는 데 쓴다
                        }),
                    )
                    .catch(() =>
                        cb({ token: "", dojangId: dojang.id, clientId: clientId() }),
                    );
            },
            transports: ["websocket"],
        });
        sockRef.current = sock;
        sock.on("connect", () => {
            setConnected(true);
            sock.emit("dojang:sync", applySync);
            buildCheckRef.current(); // 배포로 서버가 재시작돼 다시 붙은 것일 수 있다
        }); // (재)연결 시 현재 상태 일괄 수신·빌드 확인
        sock.on("disconnect", () => setConnected(false));
        // 서버가 인증 거부(error)하면 socket.io 기본 재시도에 맡기되 연결 표시는 끔
        sock.on("error", () => setConnected(false));
        sock.on(
            "presence:update",
            ({ deviceId, online }: { deviceId: string; online: boolean }) => {
                if (!online) clearFrame(deviceId); // 끊긴 기기의 마지막 스냅샷 폐기 — 재접속 때 옛 화면 번쩍임 방지
                setPresence((p) => ({ ...p, [deviceId]: online }));
            },
        );
        sock.on(
            "device:frame",
            ({ deviceId, dataUrl }: { deviceId: string; dataUrl: string }) =>
                setFrame(deviceId, dataUrl),
        ); // 그리드 멀티뷰 스냅샷 — store(기기별 구독)로 페이지 전체 리렌더 방지
        // 제어자 배지(관장 전용) — by=null 이면 해제(종료/대기/오프라인). 부관리자는 서버가 미전송 → 빈 채 유지.
        sock.on(
            "control:update",
            ({ deviceId, by }: { deviceId: string; by: string | null }) =>
                setControlBy((m) => {
                    if (by) return { ...m, [deviceId]: by };
                    if (!(deviceId in m)) return m;
                    const n = { ...m };
                    delete n[deviceId];
                    return n;
                }),
        );
        sock.on("state:update", (s: DeviceState) => {
            // 시계 차 보정: (수신시각 - 서버 ts) = 시계차+전송지연. 최솟값이 시계차에 근접.
            // 시계가 뒤로 어긋나는 경우를 위해 메시지당 +50ms 씩 천천히 회복 허용.
            const sample = Date.now() - s.ts;
            skewRef.current =
                skewRef.current == null
                    ? sample
                    : Math.min(sample, skewRef.current + 50);
            // 잠금 칩의 실제 반영 시점 — 디바이스 state 보고로 touchLocked 가 바뀌는 순간 (?diag=1).
            // diag() 는 외부 store 리스너를 깨우므로 setState updater(렌더 중 실행) 밖에서 호출해야
            // "Cannot update a component while rendering a different component" 경고가 안 난다.
            if (
                diagOn() &&
                typeof s.touchLocked === "boolean" &&
                statesRef.current[s.deviceId]?.touchLocked !== s.touchLocked
            ) {
                diag(
                    "lock",
                    `state 보고 수신 touchLocked=${s.touchLocked} (소켓 경유 칩 반영 시점)`,
                );
            }
            // 손들기 알림음/토스트는 서버가 역할별로 수신자를 정해 보내는 'hand:raised' 이벤트에서 처리(아래).
            // 여기 state:update 는 그리드 ✋배지(시각)용 상태만 반영한다.
            setStates((m) => {
                const prev = m[s.deviceId];
                const next = receivePlaybackState(prev, s, skewRef.current ?? 0, Date.now());
                return { ...m, [s.deviceId]: next };
            });
            // 낙관적 시작 — 재생 보고를 받는 즉시 타이머 시드(서버 동기화 4초 디바운스 전이라도 바로 표시).
            // 4초 뒤 syncTraining 이 reconcileActiveMap 로 서버 startedAt 을 반영하되 더 이른 값을 유지 → 안 튐.
            if (s.status === "playing")
                setActiveMap((m) =>
                    s.deviceId in m ? m : { ...m, [s.deviceId]: monoNow() }, // 경과 타이머와 같은 시계(mono-clock.ts)
                );
        });
        // 숙제가 한 편 찍힐 때마다 기기가 알린다 → 목록을 다시 읽는다.
        // 🔑 세는 규칙(80%·편수·완료 판정)은 서버 한 곳에만 두고, 화면은 그 결과를 받아 적기만 한다.
        //    여기서 숫자를 직접 올리면 두 곳이 서로 다른 답을 내는 날이 온다.
        // ⚠️ 한 편마다 오므로 몰아서 읽는다(여러 학생이 동시에 끝내도 한 번만).
        const scheduleDevicesRefresh = () => {
            if (hwRefreshRef.current) return;
            hwRefreshRef.current = setTimeout(() => {
                hwRefreshRef.current = null;
                if (dojang?.id) refreshDevices(dojang.id);
            }, 700);
        };
        sock.on("homework:update", scheduleDevicesRefresh);
        // 다른 관리 앱이 목록을 바꿨다(등록·해지·이름·집 화면·숙제·잠금) → 같은 디바운스로 다시 읽는다.
        sock.on("devices:changed", scheduleDevicesRefresh);
        // 손들기 알림 — 서버가 수신자를 역할별로 제한해 보낸다(관장=전부 / 사범=본인 제어 중인 기기만).
        // 화면 무관 토스트 + 소리. '확인'으로 어느 화면에서든 바로 손 내림.
        sock.on("hand:raised", ({ deviceId }: { deviceId: string }) => {
            const name =
                devicesRef.current.find((d) => d.id === deviceId)?.name ??
                tp("fallback.device");
            beepHand();
            showToast(tp("toast.handRaised", { name }), undefined, {
                label: tp("action.ok"),
                onClick: () => {
                    sockRef.current?.emit("command", {
                        action: "LOWER_HAND",
                        deviceId,
                    });
                    setStates((m) =>
                        m[deviceId]?.handRaised
                            ? {
                                  ...m,
                                  [deviceId]: {
                                      ...m[deviceId],
                                      handRaised: false,
                                  },
                              }
                            : m,
                    );
                },
            });
        });
        // 관장이 보낸 메시지 — 부관리자 화면 무관 토스트(+ 손들기와 다른 알림음). 라이브 전용(오프라인이면 못 받음).
        // 서버가 수신자를 정해 보낸다(특정 부관리자 개인 룸 / 전체 부관리자 룸) — 관장/디바이스에는 안 온다.
        sock.on(
            "notify:message",
            ({ from, text, messageId }: { from?: string; text?: string; messageId?: string }) => {
                if (!text) return;
                beepMsg();
                // 관장 전달사항 — 자동으로 안 사라지고 '확인'을 눌러야만 닫힌다(sticky). 헤더=보낸이, 본문=내용.
                // '확인' 누르면 읽음 확인(ack)을 서버로 보내 관장이 확인 여부를 볼 수 있게 한다.
                showToast(
                    text,
                    <I.megaphone size={17} style={{ color: "var(--accent)" }} />,
                    { label: tp("action.ok"), onClick: () => { if (messageId) sock.emit("notify:ack", { messageId }); } },
                    { sticky: true, title: tp("misc.notice", { from: from ?? tc("role.owner") }) },
                );
            },
        );
        return () => {
            sock?.close();
            sockRef.current = null;
            setConnected(false);
        };
    }, [dojang?.id, applySync, beepHand, beepMsg, showToast]);

    /* ---- 가로 태블릿 감지 — 2단 레이아웃 토글 (SSR 안전: 초기 false, matchMedia는 클라에서만) ---- */
    useEffect(() => {
        // 경계 900×600 → 760×500: 안드로이드 태블릿(갤럭시탭)은 DPR 로 가로 CSS 높이가 600 미만이라
        // 아이패드와 달리 폰으로 오분류됐다. CSS(tedu.css)의 2단 게이트와 반드시 동일하게 유지.
        const mq = window.matchMedia(
            "(orientation: landscape) and (min-width: 760px) and (min-height: 500px)",
        );
        const apply = () => setIsWide(mq.matches);
        apply();
        mq.addEventListener("change", apply);
        return () => mq.removeEventListener("change", apply);
    }, []);

    /* ---- interpolation tick — 제어 화면 + 기기 목록(devices 탭)에서 재생 중인 기기가 있으면 ---- */
    useEffect(() => {
        const anyDevicePlaying = Object.values(states).some(
            (s) => s?.online && s.status === "playing",
        );
        let anyPlaying = false;
        if (
            isWide &&
            tab === "devices" &&
            (screen === "main" || screen === "control")
        ) {
            // 2단: 왼쪽 그리드 + 오른쪽 제어가 함께 보임 → 재생 중 기기 하나라도 있으면 틱
            anyPlaying = anyDevicePlaying;
        } else if (screen === "control") {
            anyPlaying = selId ? states[selId]?.status === "playing" : false;
        } else if (screen === "main" && tab === "devices") {
            anyPlaying = anyDevicePlaying;
        }
        if (!anyPlaying) return;
        const iv = setInterval(() => setNow(Date.now()), 250);
        return () => clearInterval(iv);
    }, [screen, tab, selId, states, isWide]);

    /* ---- device views (record + live state + presence) ---- */
    // 기기별 '수업 중 진입 시각' — 그리드 위 묶음(재생 중)을 **재생 순서**로 세우는 기준(2026-09-15).
    // 서버 보고엔 재생 시작 시각이 없어 이 앱이 상태 전이를 본 순간을 적어 둔다(세션 한정·DB 미저장).
    // 🔑 이미 적혀 있으면 안 건드린다(일시정지·재생 반복해도 자리 고정). idle/오프라인이면 지운다.
    const activeAtRef = useRef<Record<string, number>>({});
    const devices: DeviceView[] = useMemo(() => {
        const seenAt = activeAtRef.current;
        const now = Date.now();
        return deviceRecords.map((r) => {
            const online = presence[r.id] ?? states[r.id]?.online ?? false;
            const active = online && (states[r.id]?.status ?? "idle") !== "idle";
            if (active) seenAt[r.id] ??= now; else delete seenAt[r.id];
            return mergeState(
                {
                    ...defaultDeviceView(r.id, r.name, r.lastSeen),
                    touchLocked: r.touchLocked ?? false,
                    atHome: r.atHome ?? false,
                    deviceName: r.deviceName,
                    studentId: r.studentId ?? null,
                    homeworkCompleted: r.homeworkCompleted ?? 0,
                    rankFormId: r.rankFormId ?? null,
                    homePlaylistId: r.homePlaylistId ?? null,
                    homeworkDoneAt: r.homeworkDoneAt ?? null,
                    hasPushToken: r.hasPushToken ?? false,
                    platform: r.platform ?? null,
                    cameraOk: r.cameraOk ?? null,
                    fromStore: r.fromStore ?? null,
                    activeAt: seenAt[r.id] ?? null,
                },
                states[r.id],
                online,
            );
        });
    }, [deviceRecords, states, presence]);

    // 기기 상태 시그니처 — 상태가 실제로 바뀔 때만 동기화 트리거(매 렌더 방지).
    const statusSig = useMemo(
        () =>
            devices
                .map((d) => `${d.id}:${d.online ? d.status : "off"}`)
                .sort()
                .join("|"),
        [devices],
    );
    useEffect(() => {
        devicesRef.current = devices;
    }, [devices]);
    // 기기 상태가 바뀌면 4초 디바운스 후 서버 동기화 — 시작/마감을 거의 즉시 반영(짧은 깜빡임은 디바운스가 흡수).
    useEffect(() => {
        if (!dojang) return;
        const id = dojang.id;
        const t = setTimeout(() => syncTraining(id, devicesRef.current), 4000);
        return () => clearTimeout(t);
    }, [statusSig, dojang, syncTraining]);
    const openRef = useRef(false); // 열린 수련세션 유무(하트비트에서 멈춤 중 폴링 판단용)
    // 60초 하트비트:
    //  · 재생 지속 → syncTraining 으로 lastActivityAt 갱신.
    //  · 멈춤만 + 열린 세션 → refreshActiveSessions 로 가볍게 폴링해 서버의 30분 자동마감(reap)을
    //    화면에 반영(타이머가 30분에 같이 사라지게). 세션이 없으면 아무 것도 안 함.
    // 앱이 앞으로 돌아오면(홈 화면 앱 다시 열기·탭 전환) 목록을 한 번 다시 읽는다 — 뒤에 있던 사이
    // 놓친 신호(다른 관리 앱의 해지·등록 등)를 덮는다. 앱이 보이는 동안엔 소켓 신호가 맡는다.
    useEffect(() => {
        if (!dojang) return;
        const id = dojang.id;
        const onVis = () => {
            if (document.visibilityState === "visible") void refreshDevices(id);
        };
        document.addEventListener("visibilitychange", onVis);
        return () => document.removeEventListener("visibilitychange", onVis);
    }, [dojang, refreshDevices]);
    useEffect(() => {
        if (!dojang) return;
        const id = dojang.id;
        const iv = setInterval(() => {
            const list = devicesRef.current;
            if (list.some((d) => d.online && d.status === "playing"))
                syncTraining(id, list);
            else if (openRef.current) refreshActiveSessions(id);
            // 숙제를 '했다'는 기기가 DB 에 직접 찍는다(소켓으로 안 온다) → 걸린 숙제가 있는 집 화면이
            // 하나라도 있으면 목록을 다시 읽어 타일의 '안 했어요'가 제때 '오늘 했어요'로 바뀌게 한다.
            // 🔑 조건을 다는 이유: 집 화면이 없는 보통 도장에는 요청이 한 번도 안 는다.
            if (list.some((d) => d.atHome && d.homePlaylistId)) void refreshDevices(id);
        }, 60000);
        return () => clearInterval(iv);
    }, [dojang, syncTraining, refreshActiveSessions, refreshDevices]);
    // 경과 타이머 — 진행 중 세션이 하나라도 있으면 250ms마다 현재시각만 갱신(리렌더 트리거).
    // 현재시각은 monoNow()(뒤로 안 가는 시계) — Date.now() 는 기기 시계 보정 때 타이머를 1초 뒤로 물렸다(10-15).
    // 표시값은 sessionSecFor 가 (now - 시작시각)으로 계산 — 일시정지/정지에도 멈추지 않고 논스톱.
    // 250ms 주기는 재생위치 클럭(now, line 723)과 동일 — 경과/남은 타이머의 초 표시가 거의 같은
    // 순간에 넘어가 흐름이 어긋나 보이지 않게 한다(1초 인터벌이면 실제 초 변경보다 최대 1초 늦게 툭 넘어감).
    const anySession = Object.keys(activeMap).length > 0;
    useEffect(() => {
        openRef.current = anySession; // 하트비트가 멈춤 중 폴링 여부 판단에 사용(최신 열린세션 유무)
    }, [anySession]);
    useEffect(() => {
        if (!anySession) return;
        const iv = setInterval(() => setSessionNow(monoNow()), 250);
        return () => clearInterval(iv);
    }, [anySession]);
    // 매 틱마다 기기별 '얼린(hidden) 누적 ms'를 갱신 — 플레이어가 백그라운드(최소화)인 동안의 경과분만 더한다.
    // (플레이어 실제 재생이 멈춘 구간이므로 수련 타이머에서 제외).
    // ⚠️큰 dt(컨트롤러 탭이 스로틀됐거나 화면잠금으로 틱이 5초+ 벌어진 구간)도, 그 동안 기기가 hidden 이었으면
    //   '전부' 빼야 타이머가 얼린 값을 유지한다. 예전 dt<5000 상한은 이 큰 구간을 못 빼서 복귀 시 sessionNow 만
    //   앞서 점프 → 수련 타이머가 안 움직이다 갑자기 9~10초 튀는 원인이었다. (hidden 인 동안 뺀 값은 경과분을
    //   못 넘으므로 sessionSecFor 가 음수/역행할 일은 없다.)
    useEffect(() => {
        if (!anySession || !sessionNow) return;
        const acc = hiddenAccumRef.current;
        for (const id of Object.keys(activeMap)) {
            const rec = acc[id] ?? (acc[id] = { ms: 0, lastTick: sessionNow });
            const dt = sessionNow - rec.lastTick;
            rec.lastTick = sessionNow;
            if (statesRef.current[id]?.hidden && dt > 0) rec.ms += dt;
        }
        for (const id of Object.keys(acc))
            if (!(id in activeMap)) delete acc[id]; // 마감된 세션 정리
    }, [sessionNow, activeMap, anySession]);
    // 기기별 경과초 — 제어화면 타이머용. 시작시각부터 벽시계 경과에서 '얼린(백그라운드) 시간'을 빼 표시.
    // 일반 일시정지/정지는 플레이어가 살아있어(포그라운드) 계속 흐르고, 플레이어가 최소화된 구간만 멈춘다.
    const sessionSecFor = (deviceId: string | null) => {
        const start = deviceId ? activeMap[deviceId] : undefined;
        if (start == null || deviceId == null) return null;
        const hiddenMs = hiddenAccumRef.current[deviceId]?.ms ?? 0;
        return Math.max(0, Math.floor((sessionNow - start - hiddenMs) / 1000));
    };
    const selDevice = devices.find((d) => d.id === selId) ?? null;
    const selPl = selPlId ? (plById[selPlId] ?? null) : null;

    // ---- 하드웨어/브라우저 뒤로가기(goBack)용 최신 네비 스냅샷 — 매 렌더 동기 갱신(스테일 클로저 회피) ----
    const navStateRef = useRef<{
        screen: Screen;
        tab: TabId;
        selId: string | null;
        isWide: boolean;
        hasDojang: boolean;
        overAddToPl: boolean;
        overAddQueue: boolean;
        overLocalPlay: boolean;
        overModal: boolean;
        batchSheet: boolean;
        sheet: boolean;
        selecting: boolean;
        plReturn: "main" | "control";
        uploadReturnPl: string | null;
        selDevice: DeviceView | null;
        selPl: PlaylistView | null;
    }>({
        screen: "dojang",
        tab: "devices",
        selId: null,
        isWide: false,
        hasDojang: false,
        overAddToPl: false,
        overAddQueue: false,
        overLocalPlay: false,
        overModal: false,
        batchSheet: false,
        sheet: false,
        selecting: false,
        plReturn: "main",
        uploadReturnPl: null,
        selDevice: null,
        selPl: null,
    });
    navStateRef.current = {
        screen,
        tab,
        selId,
        isWide,
        hasDojang: !!dojang,
        overAddToPl: !!addToPlVideo,
        overAddQueue: !!addQueue,
        overLocalPlay: !!localPlay,
        overModal: !!modal,
        batchSheet,
        sheet,
        selecting,
        plReturn,
        uploadReturnPl,
        selDevice,
        selPl,
    };
    // LibraryScreen 내부 뎁스(묶음/검색) 뒤로 소비자 — 컴포넌트가 자기 back 핸들러를 등록, goBack 이 탭 폴백 전에 먼저 호출.
    const libBackRef = useRef<(() => boolean) | null>(null);

    // 내 화면(컨트롤러)에도 응원 이펙트 — 보내는 맛. id 증가로 같은 종류도 재트리거.
    // 🛑 deviceId 를 같이 들고 있는다 — 응원을 보낸 **그 기기의 미러**에만 그린다.
    //    없으면 발사 직후 다른 기기 조종판으로 넘어갔을 때 엉뚱한 기기 위에서 터진다.
    const [myCheer, setMyCheer] = useState<{ kind: string; id: number; deviceId: string } | null>(
        null,
    );
    const myCheerSeq = useRef(0);
    const showMyCheer = useCallback((kind: string, deviceId: string) => {
        myCheerSeq.current += 1;
        setMyCheer({ kind, id: myCheerSeq.current, deviceId });
    }, []);
    // 이펙트를 지우는 책임은 '상태를 가진 여기'가 진다. 오버레이(CheerOverlay) 안 타이머만 믿으면,
    // 수업 종료로 제어화면이 대기(IdleBoard)로 갈아끼워지는 순간 오버레이가 언마운트되며 타이머가 걷히고
    // myCheer 가 남는다 → 다음 영상을 재생해 미러가 돌아오면 저절로 축하 효과가 다시 터졌다(실제 버그).
    useEffect(() => {
        if (!myCheer) return;
        const t = setTimeout(() => setMyCheer(null), 3000);
        return () => clearTimeout(t);
    }, [myCheer]);

    /* ---- socket command (targets selected device) ---- */
    /* ---- 명령이 '내 다른 기기 때문에' 거절됐나 ----
       잠금이 제어화면에만 있으면 그리드에서 누르는 재생/정지가 그대로 새 나가 결국 서로 뺏는다.
       서버가 명령 자체를 거절하므로(reason=in_use), 그때는 실패 안내 대신 이유를 말해준다. */
    const notedInUse = useCallback(
        (res: CommandResult | undefined) => {
            if (res?.ok !== false || res.reason !== "in_use") return false;
            showToast(
                tp("toast.inUseElsewhere", {
                    device: tp(`agent.${res.by ?? "other"}` as "agent.other"),
                }),
                <I.lock size={16} style={{ color: "var(--warn)" }} />,
            );
            return true;
        },
        [showToast, tp],
    );

    const cmd = useCallback(
        (action: string, arg?: number | string | string[]) => {
            const dev = devices.find((d) => d.id === selId);
            if (!dev) return;
            // 수업 종료는 오프라인이어도 서버 수련 세션은 닫아야 한다(세션 마감은 REST — 기기 온라인 불필요).
            // 소켓으로 보내는 플레이어 리셋만 온라인일 때 가능.
            if (action === "GO_HOME") {
                endTrainingFor([dev.id]);
                closeCamsForRef.current?.([dev.id]); // 종료 = 카메라도 내린다(기기·관장 폰 양쪽)
            }
            if (!dev.online) {
                // 오프라인: 세션은 위에서 닫았음. 소켓 명령은 못 보내니 종료. 수업 종료는 조용히 마감, 그 외만 안내.
                if (action !== "GO_HOME")
                    showToast(
                        tp("toast.deviceOfflineCheck"),
                        <I.wifiOff
                            size={16}
                            style={{ color: "var(--danger)" }}
                        />,
                    );
                return;
            }
            let payload: Command | null = null;
            switch (action) {
                case "PLAY":
                    payload = { action: "PLAY", deviceId: dev.id };
                    break;
                case "PAUSE":
                    payload = { action: "PAUSE", deviceId: dev.id };
                    break;
                case "STOP":
                    payload = { action: "STOP", deviceId: dev.id };
                    markIdleOptimistic([dev.id], { keepPlaylist: true }); // 배지 즉시 대기로(재생목록은 유지 — 정지는 잠깐 멈춤)
                    break; // 정지는 세션을 끝내지 않음(잠깐 멈춤) — 마감은 '수업 종료'만

                case "GO_HOME": {
                    payload = { action: "GO_HOME", deviceId: dev.id }; // 세션 마감은 위에서 처리 — 여기선 플레이어 리셋만
                    markIdleOptimistic([dev.id]); // 배지 즉시 대기로(보고 왕복 안 기다림) — 위 markIdleOptimistic 참고
                    // 오탭 가드 — '수업 전체 종료'와 동일한 '되돌리기' 토스트로 실수 복구(재생목록·영상·재생상태 복원, 위치 제외).
                    if (dev.playlistId && dev.status !== "idle") {
                        const snap = {
                            id: dev.id,
                            playlistId: dev.playlistId,
                            index: dev.index,
                            wasPlaying: dev.status === "playing",
                        };
                        showToast(
                            tp("toast.classEnded"),
                            <I.stop
                                size={16}
                                style={{ color: "var(--live)" }}
                            />,
                            {
                                label: tp("action.undo"),
                                onClick: () => {
                                    sockRef.current?.emit("command", {
                                        action: "LOAD_PLAYLIST",
                                        deviceId: snap.id,
                                        playlistId: snap.playlistId,
                                        index: snap.index,
                                        autoplay: snap.wasPlaying,
                                    });
                                    showToast(
                                        tp("toast.classRestored"),
                                        <I.cast
                                            size={16}
                                            style={{ color: "var(--live)" }}
                                        />,
                                    );
                                },
                            },
                        );
                    }
                    break;
                }
                case "RELOAD":
                    payload = { action: "RELOAD", deviceId: dev.id }; // 화면 새로고침 — 플레이어가 웹페이지를 다시 로드(최신 배포본 반영)
                    break;
                case "CHEER": // 응원 이펙트 — arg=종류(clap/heart/confetti/stamp)
                    payload = {
                        action: "CHEER",
                        deviceId: dev.id,
                        kind: String(arg ?? "confetti"),
                    };
                    // 🛑 내 화면 이펙트는 **기기가 받았을 때만**(아래 emit 콜백). 예전엔 여기서 바로 터뜨려,
                    //    기기가 안 받으면 "명령이 안 갔어요" 빨간 토스트와 축하 효과가 나란히 떴다
                    //    (재생목록 '불러옴' 토스트에서 이미 한 번 걷어낸 것과 같은 모순).
                    break;
                case "NEXT":
                    payload = { action: "NEXT", deviceId: dev.id };
                    break;
                case "PREV":
                    payload = { action: "PREV", deviceId: dev.id };
                    break;
                case "JUMP":
                    payload = {
                        action: "JUMP",
                        deviceId: dev.id,
                        index: Number(arg) || 0,
                    };
                    break;
                case "SEEK":
                    payload = {
                        action: "SEEK",
                        deviceId: dev.id,
                        positionSec: Number(arg) || 0,
                    };
                    break;
                case "SET_VOLUME":
                    payload = {
                        action: "SET_VOLUME",
                        deviceId: dev.id,
                        volume: Number(arg) || 0,
                    };
                    break;
                case "SET_RATE":
                    payload = {
                        action: "SET_RATE",
                        deviceId: dev.id,
                        rate: Number(arg) || 1,
                    };
                    break;
                case "SET_LOOP": {
                    // arg = "a,b" (초)
                    const [a, b] = String(arg).split(",").map(Number);
                    if (!(a >= 0 && b > a)) return;
                    payload = { action: "SET_LOOP", deviceId: dev.id, a, b };
                    break;
                }
                case "CLEAR_LOOP":
                    payload = { action: "CLEAR_LOOP", deviceId: dev.id };
                    break;
                case "SET_REPEAT":
                    payload = {
                        action: "SET_REPEAT",
                        deviceId: dev.id,
                        repeat: Number(arg) === 1,
                    };
                    break;
                case "SET_ITEM_REPEAT": {
                    // arg = "videoId,count" — 세션 영상별 반복
                    const [vid, c] = String(arg).split(",");
                    if (!vid) return;
                    payload = {
                        action: "SET_ITEM_REPEAT",
                        deviceId: dev.id,
                        videoId: vid,
                        count: Number(c) || 1,
                    };
                    break;
                }
                case "SET_TOUCH_LOCK":
                    payload = {
                        action: "SET_TOUCH_LOCK",
                        deviceId: dev.id,
                        locked: Number(arg) === 1,
                    };
                    break;
                case "REORDER_QUEUE": {
                    if (!Array.isArray(arg) || arg.length === 0) return;
                    payload = {
                        action: "REORDER_QUEUE",
                        deviceId: dev.id,
                        items: arg,
                    };
                    break;
                }
                case "SYNC_PLAYLIST":
                    payload = {
                        action: "SYNC_PLAYLIST",
                        deviceId: dev.id,
                        playlistId: String(arg),
                    };
                    break;
                case "LOAD_PLAYLIST":
                    payload = {
                        action: "LOAD_PLAYLIST",
                        deviceId: dev.id,
                        playlistId: String(arg),
                        autoplay: true,
                    };
                    break;
                case "LOAD_PLAYLIST_AT": {
                    // arg = "plId,index,autoplay(1|0)" — 대기 화면 카드에서 시작 위치 지정 재생
                    const [plId, idx, ap] = String(arg).split(",");
                    if (!plId) return;
                    payload = {
                        action: "LOAD_PLAYLIST",
                        deviceId: dev.id,
                        playlistId: plId,
                        index: Number(idx) || 0,
                        autoplay: ap === "1",
                    };
                    break;
                }
                default:
                    return;
            }
            if (!payload) return;
            const t0 = performance.now();
            // 성공 토스트는 ack 가 ok 일 때만 — 실패(오프라인 등)에도 "불러옴"이 뜨던 모순 제거.
            sockRef.current?.emit("command", payload, (res: CommandResult) => {
                diag(
                    "cmd",
                    `${action} ack ${(performance.now() - t0).toFixed(0)}ms ok=${res?.ok ?? false}`,
                );
                // 재생바 이동 지연을 구간별로 가르는 기록 — 여기는 '관리 앱 → 서버 → 기기 → 응답' 왕복.
                // 기기 쪽 '받고 나서 다시 흐르기까지'는 수련 화면의 seek_resume(2026-10-19 스탠바이미 딜레이 신고).
                if (action === "SEEK") perf("seek_ack", performance.now() - t0, { deviceId: dev.id }, !!res?.ok);
                if (!res?.ok) {
                    if (notedInUse(res)) return;
                    showToast(
                        tp("toast.cmdFailedRetry"),
                        <I.wifiOff
                            size={16}
                            style={{ color: "var(--danger)" }}
                        />,
                    );
                    return;
                }
                if (action === "LOAD_PLAYLIST" || action === "LOAD_PLAYLIST_AT")
                    showToast(
                        tp("toast.playlistLoaded"),
                        <I.cast size={16} style={{ color: "var(--live)" }} />,
                    );
                // 기기 화면에 실제로 터진 뒤에야 내 미러에도 같이 터뜨린다(보내는 맛).
                if (action === "CHEER") showMyCheer(String(arg ?? "confetti"), dev.id);
            });
            // 시트 닫기는 낙관적으로(전송 시도 즉시) — 실패해도 화면이 떠있을 이유는 없음.
            if (action === "LOAD_PLAYLIST" || action === "LOAD_PLAYLIST_AT")
                setSheet(false);
        },
        [
            devices,
            selId,
            showToast,
            endTrainingFor,
            showMyCheer,
            markIdleOptimistic,
        ],
    );

    /* ---- 수업 전체 종료 — 온라인 기기 전부 대기 화면으로(GO_HOME) ---- */
    const endAllClasses = useCallback(() => {
        // 🛑 '전체'는 도장에 있는 화면까지다 — 집 화면(atHome)은 뺀다. 도장에서 수업을 끝냈다고
        //    학생 집 거실 화면이 같이 꺼지면 그 집에선 아무도 이유를 모른다. 집 화면을 끄려면
        //    그 타일을 직접 열거나 다중선택으로 고르면 된다.
        const online = devices.filter((d) => d.online && atDojang(d));
        const homeIds = new Set(devices.filter((d) => !atDojang(d)).map((d) => d.id));
        // 세션 마감 대상 = 진행 중인(activeMap) 모든 기기(오프라인에 남은 세션 포함) + 온라인 기기.
        // 오프라인 기기의 열린 세션도 여기서 닫는다(안 그러면 30분 reap 까지 '진행 중' 잔존).
        const endIds = Array.from(
            new Set([...Object.keys(activeMap), ...online.map((d) => d.id)]),
        ).filter((id) => !homeIds.has(id));
        if (endIds.length === 0) {
            showToast(
                tp("toast.noClassToEnd"),
                <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
            );
            return;
        }
        // 종료 직전 상태 스냅샷 — '되돌리기'로 재생목록·위치 복원용 (재생/일시정지 중이던 기기만)
        // 위치(positionSec)는 새 소스 attach 시 초기화돼 복원이 불안정 → 재생목록·영상·재생상태만 되돌린다
        const snapshot = online
            .filter((d) => d.playlistId && d.status !== "idle")
            .map((d) => ({
                id: d.id,
                playlistId: d.playlistId!,
                index: d.index,
                wasPlaying: d.status === "playing",
            }));
        // 되묻지 않고 즉시 종료 — 온라인 기기 전부 대기 화면으로.
        // 🔑 한 대가 명령을 놓쳐도 알아챈다(2026-10-21 신고: 유튜브 재생 중이던 스탠바이미만 첫 번째 '전체 종료'에서
        //    안 꺼지고 두 번째에 꺼짐 — 운영 기록상 그 기기만 첫 명령을 처리한 흔적이 없었다. 로컬 재현은 안 됨).
        //    예전엔 던지기만 하고 응답을 안 봐서, 빠지면 배지만 잠깐 '대기'였다가 기기 보고로 '재생 중'에 돌아왔다.
        //    ① 응답이 실패(기기 3초 무응답 등)면 0.8초 뒤 한 번 더 보낸다.
        //    ② 3.5초 뒤에도 같은 영상을 '재생 중'으로 보고하면 한 번 더 보낸다(받았다고 했는데 안 멈춘 경우).
        //    '되돌리기'를 눌렀으면 ②는 하지 않는다(되돌린 재생을 다시 끄면 안 된다).
        let undone = false;
        const sendHome = (id: string, retry: boolean) =>
            sockRef.current?.emit(
                "command",
                { action: "GO_HOME", deviceId: id } satisfies Command,
                (res: CommandResult) => {
                    if (!retry || undone || res?.ok || res?.reason === "not_found" || res?.reason === "in_use") return;
                    setTimeout(() => { if (!undone) sendHome(id, false); }, 800);
                },
            );
        online.forEach((d) => sendHome(d.id, true));
        const playingBefore = online
            .filter((d) => d.status === "playing" && d.videoId)
            .map((d) => ({ id: d.id, videoId: d.videoId }));
        if (playingBefore.length)
            setTimeout(() => {
                if (undone) return;
                const still = playingBefore.filter((p) => {
                    const s = statesRef.current[p.id];
                    return s?.status === "playing" && s.videoId === p.videoId;
                });
                if (!still.length) return;
                still.forEach((p) => sendHome(p.id, false));
                markIdleOptimistic(still.map((p) => p.id));
            }, 3500);
        markIdleOptimistic(online.map((d) => d.id)); // 배지 즉시 대기로(보고 왕복 안 기다림)
        endTrainingFor(endIds); // 수련 세션 즉시 마감(오프라인 잔존 세션 포함, 상태 관찰 대기 없이)
        closeCamsForRef.current?.(endIds); // 종료 = 카메라도 내린다(기기·관장 폰 양쪽)
        const undo =
            snapshot.length === 0
                ? undefined
                : {
                      label: tp("action.undo"),
                      onClick: () => {
                          undone = true;
                          snapshot.forEach((s) =>
                              sockRef.current?.emit("command", {
                                  action: "LOAD_PLAYLIST",
                                  deviceId: s.id,
                                  playlistId: s.playlistId,
                                  index: s.index,
                                  autoplay: s.wasPlaying,
                              }),
                          );
                          showToast(
                              tp("toast.classRestoredN", { count: snapshot.length }),
                              <I.cast
                                  size={16}
                                  style={{ color: "var(--live)" }}
                              />,
                          );
                      },
                  };
        showToast(
            tp("toast.classEndedN", { count: endIds.length }),
            <I.stop size={16} style={{ color: "var(--live)" }} />,
            undo,
        );
    }, [devices, activeMap, showToast, endTrainingFor, markIdleOptimistic]);

    /* ---- 전체 일시정지 — 재생 중인 온라인 기기 전부 PAUSE ---- */
    const pauseAllClasses = useCallback(() => {
        const playing = devices.filter(
            (d) => d.online && d.status === "playing" && atDojang(d),
        );
        if (playing.length === 0) return;
        playing.forEach((d) =>
            sockRef.current?.emit("command", {
                action: "PAUSE",
                deviceId: d.id,
            }),
        );
        showToast(
            tp("toast.pausedN", { count: playing.length }),
            <I.pause size={16} style={{ color: "var(--warn)" }} />,
        );
    }, [devices, showToast]);

    /* ---- 전체 재생 — 일시정지된 온라인 기기 전부 PLAY ---- */
    const resumeAllClasses = useCallback(() => {
        const paused = devices.filter(
            (d) => d.online && d.status === "paused" && atDojang(d),
        );
        if (paused.length === 0) return;
        paused.forEach((d) =>
            sockRef.current?.emit("command", {
                action: "PLAY",
                deviceId: d.id,
            }),
        );
        showToast(
            tp("toast.playingN", { count: paused.length }),
            <I.play size={16} style={{ color: "var(--live)" }} />,
        );
    }, [devices, showToast]);

    /* ---- 말로 조작(🎤) — 들은 문장(후보 여러 개)을 lib/voice-command 로 풀어, 버튼이 보내던 명령을 그대로 보낸다 ----
       대상은 도장 화면만(집 화면 제외 — '전부'에 수련생 집 TV 가 섞이면 안 된다). 기기를 말하지 않으면 지금 열어 둔 기기.
       🛑 시험 중: 허용 계정만(owner.voice). 2026-10-22. */
    const [voiceOpen, setVoiceOpen] = useState(false);
    const tvo = useTranslations("control.voice");
    const runVoice = useCallback(
        (alts: string[], strict = false): VoiceOutcome => {
            const pool = devices.filter(atDojang);
            const vctx = {
                devices: pool.map((d) => ({ id: d.id, name: d.name, online: d.online })),
                playlists: playlists.map((p) => ({
                    id: p.id,
                    name: p.name,
                    items: p.items.map((id) => ({ id, title: byId[id]?.title ?? "" })),
                })),
                defaultIds: selId && pool.some((d) => d.id === selId) ? [selId] : [],
                strict, // 계속 듣기 — 기기 이름·"전부"가 든 말만(아이들에게 하는 말이 명령으로 가지 않게)
            };
            const tries = alts.map((a) => parseVoiceCommand(a, vctx));
            const hit = tries.find((r) => r.ok);
            perf("voice_cmd", undefined, {
                heard: alts.slice(0, 3),
                ok: !!hit,
                action: hit?.ok ? hit.action : undefined,
                reason: tries[0] && !tries[0].ok ? tries[0].reason : undefined,
                strict,
            });
            if (!hit || !hit.ok) {
                const r = tries[0];
                const heard = alts[0] ?? "";
                const reason = r && !r.ok ? r.reason : "no_action";
                // 계속 듣기에서 명령이 아닌 말·기기 이름 없는 말은 조용히 넘긴다(빨간 안내가 수업 내내 뜨지 않게).
                if (strict && (reason === "no_action" || reason === "not_addressed"))
                    return { ok: false, quiet: true, text: reason === "not_addressed" ? tvo("notAddressed") : "" };
                return {
                    ok: false,
                    text: reason === "no_device" ? tvo("noDevice") : reason === "offline" ? tvo("offline")
                        : reason === "no_playlist" ? tvo("noPlaylist", { heard }) : tvo("notUnderstood", { heard }),
                };
            }
            const targets = pool.filter((d) => hit.targets.includes(d.id));
            const ack = (res: CommandResult) => {
                if (!res?.ok && !notedInUse(res))
                    showToast(tp("toast.cmdFailedRetry"), <I.wifiOff size={16} style={{ color: "var(--danger)" }} />);
            };
            const send = (payload: Command) => sockRef.current?.emit("command", payload, ack);
            if (hit.action === "end") {
                const ids = targets.map((d) => d.id);
                endTrainingFor(ids);
                closeCamsForRef.current?.(ids);
            }
            for (const d of targets) {
                switch (hit.action) {
                    case "load":
                        send({ action: "LOAD_PLAYLIST", deviceId: d.id, playlistId: hit.playlistId!, index: hit.index ?? 0, autoplay: true });
                        break;
                    case "play": send({ action: "PLAY", deviceId: d.id }); break;
                    case "pause": send({ action: "PAUSE", deviceId: d.id }); break;
                    case "next": send({ action: "NEXT", deviceId: d.id }); break;
                    case "prev": send({ action: "PREV", deviceId: d.id }); break;
                    case "restart": send({ action: "SEEK", deviceId: d.id, positionSec: 0 }); break;
                    case "volume_up": send({ action: "SET_VOLUME", deviceId: d.id, volume: Math.min(1, Math.round((d.volume + 0.2) * 10) / 10) }); break;
                    case "volume_down": send({ action: "SET_VOLUME", deviceId: d.id, volume: Math.max(0, Math.round((d.volume - 0.2) * 10) / 10) }); break;
                    case "mute": send({ action: "SET_VOLUME", deviceId: d.id, volume: 0 }); break;
                    case "end": send({ action: "GO_HOME", deviceId: d.id }); break;
                }
            }
            const names = targets.map((d) => d.name).join(", ");
            const pl = hit.playlistId ? playlists.find((p) => p.id === hit.playlistId) : null;
            const name = pl ? `${tkdPlaylist(pl.name, locale)}${hit.view ? ` ${tvo(`view.${hit.view}`)}` : ""}` : "";
            const text = tvo(`done.${hit.action}`, { devices: names, name });
            showToast(text, <I.mic size={16} style={{ color: "var(--live)" }} />);
            return { ok: true, text };
        },
        [devices, playlists, byId, selId, notedInUse, showToast, endTrainingFor, locale, tvo, tp],
    );

    /* ---- 손들기 '확인' — 해당 기기의 든 손을 내림(LOWER_HAND). 낙관적으로 배지 즉시 해제 ---- */
    const lowerHand = useCallback((d: DeviceView) => {
        sockRef.current?.emit("command", {
            action: "LOWER_HAND",
            deviceId: d.id,
        });
        setStates((m) => {
            const st = m[d.id];
            if (!st?.handRaised) return m;
            return { ...m, [d.id]: { ...st, handRaised: false } };
        });
    }, []);

    /* ===== 기기 다중선택(즉석) — 그때그때 고른 기기들에 일괄 명령 ===== */
    // 선택 모드 종료 — 선택 집합·시트 정리
    const exitSelecting = useCallback(() => {
        setSelecting(false);
        setSelIds(new Set());
        setBatchSheet(false);
    }, []);
    // 자동 시연을 끝낼 때 하는 일 한 벌 — '시연 종료'로 나가든, 화면이 꺼져 저절로 멈추든 같다.
    // 🛑 원상복구(배속·구간반복)는 여기 있어야 한다. 부스는 하루 종일 돌고 다음 사람이 곧바로 잡는다.
    const stopDemo = useCallback(() => {
        setDemo(null);
        exitSelecting();
        if (selId) { cmd("CLEAR_LOOP"); cmd("SET_RATE", 1); }
    }, [exitSelecting, selId, cmd]);
    // ⭐ 코스 하나를 **처음부터** 시작하는 한 벌. '이어서 보기'가 코스 사이에서 이걸 다시 부른다.
    //    🛑 칸 목록을 이어 붙이는 방식으로 만들지 말 것 — 코스마다 출발 준비가 다르다(①은 재생
    //       중인 화면까지 대기로 되돌리고, 전부 기기 목록에서 출발하며, 선택 모드를 푼다).
    //       이어 붙이면 ②의 첫 칸(기기 카드 꾹 누르기)이 ①이 남겨 둔 조종판 안에서 허공을 짚는다.
    const beginCourse = useCallback((c: DemoCourse) => {
        // ⚠️ 예전엔 여기서 켜져 있는 화면을 전부 껐다(GO_HOME). 앞 사람이 틀어 놓고 간
        //    영상이 남아 있으면 조종판이 품새 목록이 아니라 재생 화면으로 열려 첫 칸이
        //    딴 데를 짚었기 때문이다. 지금은 '고르는 자리'를 세 곳 다 적어 두어
        //    (pick-form / sheet-play / load-playlist) 어느 상태에서 시작해도 돌아간다.
        //    🛑 그래서 **끄지 않는다**(사용자 지적, 2026-08-07) — 부스에서 잘 나오고
        //    있는 화면을 시연 누를 때마다 꺼 버리는 건 그 자체로 이상하다.
        // ⭐ 일시정지로 멈춰 있는 화면은 **대기로 되돌린다**(사용자 결정, 2026-08-07).
        //    앞 사람이 세워 두고 간 화면에서 이어 붙이면 '처음부터 트는 그림'이 안 나온다.
        //    🛑 재생 중인 화면은 그대로 둔다 — 잘 나오고 있는 걸 끄는 건 그 자체로 이상하다.
        // ⭐ 코스①(fromIdle)은 **재생 중인 화면까지** 대기로 되돌리고 출발한다
        //    (사용자 결정, 2026-08-10). '대기 화면에서 처음부터 트는' 이야기라
        //    이미 나오고 있으면 그 그림이 안 나오고, 조종판도 목록이 아닌 재생 화면으로
        //    열려 뒤 칸들이 줄줄이 어긋난다.
        //    🛑 ②③ 까지 끄지 말 것 — 그게 2026-08-07 에 반려된 그 동작이다.
        devices
            .filter((d) => d.online && (c.fromIdle ? d.status !== "idle" : d.status === "paused"))
            .forEach((d) => {
                endTrainingFor([d.id]);
                sockRef.current?.emit("command", { action: "GO_HOME", deviceId: d.id });
            });
        exitSelecting();
        // 코스는 전부 기기 목록에서 출발한다 — 설정 탭에 선 채로 열면 첫 칸이 허공이다.
        setTab("devices");
        setScreen("main");
        // 선택 모드만 정리하고 곧바로 시작한다.
        setTimeout(() => setDemo(c), 400);
    }, [devices, endTrainingFor, exitSelecting, setTab, setScreen]);

    // '이어서 보기'로 남은 코스. 🛑 state 가 아니라 ref 다 — 다시 그리는 것과 무관하고,
    //    onClose(그만보기)에서 즉시 비워야 하는데 setState 는 그 프레임에 안 보인다.
    const demoQueue = useRef<DemoCourse[]>([]);
    // ⭐ '계속 반복'(loop) 코스가 다시 채워 넣을 코스 목록. 큐가 비면 여기서 다시 세운다.
    //    🛑 '그만보기'(onClose)에서 반드시 비운다 — 안 비우면 다음에 낱개 코스를 틀어도 계속 돈다.
    const demoLoopChain = useRef<DemoCourse[] | null>(null);
    // 코스가 저절로 끝나고 **다음 코스로 넘어가는 중**인지. 사람이 '그만보기'로 나간 것과
    // 구별한다 — TourOverlay 는 마지막 칸에서 onDone 뒤에 onClose 도 부르기 때문이다.
    const demoChaining = useRef(false);
    // ⭐ 지금 도는 '계속 반복' 코스 자체(그만보기 전까지 유지). 화면이 다 꺼져 멈췄을 때
    //    다시 틀 대상으로 쓴다 — 부스에 그냥 틀어 두는 용도라 와이파이가 한 번 끊겼다고 죽으면 안 된다.
    const demoLoopCourse = useRef<DemoCourse | null>(null);
    // 화면이 돌아오면 다시 틀 코스(= 위 값의 사본). null 이면 다시 틀지 않는다.
    const demoResumeAt = useRef<DemoCourse | null>(null);
    // 코스(또는 이어서 보기) 하나를 **여기서부터** 시작하는 한 벌 — 설정에서 누를 때와
    // 화면이 돌아와 저절로 다시 시작할 때가 같은 길을 타야 한다.
    const startDemoCourse = useCallback((c: DemoCourse) => {
        // '이어서 보기'는 코스 목록을 큐에 세우고 첫 코스부터 시작한다.
        // 🛑 이 항목 자체(steps 가 빈 이름표)를 그대로 틀지 말 것 — 칸이 없어 바로 끝난다.
        const chained = c.chain?.map((id) => DEMO_COURSES.find((x) => x.id === id)).filter(Boolean) as DemoCourse[] | undefined;
        demoQueue.current = chained ? chained.slice(1) : [];
        // '계속 반복' 코스면 한 바퀴가 끝났을 때 다시 채울 목록을 들고 있는다(그만보기까지).
        demoLoopChain.current = c.loop && chained?.length ? chained : null;
        demoLoopCourse.current = c.loop && chained?.length ? c : null;
        demoResumeAt.current = null; // 사람이 새로 틀었으면 '기다리던 재시작'은 없던 일로
        beginCourse(chained ? chained[0] : c);
    }, [beginCourse]);

    // 🛑 도는 중에 **도장 화면이 다 꺼지면** 멈춘다(2026-08-10 사용자 지적).
    //    시연의 여러 칸은 '실제로 재생이 시작될 때까지' 기다린다 — 기기가 답을 안 하면 안내의
    //    파수꾼이 밀어낼 때까지 26초를 "도장 화면에 재생하는 중이에요." 인 채로 굳는다.
    //    부스에서 그 26초는 고장으로 읽힌다. 시작할 때만 대수를 보던 것(onStartDemo)을 도는 중에도 본다.
    // ⚠️ 3초 이어질 때만 멈춘다 — 소켓이 잠깐 끊겼다 붙는 일이 있어, 한 번 튄 걸로 시연을 죽이지 않는다.
    const demoOnlineN = demo ? devices.filter((d) => d.online).length : 1;
    useEffect(() => {
        if (!demo || demoOnlineN > 0) return;
        const t = setTimeout(() => {
            // ⭐ '계속 반복'으로 틀어 둔 것이면 여기서 끝내지 않는다 — 화면이 돌아오면 아래 효과가 다시 튼다.
            //    부스에 그냥 틀어 두는 용도라(2026-08-19) 와이파이가 한 번 끊긴 걸로 죽으면 안 된다.
            demoResumeAt.current = demoLoopCourse.current;
            const waiting = !!demoResumeAt.current;
            stopDemo();
            showToast(
                waiting ? "화면이 꺼져서 잠깐 멈췄어요 — 다시 켜지면 이어서 시작할게요" : "도장 화면이 꺼져서 시연을 멈췄어요",
                <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
            );
        }, 3000);
        return () => clearTimeout(t);
    }, [demo, demoOnlineN, stopDemo, showToast]);
    // 화면이 돌아오면 스스로 다시 시작(위에서 기다리기로 한 경우만). 3초 이어질 때만 — 한 대가
    // 잠깐 붙었다 떨어지는 걸로 틀었다 멈췄다 하지 않게.
    const demoOnlineNow = devices.filter((d) => d.online).length;
    useEffect(() => {
        const c = demoResumeAt.current;
        if (demo || !c || demoOnlineNow < c.needDevices) return;
        const t = setTimeout(() => {
            demoResumeAt.current = null;
            showToast("화면이 다시 켜져서 시연을 처음부터 시작할게요", <I.play size={16} style={{ color: "var(--live)" }} />);
            startDemoCourse(c);
        }, 3000);
        return () => clearTimeout(t);
    }, [demo, demoOnlineNow, showToast, startDemoCourse]);
    // 탭 → 선택 토글 (선택 모드 중)
    const toggleSelectDevice = useCallback((d: DeviceView) => {
        setSelIds((s) => {
            const n = new Set(s);
            if (n.has(d.id)) n.delete(d.id);
            else n.add(d.id);
            return n;
        });
    }, []);
    // 길게 누름 → 선택 모드 진입 + 그 기기 토글 (사진앱 다중선택과 동일)
    const longPressDevice = useCallback((d: DeviceView) => {
        setSelecting(true);
        setSelIds((s) => {
            const n = new Set(s);
            if (n.has(d.id)) n.delete(d.id);
            else n.add(d.id);
            return n;
        });
    }, []);
    const selectAllOnline = useCallback(() => {
        // '전체 선택'도 도장 화면까지 — 집 화면은 관장이 그 타일을 직접 눌러서만 고른다.
        setSelIds(new Set(devices.filter((d) => d.online && atDojang(d)).map((d) => d.id)));
    }, [devices]);

    // 선택된 온라인 기기에 같은 명령을 개별 emit (전체 일괄 패턴과 동일 — 브로드캐스트 아님).
    // 오프라인 선택분은 제외하고 토스트로 알림.
    const runBatch = useCallback(
        (
            build: (id: string) => Command,
            verb: string,
            icon: ReactNode,
            // 재생/일시정지처럼 '이미 틀어둔 영상'을 전제하는 명령은 영상이 없는 기기를 건너뛴다.
            // 안 거르면 빈 기기에도 PLAY 가 날아가 배지가 '재생 중'으로 바뀐다(실제론 아무것도 안 틀었는데).
            needsVideo = false,
        ) => {
            const online = devices.filter((d) => selIds.has(d.id) && d.online);
            const targets = needsVideo
                ? online.filter((d) => !!d.playlistId)
                : online;
            const offline = devices.filter(
                (d) => selIds.has(d.id) && !d.online,
            ).length;
            const empty = online.length - targets.length; // 켜져 있지만 영상을 안 고른 기기
            if (targets.length === 0) {
                showToast(
                    needsVideo && online.length > 0
                        ? tp("toast.batchNoVideo")
                        : tp("toast.batchNoneOn"),
                    <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
                );
                return;
            }
            targets.forEach((d) =>
                sockRef.current?.emit("command", build(d.id)),
            );
            showToast(
                tp(`batch.${verb}`, { count: targets.length }) +
                    (offline ? tp("batch.exclOffline", { count: offline }) : "") +
                    (empty ? tp("batch.exclEmpty", { count: empty }) : ""),
                icon,
            );
        },
        [devices, selIds, showToast],
    );

    // 마스터 재생 큐 기록 — 제어화면 미러/큐 복원용. plId 는 플레이어가 보고할 합성 재생목록 id 와 반드시 일치.
    const recordMaster = useCallback(
        (
            deviceIds: string[],
            plId: string,
            title: string,
            videos: MasterPlayVideo[],
        ) => {
            setMasterQueues((m) => {
                const next = { ...m };
                for (const id of deviceIds) next[id] = { plId, title, videos };
                return next;
            });
        },
        [],
    );

    // 표준 품새(마스터) 단일 영상 재생 — batch 명시(단일 시트=false/일괄 시트=true). 미지정 시 라이브 selecting 추론(호환).
    // ⚠️가로 2단(isWide)에선 단일 시트가 열려도 selecting=true 일 수 있어(왼쪽 그리드 다중선택+오른쪽 제어판) 반드시 명시로 넘긴다.
    const loadMaster = useCallback(
        (video: MasterPlayVideo, batch?: boolean) => {
            if (batch ?? (selecting && selIds.size > 0)) {
                recordMaster([...selIds], `master:${video.id}`, video.title, [
                    video,
                ]);
                runBatch(
                    (id) => ({
                        action: "LOAD_MASTER",
                        deviceId: id,
                        video,
                        autoplay: true,
                    }),
                    "play",
                    <I.cast size={16} style={{ color: "var(--live)" }} />,
                );
                return;
            }
            const dev = devices.find((d) => d.id === selId) ?? null;
            if (!dev) {
                showToast(
                    tp("toast.selectDeviceFirst"),
                    <I.cast size={16} style={{ color: "var(--warn)" }} />,
                );
                return;
            }
            if (!dev.online) {
                showToast(
                    tp("toast.deviceOffline"),
                    <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
                );
                return;
            }
            recordMaster([dev.id], `master:${video.id}`, video.title, [video]);
            sockRef.current?.emit(
                "command",
                {
                    action: "LOAD_MASTER",
                    deviceId: dev.id,
                    video,
                    autoplay: true,
                } as Command,
                (res: CommandResult) => {
                    if (!res?.ok) {
                        showToast(
                            tp("toast.cmdFailed"),
                            <I.wifiOff
                                size={16}
                                style={{ color: "var(--danger)" }}
                            />,
                        );
                        return;
                    }
                    showToast(
                        tp("toast.stdFormPlaying"),
                        <I.cast size={16} style={{ color: "var(--live)" }} />,
                    );
                },
            );
        },
        [selecting, selIds, devices, selId, runBatch, showToast, recordMaster],
    );

    // 마스터 여러 영상(한 각도 품마디 전체) 재생 — batch 명시(단일 시트=false/일괄 시트=true). 미지정 시 selecting 추론(호환).
    const loadMasterSet = useCallback(
        (
            videos: MasterPlayVideo[],
            title: string,
            index = 0,
            batch?: boolean,
            practice?: DrillPlayOpt | null, // 품새 드릴 '수련 모드'(테스트 계정만) — 기기가 마디마다 멈춰 기다린다
        ) => {
            if (!videos.length) return;
            const plId = `masterset:${title}`;
            if (batch ?? (selecting && selIds.size > 0)) {
                recordMaster([...selIds], plId, title, videos);
                runBatch(
                    (id) => ({
                        action: "LOAD_MASTER_SET",
                        deviceId: id,
                        videos,
                        title,
                        index,
                        autoplay: true,
                        ...(practice ? { practice } : {}),
                    }),
                    "play",
                    <I.cast size={16} style={{ color: "var(--live)" }} />,
                );
                return;
            }
            const dev = devices.find((d) => d.id === selId) ?? null;
            if (!dev) {
                showToast(
                    tp("toast.selectDeviceFirst"),
                    <I.cast size={16} style={{ color: "var(--warn)" }} />,
                );
                return;
            }
            if (!dev.online) {
                showToast(
                    tp("toast.deviceOffline"),
                    <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
                );
                return;
            }
            recordMaster([dev.id], plId, title, videos);
            sockRef.current?.emit(
                "command",
                {
                    action: "LOAD_MASTER_SET",
                    deviceId: dev.id,
                    videos,
                    title,
                    index,
                    autoplay: true,
                    ...(practice ? { practice } : {}),
                } as Command,
                (res: CommandResult) => {
                    if (!res?.ok) {
                        showToast(
                            tp("toast.cmdFailed"),
                            <I.wifiOff
                                size={16}
                                style={{ color: "var(--danger)" }}
                            />,
                        );
                        return;
                    }
                    showToast(
                        tp("toast.playName", { name: dev.name }),
                        <I.cast size={16} style={{ color: "var(--live)" }} />,
                    );
                },
            );
        },
        [selecting, selIds, devices, selId, runBatch, showToast, recordMaster],
    );

    // 재생목록 탭 드릴에서 단건(전체 한 각도 / 마디) 재생 — 기기 선택 후 그 기기에 LOAD_MASTER.
    const pickThenPlayMaster = useCallback(
        (video: MasterPlayVideo) => {
            const online = devices.filter((d) => d.online);
            if (online.length === 0) {
                showToast(
                    tp("toast.noDevicesOn"),
                    <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
                );
                return;
            }
            setModal({
                kind: "action",
                title: tp("dialog.whichDevice"),
                subtitle: tp("dialog.sendsVideo"),
                actions: online.map((d) => ({
                    label: d.name,
                    icon: <I.monitor size={19} />,
                    hint:
                        d.status === "playing"
                            ? tc("status.playing")
                            : d.status === "paused"
                              ? tc("status.paused")
                              : tc("status.idle"),
                    onClick: () => {
                        recordMaster(
                            [d.id],
                            `master:${video.id}`,
                            video.title,
                            [video],
                        );
                        sockRef.current?.emit("command", {
                            action: "LOAD_MASTER",
                            deviceId: d.id,
                            video,
                            autoplay: true,
                        } as Command);
                        setSelId(d.id);
                        setTab("devices"); // 태블릿 2단 제어판은 '기기' 탭에서만 렌더 — 라이브러리서 캐스트해도 제어화면 뜨게 탭 전환
                        setScreen("control");
                        showToast(
                            tp("toast.playName", { name: d.name }),
                            <I.cast
                                size={16}
                                style={{ color: "var(--live)" }}
                            />,
                        );
                    },
                })),
            });
        },
        [devices, showToast, recordMaster],
    );

    // 재생목록 탭 드릴 '마디별' — 그 각도 품마디 전체를 기기 선택 후 LOAD_MASTER_SET. index=누른 마디부터 시작.
    const pickThenPlayMasterSet = useCallback(
        (videos: MasterPlayVideo[], title: string, index = 0, practice?: DrillPlayOpt | null) => {
            if (!videos.length) return;
            const online = devices.filter((d) => d.online);
            if (online.length === 0) {
                showToast(
                    tp("toast.noDevicesOn"),
                    <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
                );
                return;
            }
            setModal({
                kind: "action",
                title: tp("dialog.whichDevice"),
                subtitle: tp("dialog.sendsVideo"),
                actions: online.map((d) => ({
                    label: d.name,
                    icon: <I.monitor size={19} />,
                    hint:
                        d.status === "playing"
                            ? tc("status.playing")
                            : d.status === "paused"
                              ? tc("status.paused")
                              : tc("status.idle"),
                    onClick: () => {
                        recordMaster(
                            [d.id],
                            `masterset:${title}`,
                            title,
                            videos,
                        );
                        sockRef.current?.emit("command", {
                            action: "LOAD_MASTER_SET",
                            deviceId: d.id,
                            videos,
                            title,
                            index,
                            autoplay: true,
                            ...(practice ? { practice } : {}),
                        } as Command);
                        setSelId(d.id);
                        setTab("devices"); // 태블릿 2단 제어판은 '기기' 탭에서만 렌더 — 라이브러리서 캐스트해도 제어화면 뜨게 탭 전환
                        setScreen("control");
                        showToast(
                            tp("toast.playName", { name: d.name }),
                            <I.cast
                                size={16}
                                style={{ color: "var(--live)" }}
                            />,
                        );
                    },
                })),
            });
        },
        [devices, showToast, recordMaster],
    );

    // 재생목록 탭 — 마스터 영상 탭 = 로컬 미리보기(내 재생목록과 동일 모델). 미리보기의 '디바이스에 재생'은
    // 기존 기기선택 전송(pickThenPlayMaster/Set)을 그대로 재사용 — 세트는 현재 보고 있는 index 부터 전송.
    const previewMasterSet = useCallback(
        (videos: MasterPlayVideo[], title: string, index = 0, practice?: DrillPlayOpt | null) => {
            if (!videos.length) return;
            setLocalPlay({
                videos: videos.map(masterToVideoView),
                idx: index,
                title,
                onSend: (i) => {
                    setLocalPlay(null);
                    pickThenPlayMasterSet(videos, title, i, practice);
                },
                // 제공(마스터 라이브러리) 클립도 미리보기서 '＋ 담기' — 지금 보고 있는 클립을 내 재생목록에 담는다.
                onAddToPlaylist: (i) => setAddToPlVideo(videos[i]?.id ?? videos[0].id),
            });
        },
        [pickThenPlayMasterSet],
    );
    const previewMaster = useCallback(
        (video: MasterPlayVideo) => {
            setLocalPlay({
                videos: [masterToVideoView(video)],
                idx: 0,
                title: video.title,
                onSend: () => {
                    setLocalPlay(null);
                    pickThenPlayMaster(video);
                },
                onAddToPlaylist: () => setAddToPlVideo(video.id),
            });
        },
        [pickThenPlayMaster],
    );

    // 세트리스트 '기기로 재생하기' — 전체 세트(정면/후면/부감) 재생목록. batch 명시(단일 시트=false/일괄 시트=true). 미지정 시 selecting 추론.
    const playMasterSet = useCallback(
        (playlistId: string, batch?: boolean) => {
            if (batch ?? (selecting && selIds.size > 0)) {
                runBatch(
                    (id) => ({
                        action: "LOAD_PLAYLIST",
                        deviceId: id,
                        playlistId,
                        autoplay: true,
                    }),
                    "play",
                    <I.cast size={16} style={{ color: "var(--live)" }} />,
                );
                return;
            }
            const dev = devices.find((d) => d.id === selId) ?? null;
            if (!dev) {
                showToast(
                    tp("toast.selectDeviceFirst"),
                    <I.cast size={16} style={{ color: "var(--warn)" }} />,
                );
                return;
            }
            if (!dev.online) {
                showToast(
                    tp("toast.deviceOffline"),
                    <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
                );
                return;
            }
            sockRef.current?.emit(
                "command",
                {
                    action: "LOAD_PLAYLIST",
                    deviceId: dev.id,
                    playlistId,
                    autoplay: true,
                } as Command,
                (res: CommandResult) => {
                    if (!res?.ok) {
                        showToast(
                            tp("toast.cmdFailed"),
                            <I.wifiOff
                                size={16}
                                style={{ color: "var(--danger)" }}
                            />,
                        );
                        return;
                    }
                    showToast(
                        tp("toast.playName", { name: dev.name }),
                        <I.cast size={16} style={{ color: "var(--live)" }} />,
                    );
                },
            );
        },
        [selecting, selIds, devices, selId, runBatch, showToast],
    );

    const onBatch = useCallback(
        (action: "PLAY" | "PAUSE" | "STOP" | "RELOAD") => {
            if (action === "PLAY")
                runBatch(
                    (id) => ({ action: "PLAY", deviceId: id }),
                    "play",
                    <I.play size={16} style={{ color: "var(--live)" }} />,
                    true, // 영상을 안 고른 기기는 제외 — 빈 기기에 PLAY 를 보내면 배지만 '재생 중'이 된다
                );
            else if (action === "PAUSE")
                runBatch(
                    (id) => ({ action: "PAUSE", deviceId: id }),
                    "pause",
                    <I.pause size={16} style={{ color: "var(--warn)" }} />,
                    true,
                );
            else if (action === "RELOAD")
                // 화면 새로고침 — 정비용(배포 최신본 반영). 수업용 다중선택 바에선 뺐고, 코드 경로만 유지.
                runBatch(
                    (id) => ({ action: "RELOAD", deviceId: id }),
                    "reload",
                    <I.refresh size={16} style={{ color: "var(--live)" }} />,
                );
            else
                // 일괄 정지는 '잠깐 멈춤'일 뿐 — 수련 세션을 끝내지 않는다(마감은 '수업 종료'만). (UI 버튼은 없고 코드 경로만 유지)
                runBatch(
                    (id) => ({ action: "STOP", deviceId: id }),
                    "stop",
                    <I.stop size={16} style={{ color: "var(--live)" }} />,
                );
        },
        [runBatch],
    );

    // 선택 기기에 같은 재생목록 동시 재생 ('같은 영상 틀기')
    const batchLoad = useCallback(
        (playlistId: string) => {
            runBatch(
                (id) => ({
                    action: "LOAD_PLAYLIST",
                    deviceId: id,
                    playlistId,
                    autoplay: true,
                }),
                "sameVideo",
                <I.cast size={16} style={{ color: "var(--live)" }} />,
            );
            setBatchSheet(false);
        },
        [runBatch],
    );

    /* ---- 최근 캐스트 — 로드시트에서 무엇을 틀 때마다 기록(중복 제거·최대 3), 칩 1탭으로 재적용 ---- */
    const pushRecent = useCallback((r: RecentCast) => {
        const key = (x: RecentCast) =>
            x.kind === "playlist"
                ? `p:${x.id}`
                : x.kind === "master"
                  ? `m:${x.video.id}`
                  : `s:${x.title}#${x.index}`;
        // 중복 제거 — 같은 대상(key)뿐 아니라 '보이는 라벨이 같은' 것도 하나로.
        //  같은 품새 '전체'라도 경로가 다르면 종류가 갈린다(CTA=playlist / 마디 행 탭=masterset).
        //  둘 다 라벨은 똑같아, key 만으로는 못 걸러 글자가 같은 칩이 2개 남았다 → 라벨까지 보고 합친다.
        setRecentCasts((prev) =>
            [r, ...prev.filter((x) => key(x) !== key(r) && x.label !== r.label)].slice(0, 3),
        );
    }, []);
    // 최근 칩 재적용 — 단일 시트=선택 기기(selId), 일괄 시트=선택 기기들. batch 를 종류 불문 명시 전파(라이브 selecting 의존 제거).
    const replayRecent = useCallback(
        (r: RecentCast, batch: boolean) => {
            if (r.kind === "playlist") {
                if (batch) batchLoad(r.id);
                else cmd("LOAD_PLAYLIST", r.id);
            } else if (r.kind === "master") loadMaster(r.video, batch);
            else loadMasterSet(r.videos, r.title, r.index, batch);
        },
        [batchLoad, cmd, loadMaster, loadMasterSet],
    );

    /* ===== 하드웨어/브라우저 뒤로가기 — 인앱 화면 스택 pop (라우터 미도입, 상태머신 위에 back 신호만 얹음) ===== */
    // 재생목록 상세 닫기 — render 인라인 onClose 와 goBack 이 공유(반환 경로 이중관리 제거). 값은 navStateRef 에서 읽는다.
    const closePlDetail = useCallback(() => {
        const s = navStateRef.current;
        if (s.plReturn === "control" && s.selDevice && s.selPl) {
            // 제어 화면에서 온 경우 — 닫으면서 그 기기에 반영(빈 목록이면 생략). 재생 중이면 무중단 동기화.
            if (s.selPl.items.length > 0)
                cmd(
                    s.selDevice.playlistId === s.selPl.id
                        ? "SYNC_PLAYLIST"
                        : "LOAD_PLAYLIST",
                    s.selPl.id,
                );
            setScreen("control");
        } else setScreen("main");
        setPlReturn("main");
    }, [cmd]);
    const closeUpload = useCallback(() => {
        const s = navStateRef.current;
        if (s.uploadReturnPl) {
            setScreen("pldetail");
            setUploadReturnPl(null);
        } // 재생목록에서 진입 → 그 상세로 복귀
        else setScreen("main");
    }, []);
    // 뒤로가기 우선순위 — 위에 뜬 오버레이부터 닫고, 없으면 화면을 pop. 소비하면 true, 최상위(홈/도장선택)면 false.
    const goBack = useCallback((): boolean => {
        const s = navStateRef.current;
        if (s.overAddToPl) {
            setAddToPlVideo(null);
            return true;
        } // z60 담기 시트
        if (s.overAddQueue) {
            setAddQueue(null);
            return true;
        } // z60 제어 화면 '영상 추가' 오버레이
        if (s.overLocalPlay) {
            setLocalPlay(null);
            return true;
        } // z45 미리보기
        if (s.overModal) {
            setModal(null);
            return true;
        } // z40 액션/입력/확인
        if (s.batchSheet) {
            setBatchSheet(false);
            return true;
        }
        if (s.sheet) {
            setSheet(false);
            return true;
        }
        if (s.selecting) {
            exitSelecting();
            return true;
        }
        switch (s.screen) {
            case "control":
                setScreen("main");
                if (s.isWide) setSelId(null);
                return true;
            case "pldetail":
                closePlDetail();
                return true;
            case "upload":
                closeUpload();
                return true;
            case "formdrill":
            case "pairing":
            case "training":
            case "members":
            case "account":
                setScreen("main");
                return true;
            case "dojang":
                if (s.hasDojang) {
                    setScreen("main");
                    return true;
                }
                return false; // 도장 있으면 전환 취소=main, 없으면 홈
            case "main":
                if (s.tab === "library" && libBackRef.current?.()) return true; // 라이브러리 내부 뎁스(묶음/검색) 먼저 pop
                if (s.tab !== "devices") {
                    setTab("devices");
                    return true;
                } // 라이브러리/설정 → 기기
                if (s.isWide && s.selId) {
                    setSelId(null);
                    return true;
                } // 가로 2단: 기기 선택 해제
                return false; // 기기 탭 = 홈(루트)
            default:
                return false;
        }
    }, [exitSelecting, closePlDetail, closeUpload]);
    const goBackRef = useRef(goBack);
    useEffect(() => {
        goBackRef.current = goBack;
    }, [goBack]);
    // 재장착형 단일 센티넬 — 마운트 시 history 엔트리 1개 심고, popstate 마다 goBack 소비하면 재장착(길이 불변).
    // 최상위에서 뒤로 = '한 번 더 누르면 종료'(2초 창) — 실수로 앱을 나가는 것 방지(PWA/키오스크).
    const exitArmedRef = useRef(false);
    const exitingRef = useRef(false); // 루트 종료 트래버설(history.back) 이 유발하는 재진입 popstate 억제(같은 문서 하위 엔트리 진입 시)
    useEffect(() => {
        if (typeof window === "undefined") return;
        // 기존 history.state 를 보존(…state)해 Next 라우터 키를 살린 채 tedu 센티넬만 얹는다.
        const arm = () =>
            window.history.pushState({ ...window.history.state, tedu: 1 }, "");
        // StrictMode(dev)·우발적 재실행 방어 — 이미 센티넬(현재 엔트리 tedu)이면 중복 push 안 함(센티넬 1개 유지).
        if (!(window.history.state as { tedu?: number } | null)?.tedu) arm();
        const onPop = () => {
            if (exitingRef.current) {
                exitingRef.current = false;
                return;
            } // 종료 트래버설 재진입 — 소비만(재장착/토스트 안 함)
            if (goBackRef.current()) {
                arm();
                return;
            } // 인앱 back 소비 → 센티넬 재장착(길이 불변)
            if (exitArmedRef.current) {
                // 2번째 루트 back → 실제 이탈
                exitArmedRef.current = false;
                exitingRef.current = true;
                window.history.back();
                window.setTimeout(() => {
                    exitingRef.current = false;
                }, 100); // back() 이 no-op(최초 엔트리)이면 플래그 잔존 방지
                return;
            }
            exitArmedRef.current = true;
            arm(); // 재장착(2초 창 동안 인앱 back 보호 유지)
            showToast(tp("toast.backToLeave"));
            window.setTimeout(() => {
                exitArmedRef.current = false;
            }, 2000);
        };
        window.addEventListener("popstate", onPop);
        return () => window.removeEventListener("popstate", onPop);
    }, [showToast]);

    /* ---- 그리드 타일 미리보기 탭 — 화면 전환 없이 해당 기기 재생/일시정지 토글 ---- */
    const toggleDevice = useCallback(
        (dev: DeviceView) => {
            if (!dev.online) {
                showToast(
                    tp("toast.deviceOfflineCheck"),
                    <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
                );
                return;
            }
            const payload: Command = {
                action: dev.status === "playing" ? "PAUSE" : "PLAY",
                deviceId: dev.id,
            };
            sockRef.current?.emit("command", payload, (res: CommandResult) => {
                if (!res?.ok) {
                    if (notedInUse(res)) return; // 내 다른 기기가 잡고 있다 — 이유를 말해준다
                    console.warn("command failed:", res?.reason);
                    showToast(
                        tp("toast.cmdFailedRetry"),
                        <I.wifiOff
                            size={16}
                            style={{ color: "var(--danger)" }}
                        />,
                    );
                }
            });
        },
        [showToast, notedInUse],
    );

    /* ---- 제어화면 점유 — 같은 계정을 두 기기에 켜둔 경우만 막는다 ----
       한 계정을 폰·태블릿에 켜두고 같은 기기를 만지면 서로 명령을 뺏는데, 관장 명령은 제어자로
       기록되지 않아 **양쪽 다 상대가 만지는 걸 볼 수도 없다**. 그래서 서버가 (기기,계정)당 한 세션만
       인정한다. 🛑 사람이 다른 경우(관장↔사범)는 여기서 안 막는다 — 그건 withTakeover 가 맡는다.
       서버가 답이 없거나 구버전이면 **막지 않는다** — 잠금 때문에 기기를 못 쓰는 게 더 나쁘다. */
    const claimDevice = useCallback(
        (deviceId: string): Promise<{ ok: boolean; by?: string }> =>
            new Promise((resolve) => {
                const sock = sockRef.current;
                if (!sock) return resolve({ ok: true });
                const ask = () =>
                    sock
                        .timeout(2500)
                        .emit(
                            "device:claim",
                            { deviceId },
                            (err: unknown, res?: { ok: boolean; by?: string }) =>
                                resolve(err ? { ok: true } : (res ?? { ok: true })),
                        );
                if (sock.connected) return ask();
                /* 잠깐 끊겨 있는 사이엔 물어보지도 않고 통과시켰다 → **아이폰에서 안내가 안 뜨던 이유**.
                   화면을 껐다 켜거나 다른 앱에 다녀오면 소켓이 끊기고(iOS 는 특히 빨리 끊는다) 돌아온
                   직후 몇 초는 재연결 중인데, 하필 그때 기기를 누르면 잠금이 통째로 새 나갔다.
                   → 재연결을 재촉하고 잠깐(3초) 기다렸다 묻는다. 그래도 안 붙으면 지금까지처럼 통과. */
                let done = false;
                const finish = (fn: () => void) => {
                    if (done) return;
                    done = true;
                    clearTimeout(timer);
                    sock.off("connect", onConnect);
                    fn();
                };
                const onConnect = () => finish(ask);
                const timer = setTimeout(() => finish(() => resolve({ ok: true })), 3000);
                sock.on("connect", onConnect);
                sock.connect(); // 뒤로 물러난 재연결 대기(backoff)를 앞당긴다 — 이미 붙는 중이면 무시된다
            }),
        [],
    );

    /* ---- open a device (attach for snapshot) ---- */
    const attachDevice = useCallback((deviceId: string) => {
        sockRef.current?.emit(
            "device:attach",
            { deviceId },
            (snap: DeviceState | null) => {
                if (snap)
                    setStates((m) => ({
                        ...m,
                        [deviceId]: {
                            ...snap,
                            ts: snap.ts + (skewRef.current ?? 0),
                            rxAt: Date.now(), // 받은 로컬 시각(신선도 판정용)
                        },
                    }));
            },
        );
    }, []);

    // 다른 기기에서 쓰는 중 — 그냥 못 들어간다. 🛑 여기서 뺏는 길은 두지 않는다(사용자 결정):
    // '이어받기' 같은 우회가 있으면 결국 서로 뺏는 지금과 같아진다. 먼저 잡은 쪽이 놓아야 열린다.
    // 그래서 이 안내는 확인 하나뿐 — 대신 '어디서 쓰는 중인지'와 '어떻게 하면 풀리는지'를 말해준다.
    const showInUse = useCallback(
        (by: string | undefined) => {
            setModal({
                kind: "notice",
                title: tp("dialog.otherSessionTitle"),
                message: tp("dialog.otherSessionMsg", {
                    device: tp(`agent.${by ?? "other"}` as "agent.other"),
                }),
            });
        },
        [tp],
    );

    const openDevice = useCallback(
        (d: DeviceView) => {
            const enter = () => {
                setSelId(d.id);
                if (!isWide) setScreen("control"); // 가로 2단에선 selId만으로 오른쪽 패널 구동
                attachDevice(d.id);
            };
            void claimDevice(d.id).then((r) => (r.ok ? enter() : showInUse(r.by)));
        },
        [isWide, claimDevice, attachDevice, showInUse],
    );

    // 제어화면에서 물러난다 — goBack 의 control 분기와 같은 동작(들어온 길이 어디였든 나가는 길은 하나).
    const leaveControl = useCallback(() => {
        setScreen("main");
        if (isWide) setSelId(null); // 가로 2단은 selId 가 오른쪽 패널을 쥐고 있다
    }, [isWide]);

    /* ---- 점유는 '제어화면이 열려 있는 동안' 유지한다 ----
       들어오는 길이 여럿이라(기기 탭 · 재생목록 캐스트 · 라이브러리 캐스트…) 한 곳에서 잡고
       한 곳에서 놓는다. 나가는 길(뒤로·화면전환·언마운트·기기 바꿈)도 전부 이 cleanup 이 받는다.
       openDevice 가 미리 막아주므로 대개는 여기서 통과만 하고, 그 외 경로로 들어온 경우를 여기서 되돌린다. */
    const heldId = (isWide ? selId : screen === "control" ? selId : null) ?? null;
    useEffect(() => {
        if (!heldId) return;
        let left = false;
        const claim = () =>
            void claimDevice(heldId).then((r) => {
                if (left || r.ok) return;
                leaveControl(); // openDevice 를 안 거쳐 들어온 길(재생목록 캐스트 등) — 여기서 되돌린다
                showInUse(r.by);
            });
        claim();
        /* 끊겼다 붙으면 다시 잡는다 — 연결이 끊긴 동안 다른 기기가 이 기기를 잡았을 수 있다.
           그때 조용히 제어화면에 남아 있으면 누르는 것마다 거절당하는 '먹통 화면'이 된다.
           (같은 탭이면 서버가 내 점유를 그대로 돌려주므로, 아무도 안 가져갔으면 그냥 통과한다.) */
        const sock = sockRef.current;
        sock?.on("connect", claim);
        return () => {
            left = true;
            sock?.off("connect", claim);
            sockRef.current?.emit("device:release", { deviceId: heldId });
        };
    }, [heldId, claimDevice, showInUse, leaveControl]);


    /* ---- 넘겨받기 가드 — 다른 사람(부관리자)이 조종 중인 기기를 관장이 건드릴 때 확인 ----
       controlBy 는 관장 룸에만 오므로 부관리자에겐 빈 채로 → 부관리자는 이 확인이 안 뜬다.
       지금 조종자가 '나(관장)'가 아니면 "OOO님이 사용 중, 넘겨받을까요?"를 먼저 묻고, 확인 시 진행. */
    const withTakeover = useCallback(
        (dev: DeviceView, proceed: () => void) => {
            const by = controlBy[dev.id];
            if (canManage && by && by !== owner.name) {
                setModal({
                    kind: "confirm",
                    title: tp("dialog.inUseTitle"),
                    message: tp("dialog.inUseMsg", { by, name: dev.name }),
                    confirmLabel: tp("action.proceed"),
                    onConfirm: () => proceed(),
                });
                return;
            }
            proceed();
        },
        [controlBy, canManage, owner.name],
    );

    /* ---- REST CRUD handlers ---- */
    /* 집/도장 표식은 등록할 때 한 번 정한다. 되돌리는 메뉴는 2026-09-03 뺐다(사용자 결정) —
       잘못 등록한 화면은 '등록 해제' 후 다시 등록한다. */
    /* 보이는 범위(급수 상한) — "이 학생이 어디까지 배웠나"를 품새 하나로 가리킨다.
       그 위 품새는 **학생 화면 목록에서 사라진다**(서버가 /api/device/playlists 에서 자른다).
       🛑 관장이 보내는 재생은 안 막는다 — 다음 급수를 미리 보여주는 것도 수업이다. */
    const setRankForm = useCallback(
        async (dev: DeviceView, rankFormId: string | null) => {
            const r = await fetch(`/api/devices/${dev.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ rankFormId }),
            }).catch(() => null);
            if (!r?.ok) {
                showToast(
                    tp("toast.rangeFailed"),
                    <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
                );
                return;
            }
            if (dojang) devicesChanged(dojang.id);
            const f = masterForms.find((m) => m.id === rankFormId);
            showToast(
                rankFormId === RANK_BASICS
                    ? tp("toast.rangeBasics")
                    : f
                      ? tp("toast.rangeSet", { form: f.name })
                      : tp("toast.rangeAll"),
                <I.layers size={16} style={{ color: "var(--accent)" }} />,
            );
        },
        [dojang, devicesChanged, showToast, masterForms],
    );
    // 범위 고르기 시트 — '제한 없음' + 품새들. 기본동작은 급수와 무관해 목록에 넣지 않는다(늘 보인다).
    // 자습 범위 — '무엇을 틀까요?' 얼개의 RangeSheet 로(2026-09-04). 🛑 ActionSheet 목록으로 되돌리지 말 것.
    const rankMenu = (dev: DeviceView) => setRankPickDev(dev);

    /* 숙제 — 이 집 화면에 걸어두는 재생목록 한 벌.
       🔑 실어두는(LOAD_PLAYLIST) 게 아니다. 실으면 플레이어가 재생 화면으로 넘어가 학생의 자유
          브라우즈가 가려진다(관장이 1순위로 꼽은 게 그 자유다). 숙제는 학생 카드 화면 **맨 앞 카드**로
          놓이고, 누르는 건 학생 몫이다. 관장이 바꾸면 그 카드만 바뀐다 = "숙제가 바뀐다". */
    // 🔑 **여러 대에 한 번에**도 이 한 길로 낸다(2026-09-01). 같은 급수 아이들에게 같은 숙제를 내는 게
    //    실제 쓰임이라, 한 대씩 열 번 여는 건 그 자체가 안 쓰게 만드는 이유가 된다.
    //    일괄 API 를 새로 만들지는 않는다 — 기기마다 권한·집 화면 여부를 서버가 각각 판단하는 게 맞다.
    const setHomework = useCallback(
        async (devs: DeviceView[], playlistId: string | null) => {
            const rs = await Promise.all(
                devs.map((d) =>
                    fetch(`/api/devices/${d.id}`, {
                        method: "PATCH",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ homePlaylistId: playlistId }),
                    })
                        .then((r) => r.ok)
                        .catch(() => false),
                ),
            );
            const okN = rs.filter(Boolean).length;
            if (dojang) devicesChanged(dojang.id);
            if (okN === 0) {
                showToast(
                    tp("toast.homeworkFailed"),
                    <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
                );
                return;
            }
            // 🛑 일부만 됐으면 그렇게 말한다 — 다 된 척하면 관장이 '숙제 못 받은 아이'를 모른 채 지나간다.
            if (okN < devs.length) {
                showToast(
                    tp("toast.homeworkPartial", { ok: okN, count: devs.length }),
                    <I.wifiOff size={16} style={{ color: "var(--warn)" }} />,
                );
                return;
            }
            const name = playlistId ? (plById[playlistId]?.name ?? tp("fallback.playlist")) : "";
            showToast(
                devs.length > 1
                    ? playlistId
                        ? tp("toast.homeworkSetN", { count: devs.length, name })
                        : tp("toast.homeworkClearedN", { count: devs.length })
                    : playlistId
                      ? tp("toast.homeworkSet", { name })
                      : tp("toast.homeworkCleared"),
                <I.list size={16} style={{ color: "var(--accent)" }} />,
            );
        },
        [dojang, devicesChanged, showToast, plById],
    );
    // 숙제 고르기 — 관장이 만든 목록을 먼저 세운다(대개 그 아이용으로 새로 만든 것이라).
    // devs 가 여럿이면 체크는 **다 같은 숙제일 때만** 켠다(섞여 있으면 무엇도 '지금 것'이 아니다).
    const homeworkMenu = (devs: DeviceView[]) => {
        if (!devs.length) return;
        // ⭐ 2026-09-04 숙제로 낼 재생목록 고르기는 **'무엇을 틀까요?' 시트(LoadSheet) 그대로**다 —
        //    [기본 동작·품새 | 내 재생목록] 탭·기본동작/태극 묶음·썸네일 줄·파란 '만들기'. 위에 '숙제 없음' 한 줄만 더.
        // 🛑 ActionSheet 로 재생목록을 나열하는 판(큰 상자·아이콘 줄) 두 번 다 반려됐다. 다시 그리지 말 것.
        setHwPickDevs(devs);
    };
    // 고른 기기들에 한 번에 숙제 — 선택 줄의 버튼이 여기로 온다. 집 화면만 남긴다(숙제는 집 화면에서만 먹는다).
    const renameDevice = (dev: DeviceView) =>
        setModal({
            kind: "prompt",
            title: tp("dialog.renameDeviceTitle"),
            label: tp("field.name"),
            // 🔑 집 화면은 목록에 **학생 이름**으로 뜨지만(dev.name), 여기서 고치는 건 그 화면 이름이다.
            //    dev.name 을 채워 넣으면 관장이 학생 이름을 고치는 줄 알고 지웠다 다시 쓴다.
            initial: dev.deviceName ?? dev.name,
            placeholder: tp("ph.deviceName"),
            onSubmit: async (name) => {
                await fetch(`/api/devices/${dev.id}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ name }),
                });
                if (dojang) devicesChanged(dojang.id);
                showToast(tp("toast.renamed"));
            },
        });
    // ---------- 수련생 링크(홈 트레이닝 등록의 링크 방식, 2026-09-10) ----------
    // 수련생 + 링크 만들기(POST /students). 목록을 다시 읽고 토큰을 돌려준다(화면이 링크를 보여준다).
    const createStudentLink = async (name: string, rankFormId: string | null, classId: string | null = null): Promise<{ token: string } | null> => {
        if (!dojang) return null;
        const r = await fetch(`/api/dojangs/${dojang.id}/students`, {
            method: "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name, rankFormId, classId }),
        }).catch(() => null);
        if (!r?.ok) return null;
        const body = (await r.json()) as { linkToken?: string | null };
        void devicesChanged(dojang.id);
        return body.linkToken ? { token: body.linkToken } : null;
    };
    // 링크 (다시) 만들기 — 옛 링크는 죽고 붙은 기기는 남는다(서버 규칙). 처음 만드는 수련생(코드 등록)도 여기.
    const regenStudentLink = async (studentId: string): Promise<string | null> => {
        const r = await fetch(`/api/students/${studentId}/link`, { method: "POST" }).catch(() => null);
        if (!r?.ok) return null;
        const body = (await r.json()) as { linkToken?: string | null };
        if (dojang) void devicesChanged(dojang.id);
        return body.linkToken ?? null;
    };
    // 링크 시트 — 링크 복사·공유·새로 만들기(+ 링크만 있는 수련생은 지우기). 부제에 링크 자체를 적어 눈으로 확인한다.
    const linkMenu = (st: StudentView) => {
        const url = st.linkToken ? homeLinkUrl(st.linkToken) : null;
        const shareText = tl("shareText", { dojang: dojang?.name ?? "", name: st.name });
        const regen = () => setModal({
            kind: "confirm",
            title: tl("regenConfirmTitle"),
            message: tl("regenConfirmMsg"),
            confirmLabel: tl("regen"),
            onConfirm: async () => {
                const token = await regenStudentLink(st.id);
                if (!token) return showToast(tl("failed"));
                showToast(tl("regenDone"));
                linkMenu({ ...st, linkToken: token });
            },
        });
        setModal({
            kind: "action",
            title: tl("menuTitle"),
            subtitle: url ?? tl("menuSub", { name: st.name, count: st.deviceCount }),
            actions: [
                ...(url
                    ? [
                          { label: tl("copy"), icon: <I.copy size={19} />, onClick: async () => { if (await copyText(url)) showToast(tl("copied")); } } satisfies ModalAction,
                          ...(canShare()
                              ? [{ label: tl("share"), icon: <I.share size={19} />, onClick: () => void shareLink({ title: shareText, text: shareText, url }) } satisfies ModalAction]
                              : []),
                          // 확인창이 한 번 더 있으니 줄 자체는 조용히(주황 면은 시트의 주인공이 돼 버린다).
                          { label: tl("regen"), desc: tl("regenHint"), icon: <I.refresh size={19} />, onClick: regen } satisfies ModalAction,
                      ]
                    : [{ label: tl("makeFirst"), desc: tl("makeFirstHint"), icon: <I.link size={19} />, accent: true, onClick: async () => {
                          const token = await regenStudentLink(st.id);
                          if (!token) return showToast(tl("failed"));
                          linkMenu({ ...st, linkToken: token });
                      } } satisfies ModalAction]),
                // 링크만 있는 수련생은 목록에서 고를 수 없어 '선택 → 반 옮기기'가 안 닿는다 — 반을 만든 도장에선 여기서 옮긴다.
                ...(st.deviceCount === 0 && classes.length > 0
                    ? [{ label: th("moveClass"), hint: classNameOf(st.id) ?? tk("rowNone"), icon: <I.layers size={19} />, onClick: () => moveStudentsClass([st.id]) } satisfies ModalAction]
                    : []),
                // 링크만 있고 기기가 없는 수련생 — 잘못 만들었으면 여기서 지운다(기기가 있으면 학생 시트의 '수련생 지우기'가 한다).
                ...(st.deviceCount === 0
                    ? [{ label: tp("action.deleteStudent"), icon: <I.trash size={19} />, danger: true, onClick: () => setModal({
                          kind: "confirm",
                          title: tp("dialog.deleteStudentTitle"),
                          message: tp("dialog.deleteStudentMsg", { name: st.name }),
                          confirmLabel: tp("action.deleteStudent"),
                          onConfirm: async () => {
                              const r = await fetch(`/api/students/${st.id}`, { method: "DELETE" }).catch(() => null);
                              if (!r?.ok) return showToast(tp("toast.deleteStudentFailed"));
                              if (dojang) devicesChanged(dojang.id);
                              showToast(tp("toast.deletedStudent", { name: st.name }));
                          },
                      }) } satisfies ModalAction]
                    : []),
            ],
        });
    };
    // ---------- 반(2026-10-07) ----------
    // 반은 수련생마다 하나. 만들고 고치는 자리는 설정 > 수련생과 홈 트레이닝 기기 > 반 나누기 한 곳,
    // 넣고 옮기는 자리는 홈 트레이닝 탭의 '선택 → 반 옮기기' 한 곳(한 명이든 여러 명이든).
    const classCount = (classId: string | null) => students.filter((s) => (s.classId && classes.some((c) => c.id === s.classId) ? s.classId : null) === classId).length;
    const classNameOf = (studentId: string | null | undefined) => {
        const cid = studentId ? students.find((s) => s.id === studentId)?.classId : null;
        return cid ? classes.find((c) => c.id === cid)?.name ?? null : null;
    };
    const saveClassName = async (id: string | null, name: string): Promise<ClassView | null> => {
        if (!dojang || !name.trim()) return null;
        const r = await fetch(id ? `/api/classes/${id}` : `/api/dojangs/${dojang.id}/classes`, {
            method: id ? "PATCH" : "POST", headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ name: name.trim() }),
        }).catch(() => null);
        if (!r?.ok) { showToast(tk(r?.status === 409 ? "duplicate" : "failed")); return null; }
        const saved = (await r.json()) as ClassView;
        await devicesChanged(dojang.id);
        showToast(id ? tk("renamed") : tk("created", { name: name.trim() }));
        return saved;
    };
    // 등록 화면 반 칸의 '새 반 만들기' — 이름을 묻고, 만든 반을 돌려준다(화면이 그 반을 골라 둔다). 취소하면 답이 없다.
    const addClassInline = () => new Promise<ClassView | null>((resolve) => setModal({
        kind: "prompt", title: tk("addTitle"), label: tk("nameLabel"), placeholder: tk("namePh"), confirmLabel: tk("add"), danger: false,
        onSubmit: (name) => void saveClassName(null, name).then(resolve),
    }));
    const addClass = () => setModal({
        kind: "prompt", title: tk("addTitle"), label: tk("nameLabel"), placeholder: tk("namePh"), confirmLabel: tk("add"), danger: false,
        onSubmit: (name) => void saveClassName(null, name),
    });
    const classMenu = (c: ClassView) => setModal({
        kind: "action", title: c.name, subtitle: tk("count", { count: classCount(c.id) }),
        actions: [
            { label: tk("rename"), icon: <I.layers size={19} />, onClick: () => setModal({
                kind: "prompt", title: tk("rename"), label: tk("nameLabel"), initial: c.name, placeholder: tk("namePh"), danger: false,
                onSubmit: (name) => void saveClassName(c.id, name),
            }) },
            { label: tk("delete"), icon: <I.trash size={19} />, danger: true, onClick: () => setModal({
                kind: "confirm", title: tk("deleteTitle"), message: tk("deleteMsg", { name: c.name, count: classCount(c.id) }), confirmLabel: tk("delete"),
                onConfirm: async () => {
                    const r = await fetch(`/api/classes/${c.id}`, { method: "DELETE" }).catch(() => null);
                    if (!r?.ok) return showToast(tk("failed"));
                    if (dojang) await devicesChanged(dojang.id);
                    showToast(tk("deleted"));
                },
            }) },
        ],
    });
    // 반 옮기기 — 고른 수련생(홈 트레이닝 탭의 줄 = 대표 기기)을 한 반으로. 맨 아래 '반 없음'.
    const moveClassMenu = (picked: DeviceView[]) =>
        moveStudentsClass(Array.from(new Set(picked.map((d) => d.studentId).filter((x): x is string => !!x))));
    const moveStudentsClass = (ids: string[]) => {
        if (!dojang) return;
        if (ids.length === 0) return;
        const apply = async (c: ClassView | null) => {
            const r = await fetch(`/api/dojangs/${dojang.id}/classes/assign`, {
                method: "POST", headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ studentIds: ids, classId: c?.id ?? null }),
            }).catch(() => null);
            if (!r?.ok) return showToast(tk("moveFailed"));
            await devicesChanged(dojang.id);
            showToast(c ? tk("moved", { count: ids.length, name: c.name, josa: josaRo(c.name) }) : tk("movedNone", { count: ids.length }));
        };
        setModal({
            kind: "action", title: tk("moveTitle"), subtitle: tk("moveSub", { count: ids.length }),
            actions: [
                ...classes.map((c) => ({ label: c.name, hint: tk("count", { count: classCount(c.id) }), onClick: () => void apply(c) } satisfies ModalAction)),
                { label: tk("rowNone"), onClick: () => void apply(null) },
            ],
        });
    };
    const renameStudent = (dev: DeviceView) =>
        setModal({
            kind: "prompt",
            title: tp("dialog.renameStudentTitle"),
            label: tp("field.studentName"),
            initial: dev.name,
            placeholder: tp("ph.studentName"),
            onSubmit: async (studentName) => {
                const r = await fetch(`/api/devices/${dev.id}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ studentName }),
                }).catch(() => null);
                if (!r?.ok) return showToast(tp("toast.renameFailed"));
                if (dojang) devicesChanged(dojang.id);
                showToast(tp("toast.renamed"));
            },
        });
    const unpairDevice = (dev: DeviceView) =>
        setModal({
            kind: "confirm",
            title: tp("dialog.unpairTitle"),
            // 🔑 집 화면은 목록 이름이 **학생 이름**이라 도장용 문구("'철수' 기기를 도장에서 제거")를 그대로 쓰면
            //    학생을 지우는 것처럼 읽힌다(2026-09-04 사용자 혼동). 화면 하나만 떼고 학생·기록은 남는다고 말한다.
            message: dev.atHome
                ? tp("dialog.unpairHomeMsg", { device: dev.deviceName ?? dev.name, name: dev.name })
                : tp("dialog.unpairMsg", { name: dev.name }),
            confirmLabel: tp("action.unpair"),
            onConfirm: async () => {
                await fetch(`/api/devices/${dev.id}`, { method: "DELETE" });
                // 지운 **뒤** 그 기기에 알린다 — 기기가 스스로 등록 화면(등록번호)으로 돌아간다.
                // 🔑 순서가 중요하다: 먼저 알리면 기기가 아직 지워지기 전에 '살아있다'고 확인하고
                //    그대로 남는다(기기는 이 신호를 REST 401 로 재확인한 뒤에만 등록을 버린다).
                sockRef.current?.emit("device:unpaired", { deviceId: dev.id });
                setScreen("main");
                setSelId(null);
                if (dojang) devicesChanged(dojang.id);
                showToast(tp("toast.unpaired"));
            },
        });
    // 학생 지우기 — 그 학생과 학생 화면 등록을 함께 없앤다.
    // 🔑 화면만 등록 해제하면 학생은 남는다(등록 때 '등록된 학생'으로 다시 이으라고 남기는 것이다).
    //    그래서 아주 지우는 길은 따로 둔다 — 잘못 만든 학생·그만둔 아이가 목록에 영영 남지 않게.
    const deleteStudent = (dev: DeviceView) =>
        setModal({
            kind: "confirm",
            title: tp("dialog.deleteStudentTitle"),
            message: tp("dialog.deleteStudentMsg", { name: dev.name }),
            confirmLabel: tp("action.deleteStudent"),
            onConfirm: async () => {
                if (!dev.studentId) return;
                const r = await fetch(`/api/students/${dev.studentId}`, { method: "DELETE" }).catch(() => null);
                const body = r?.ok ? ((await r.json()) as { deviceIds?: string[] }) : null;
                if (!body) return showToast(tp("toast.deleteStudentFailed"));
                // 지운 **뒤** 그 화면들에 알린다 — 순서가 바뀌면 기기가 '살아있다'고 확인하고 그대로 남는다.
                for (const id of body.deviceIds ?? []) sockRef.current?.emit("device:unpaired", { deviceId: id });
                setScreen("main");
                setSelId(null);
                if (dojang) devicesChanged(dojang.id);
                showToast(tp("toast.deletedStudent", { name: dev.name }));
            },
        });
    /* 재생목록 상세 → 기기 선택해 바로 재생 (이전엔 "기기를 선택해 불러오세요" 안내만 띄움) */
    const loadToDevice = (playlistId: string) => {
        const online = devices.filter((d) => d.online);
        if (online.length === 0) {
            showToast(tp("toast.noDevicesOn"));
            return;
        }
        setModal({
            kind: "action",
            title: tp("dialog.whichDevice"),
            subtitle: tp("dialog.sendsPlaylist"),
            actions: online.map((d) => ({
                label: d.name,
                icon: <I.monitor size={19} />,
                hint:
                    d.status === "playing"
                        ? tc("status.playing")
                        : d.status === "paused"
                          ? tc("status.paused")
                          : tc("status.idle"),
                onClick: () => {
                    sockRef.current?.emit(
                        "command",
                        {
                            action: "LOAD_PLAYLIST",
                            deviceId: d.id,
                            playlistId,
                            autoplay: true,
                        } satisfies Command,
                        (res: CommandResult) => {
                            if (!res?.ok) {
                                console.warn("command failed:", res?.reason);
                                showToast(
                                    tp("toast.cmdFailedRetry"),
                                );
                            }
                        },
                    );
                    setSelId(d.id);
                    setTab("devices"); // 태블릿 2단 제어판은 '기기' 탭에서만 렌더 — 라이브러리서 캐스트해도 제어화면 뜨게 탭 전환
                    setScreen("control");
                    showToast(
                        tp("toast.playlistLoaded"),
                        <I.cast size={16} style={{ color: "var(--live)" }} />,
                    );
                },
            })),
        });
    };

    // 플레이어 터치 잠금 — 소켓 명령 먼저(즉시 적용), DB 영속(PATCH)은 병행. 해제 권한은 컨트롤러뿐.
    // 모바일 실측(MOBILE_DIAG): 미러 다운로드가 회선을 점유하면 PATCH 가 2~6초 — 예전처럼 PATCH 를
    // 기다렸다 명령을 보내면 그 시간만큼 잠금이 늦었다. 칩은 디바이스 state 보고(소켓)로 ~0.2초 내 반영.
    const setTouchLock = useCallback(
        async (dev: DeviceView, locked: boolean) => {
            const t0 = performance.now();
            diag(
                "lock",
                `토글 시작 → ${locked ? "잠금" : "해제"} (online=${dev.online})`,
            );
            if (dev.online) {
                cmd("SET_TOUCH_LOCK", locked ? 1 : 0);
                showToast(
                    locked
                        ? tp("toast.touchLocked")
                        : tp("toast.touchUnlocked"),
                    <I.lock
                        size={16}
                        style={{
                            color: locked ? "var(--warn)" : "var(--live)",
                        }}
                    />,
                );
            }
            const r = await fetch(`/api/devices/${dev.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ touchLocked: locked }),
            }).catch(() => null);
            diag(
                "lock",
                `PATCH 완료 +${(performance.now() - t0).toFixed(0)}ms ok=${!!r?.ok}`,
            );
            if (!r?.ok) {
                // 영속 실패 — 재부팅/재접속 때 옛 값으로 돌아가므로 즉시 원복해 상태 불일치를 막는다
                showToast(
                    tp("toast.touchSaveFail"),
                    <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
                );
                if (dev.online) cmd("SET_TOUCH_LOCK", locked ? 0 : 1);
                if (dojang) devicesChanged(dojang.id);
                return;
            }
            if (!dev.online)
                showToast(
                    locked
                        ? tp("toast.touchLocked")
                        : tp("toast.touchUnlocked"),
                    <I.lock
                        size={16}
                        style={{
                            color: locked ? "var(--warn)" : "var(--live)",
                        }}
                    />,
                );
            if (dojang)
                devicesChanged(dojang.id).then(() =>
                    diag(
                        "lock",
                        `기기 목록 재조회 완료 +${(performance.now() - t0).toFixed(0)}ms (REST 경유 칩 반영 시점)`,
                    ),
                );
        },
        [cmd, dojang, devicesChanged, showToast],
    );

    // 화면 새로고침 — 플레이어 웹페이지를 다시 로드해 '배포된 최신본'을 받게 한다(앱 재빌드 불필요).
    // 벽에 붙어 계속 켜진 키오스크는 스스로 리로드하지 않으므로, 새 버전 반영은 이 원격 리로드로.
    // 누르면 바로 실행하고 알림만 띄운다 — 예전엔 확인 창을 한 번 더 거쳤는데(재생 중이면 잠깐 끊긴다는 안내)
    // 배포 뒤 기기마다 누를 때 번거롭기만 했다(2026-09-21 사용자 "굳이 안내 띄워야 하나? 그거 누르면 새로고침되면 안 돼?").
    // 여러 대 일괄 새로고침(onBatch RELOAD)은 원래 확인 없이 바로였다.
    const reloadDevice = (dev: DeviceView) => {
        if (!dev.online) {
            showToast(
                tp("toast.deviceOfflineReconnect"),
                <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
            );
            return;
        }
        cmd("RELOAD");
        showToast(
            tp("toast.screenRefreshed"),
            <I.refresh size={16} style={{ color: "var(--live)" }} />,
        );
    };

    const launchPlayerApp = async (dev: DeviceView) => {
        if (!dojang) return;
        // 예전엔 online 이면 "이미 실행 중"으로 막았는데, online(소켓 연결) ≠ 화면에 떠 있음이다:
        // 백그라운드로 빠지거나 포그라운드인 채 먹통이 돼도 소켓은 살아 있어, 정작 앞으로 끌어와야 할 상황을 차단했다.
        // OPEN_PLAYER 푸시는 이미 떠 있는 앱엔 무해(no-op: onNewIntent 만, WebView·재생 그대로)하고, 내려가 있으면 앞으로 부른다.
        // 그러니 상태와 무관하게 항상 쏜다 — "이미 실행 중"으로 막지 않는다.
        // hasPushToken 은 stale 일 수 있다(기기가 방금 등록해 토큰을 올렸으면 false 로 남음).
        // 클라에서 선차단하지 않고 서버가 실제 DB 로 판단하게 한다 — 토큰이 정말 없을 때만 아래 NO_PUSH_TOKEN 으로 안내.
        const res = await fetch(
            `/api/dojangs/${dojang.id}/devices/${dev.id}/launch`,
            {
                method: "POST",
            },
        ).catch(() => null);
        // 결과와 무관하게 기기 목록을 다시 읽어 hasPushToken 을 최신화(등록 여부 표시가 계속 어긋나지 않게).
        void refreshDevices(dojang.id);
        if (res?.ok) {
            // 진짜 포그라운드(online && !hidden)면 이미 떠 있는 앱에 무해한 no-op 을 쏜 것 — "보냈어요"는 군더더기라
            // 있는 그대로 알려준다. 백그라운드·먹통·오프라인이면 실제로 불러오는 것이므로 "요청 보냈어요".
            showToast(
                dev.online && !dev.hidden
                    ? tp("toast.playerRunning")
                    : tp("toast.playerLaunchSent"),
                <I.power size={16} style={{ color: "var(--live)" }} />,
            );
            return;
        }
        const body = await res?.json().catch(() => null);
        const code = body?.error?.code;
        // NO_PUSH_TOKEN: 기기 앱에서 등록한 적이 없거나(브라우저로 등록), 기기를 지웠다 다시 등록해 토큰이 사라진 상태.
        // 앱을 한 번 열어 등록하면 토큰이 다시 저장된다 — 관장이 바로 할 수 있는 행동으로 안내한다.
        const message =
            code === "NO_PUSH_TOKEN"
                ? tp("toast.noPushToken")
                : code === "STORE_BUILD"
                  ? tp("toast.storeBuild")
                  : code === "FCM_NOT_CONFIGURED"
                  ? tp("toast.fcmNotConfigured")
                  : tp("toast.launchFailed");
        showToast(
            message,
            <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
        );
    };

    // 설정 > 학생과 집 화면에서 화면 한 줄을 눌렀을 때 — 기기 자체를 손보는 항목만(이름·등록 해제·학생 지우기).
    const homeDeviceManageMenu = (dev: DeviceView) => {
        setModal({
            kind: "action",
            title: dev.deviceName ?? dev.name,
            subtitle: dev.studentId ? dev.name : undefined,
            actions: [
                ...(dev.studentId
                    ? [{
                          label: tp("menu.renameStudent"),
                          hint: dev.name,
                          icon: <I.user size={19} />,
                          onClick: () => renameStudent(dev),
                      } satisfies ModalAction]
                    : []),
                // 여기선 화면이 하나뿐이어도 이름을 바꿀 수 있게 둔다 — 이 화면의 목적이 기기 관리라서.
                {
                    label: tp("menu.renameDevice"),
                    hint: dev.deviceName,
                    icon: <I.monitor size={19} />,
                    onClick: () => renameDevice(dev),
                },
                // 화면 하나만 떼기 — 학생은 남는다(새 화면으로 다시 이을 수 있게).
                {
                    label: tp("action.unpair"),
                    icon: <I.logout size={19} />,
                    danger: true,
                    onClick: () => unpairDevice(dev),
                },
                // 아주 지우기 — 학생과 그 학생 화면 전부. 되돌릴 수 없어 맨 끝에 둔다.
                ...(dev.studentId
                    ? [{
                          label: tp("action.deleteStudent"),
                          icon: <I.trash size={19} />,
                          danger: true,
                          onClick: () => deleteStudent(dev),
                      } satisfies ModalAction]
                    : []),
            ],
        });
    };
    // 홈워킹 탭은 같은 학생의 화면 여러 대를 **한 줄로 접는다**(대표 기기 하나). 그래서 화면 한 대를 고르는 일
    // (이름 바꾸기·등록 해제)은 그 학생 화면이 둘 이상이면 **어느 화면인지 먼저 묻는다**(2026-09-04 사용자 지적 —
    // 전엔 대표 기기에 조용히 걸려 어느 폰이 풀리는지 알 수 없었다). 한 대뿐이면 바로 간다.
    const homeScreensOf = (dev: DeviceView) =>
        dev.studentId ? devices.filter((d) => d.atHome && d.studentId === dev.studentId) : [dev];
    const pickHomeScreen = (dev: DeviceView, title: string, then: (d: DeviceView) => void) => {
        const list = homeScreensOf(dev);
        if (list.length <= 1) return then(dev);
        setModal({
            kind: "action",
            title,
            subtitle: tp("dialog.whichScreenSub", { name: dev.name, count: list.length }),
            actions: list.map((d) => ({
                label: d.deviceName ?? d.name,
                hint: d.online ? tHome("online") : lastSeenText(tHome, d.lastSeen, locale),
                icon: <I.monitor size={19} />,
                onClick: () => then(d),
            })),
        });
    };
    const deviceMenu = (dev: DeviceView) => {
        // 집 화면(학생 집) — 도장 화면과 할 수 있는 일이 다르다. 재생·잠금·원격실행이 아니라
        // '이 아이에게 무엇을 내주나'(숙제·보이는 범위)가 이 메뉴의 전부다.
        if (dev.atHome) {
            // ⚠️ 2026-09-03 숙제·자습 범위만 남기고 기기 항목을 설정 > 학생과 집 화면으로 옮겼다가, 다음 날 사용자가
            //    "이전이 낫다"고 해 되돌렸다(2026-09-04). 여기서도 이름·등록 해제·학생 지우기까지 다 된다.
            //    설정 > 학생과 집 화면(homeDeviceManageMenu)은 그대로 있다 — 같은 일을 두 자리에서 할 수 있는 것.
            setModal({
                kind: "action",
                title: dev.name,
                // 반이 있으면 부제 앞에 반 이름 — 시트에 줄을 더하지 않고 여기서만 보인다(옮기기는 '선택 → 반 옮기기').
                subtitle: [classNameOf(dev.studentId), tp("menu.homeStudentSettings")].filter(Boolean).join(" · "),
                actions: [
                    // 목록에 뜨는 이름은 **학생 이름**이다 — 그래서 이게 먼저다.
                    // 🔑 두 '이름 바꾸기'가 나란히 설 때(한 학생이 폰·PC 둘 다 등록한 경우) 헷갈리지 않게
                    //    **지금 이름을 옆에 적는다** — 설명을 읽지 않아도 무엇을 고치는지 눈으로 갈린다.
                    ...(dev.studentId
                        ? [{
                              label: tp("menu.renameStudent"),
                              hint: dev.name,
                              icon: <I.user size={19} />,
                              onClick: () => renameStudent(dev),
                          } satisfies ModalAction]
                        : []),
                    // 화면 이름은 **그 학생 화면이 둘 이상일 때만** 뜻이 있다(폰과 PC 를 갈라 부를 때).
                    // 한 대뿐이면 고쳐 봐야 아무 데도 안 쓰인다 — 줄만 늘어난다.
                    // 둘 이상이면 이 줄은 대표 기기 이름이 아니라 대수를 보이고, 누르면 어느 화면인지 먼저 고른다.
                    ...(!dev.studentId || homeScreensOf(dev).length > 1
                        ? [{
                              label: tp("menu.renameDevice"),
                              hint: homeScreensOf(dev).length > 1 ? tp("menu.screenCount", { count: homeScreensOf(dev).length }) : dev.deviceName,
                              icon: <I.monitor size={19} />,
                              onClick: () => pickHomeScreen(dev, tp("dialog.whichScreenRename"), renameDevice),
                          } satisfies ModalAction]
                        : []),
                    // 숙제 — 학생 카드 화면 맨 앞에 뜨는 그 한 장.
                    ...(owner.homeDevice
                        ? [{
                              label: tp("menu.homework"),
                              hint: dev.homePlaylistId
                                  ? (plById[dev.homePlaylistId]?.name ?? tp("fallback.playlist"))
                                  : tp("menu.homeworkNone"),
                              icon: <I.list size={19} />,
                              onClick: () => homeworkMenu([dev]),
                          } satisfies ModalAction]
                        : []),
                    // 자습 범위 — 배운 데까지만 학생 목록에 남긴다(관장이 보내는 건 안 막는다).
                    ...(owner.homeDevice
                        ? [{
                              label: tp("menu.range"),
                              hint: dev.rankFormId === RANK_BASICS
                                  ? tp("range.upTo", { form: tp("range.basicsName") })
                                  : (masterForms.find((f) => f.id === dev.rankFormId)?.name ?? tp("range.all")),
                              icon: <I.layers size={19} />,
                              onClick: () => rankMenu(dev),
                          } satisfies ModalAction]
                        : []),
                    // 홈 트레이닝 링크 — 예전에 코드로 등록한 수련생도 여기서 링크를 처음 만들 수 있다.
                    ...(owner.homeDevice && dev.studentId
                        ? [{
                              label: tp("menu.homeLink"),
                              hint: students.find((x) => x.id === dev.studentId)?.linkToken ? tp("menu.screenCount", { count: homeScreensOf(dev).length }) : tl("menuNone"),
                              icon: <I.link size={19} />,
                              onClick: () => {
                                  const st = students.find((x) => x.id === dev.studentId);
                                  linkMenu(st ?? { id: dev.studentId!, name: dev.name, rankFormId: dev.rankFormId, homePlaylistId: dev.homePlaylistId, linkToken: null, deviceCount: homeScreensOf(dev).length });
                              },
                          } satisfies ModalAction]
                        : []),
                    // '도장 화면으로 되돌리기'는 없다(2026-09-03 사용자 결정) — 잘못 등록한 화면은 아래 '등록 해제'로
                    // 떼고 다시 등록하면 된다.
                    // 화면 하나만 떼기 — 학생은 남는다(새 화면으로 다시 이을 수 있게). 둘 이상이면 어느 화면인지 고른다.
                    {
                        label: tp("action.unpair"),
                        hint: homeScreensOf(dev).length > 1 ? tp("menu.screenCount", { count: homeScreensOf(dev).length }) : undefined,
                        icon: <I.logout size={19} />,
                        danger: true,
                        onClick: () => pickHomeScreen(dev, tp("dialog.whichScreenUnpair"), unpairDevice),
                    },
                    // 아주 지우기 — 학생과 그 학생 화면 전부. 되돌릴 수 없어 맨 끝에 둔다.
                    ...(dev.studentId
                        ? [{
                              label: tp("action.deleteStudent"),
                              icon: <I.trash size={19} />,
                              danger: true,
                              onClick: () => deleteStudent(dev),
                          } satisfies ModalAction]
                        : []),
                ],
            });
            return;
        }
        setModal({
            kind: "action",
            title: dev.name,
            subtitle: dev.online ? tc("dash.online") : tc("status.offline"),
            actions: [
                // 터치 잠금 토글 — 헤더 과밀 정리로 더보기 메뉴로 이동 (2026-06-29).
                // 잠긴 상태(해제 버튼)는 이전 헤더 버튼과 동일하게 주황(warn)으로 표시.
                {
                    label: dev.touchLocked ? tp("menu.unlockTouch") : tp("menu.lockTouch"),
                    icon: dev.touchLocked ? <I.unlock size={19} /> : <I.lock size={19} />,
                    warn: dev.touchLocked,
                    onClick: () => setTouchLock(dev, !dev.touchLocked),
                },
                // 원격 실행은 '플레이어 앱'으로 등록한 안드로이드 기기만 가능하다(브라우저로 등록한 기기는 알림을 못 받음).
                // 앱이 없는 플랫폼(아이폰·PC 등)은 아예 숨기고, 앱 기기인데 등록 기록이 없으면 흐리게 + 이유를 붙인다.
                // 막지는 않는다 — 방금 앱에서 등록했다면 hasPushToken 이 아직 stale false 일 수 있어 서버가 최종 판단.
                // 스토어판(fromStore)은 안드로이드여도 못 켠다 — 기기 메뉴에서도 숨긴다(제어화면엔 안내가 뜬다).
                ...(remoteLaunchCopy(dev.platform, dev.hasPushToken ?? false, dev.fromStore).canLaunch
                    ? [
                          {
                              label: tp("menu.launchApp"),
                              icon: <I.power size={19} />,
                              // online 이어도 백그라운드·먹통일 수 있어 "이미 실행 중"으로 막지 않는다(눌러서 앞으로 부른다).
                              // 등록 기록이 없을 때만 흐리게 + 이유를 붙인다(막지는 않음 — 서버가 최종 판단).
                              hint: dev.hasPushToken ? undefined : tp("menu.needAppRegister"),
                              dim: !dev.hasPushToken,
                              onClick: () => {
                                  void launchPlayerApp(dev);
                              },
                          } satisfies ModalAction,
                      ]
                    : []),
                // 화면 새로고침 — 새 버전 배포 후 벽 기기에 반영시킬 때(원격 리로드).
                {
                    label: tp("dialog.reloadTitle"),
                    icon: <I.refresh size={19} />,
                    onClick: () => reloadDevice(dev),
                },
                // 수업 종료는 제어화면 헤더의 보이는 버튼으로 빼냄(2026-06-29) — 메뉴에서 제거.
                {
                    label: tp("action.rename"),
                    icon: <I.settings size={19} />,
                    onClick: () => renameDevice(dev),
                },
                // 🛑 숙제·혼자 고를 수 있는 범위는 여기 없다 — 둘 다 집 화면에서만 뜻이 있어 위쪽
                //    집 화면 메뉴가 통째로 맡는다(도장 화면은 수업용이라 안 자르고, 숙제도 안 나온다).
                {
                    label: tp("action.unpair"),
                    icon: <I.logout size={19} />,
                    danger: true,
                    onClick: () => unpairDevice(dev),
                },
            ],
        });
    };

    const renameVideo = (v: VideoView) =>
        setModal({
            kind: "prompt",
            title: tp("dialog.renameVideoTitle"),
            label: tp("field.title"),
            initial: v.title,
            placeholder: tp("ph.videoTitle"),
            onSubmit: async (title) => {
                await fetch(`/api/videos/${v.id}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ title }),
                });
                if (dojang) refreshVideos(dojang.id);
                showToast(tp("toast.renamed"));
            },
        });
    const deleteVideo = (v: VideoView) =>
        setModal({
            kind: "confirm",
            title: tp("dialog.deleteVideoTitle"),
            message: tp("dialog.deleteVideoMsg", { title: v.title }),
            onConfirm: async () => {
                await fetch(`/api/videos/${v.id}`, { method: "DELETE" });
                if (dojang) {
                    refreshVideos(dojang.id);
                    refreshPlaylists(dojang.id);
                }
                showToast(tp("toast.deleted"));
            },
        });
    const videoMenu = (v: VideoView) =>
        setModal({
            kind: "action",
            title: v.title,
            subtitle: `${fmt(v.durationSec)}${v.sizeBytes != null ? ` · ${(v.sizeBytes / 1024 / 1024).toFixed(0)}MB` : ""}`,
            actions: [
                {
                    label: tp("action.rename"),
                    icon: <I.settings size={19} />,
                    onClick: () => renameVideo(v),
                },
                {
                    label: tc("common.delete"),
                    icon: <I.trash size={19} />,
                    danger: true,
                    onClick: () => deleteVideo(v),
                },
            ],
        });

    const createPlaylist = (origin?: "main" | "control") =>
        setModal({
            kind: "prompt",
            title: tp("dialog.newPlaylistTitle"),
            label: tp("field.name"),
            placeholder: tp("ph.playlistExample"),
            confirmLabel: tp("action.create"),
            onSubmit: async (name) => {
                if (!dojang) return;
                const pl: PlaylistRaw = await fetch(
                    `/api/dojangs/${dojang.id}/playlists`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ name, items: [] }),
                    },
                ).then((r) => r.json());
                await refreshPlaylists(dojang.id);
                if (pl?.id) {
                    setSelPlId(pl.id);
                    setPlReturn(origin === "control" ? "control" : "main");
                    // 만들자마자 영상 고르기 시트를 연다 — 빈 목록("아직 영상이 없습니다")에 떨궈 놓고
                    // '영상 추가'를 한 번 더 누르게 하는 건 이름을 지은 직후 아무도 원하지 않는 단계다.
                    setPlAutoPick(true);
                    setScreen("pldetail");
                }
                showToast(tp("toast.playlistCreated"));
            },
        });
    // 영상별 반복 횟수(repeats)는 항목과 함께 PUT — 생략 시 기존 맵 유지 (정렬/추가/삭제에도 보존)
    // 저장 성공 여부를 반환 — 실패해도 재조회로 서버 순서에 맞춰 화면을 되돌린다.
    const persistPlaylist = useCallback(
        async (
            plId: string,
            items: string[],
            repeats?: Record<string, number>,
        ) => {
            const rep = repeats ?? plById[plId]?.repeats ?? {};
            const res = await fetch(`/api/playlists/${plId}`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({
                    items: items.map((videoId, position) => ({
                        videoId,
                        position,
                        repeat: rep[videoId] ?? 1,
                    })),
                }),
            }).catch(() => null);
            if (dojang) await refreshPlaylists(dojang.id);
            return !!res?.ok;
        },
        [dojang, refreshPlaylists, plById],
    );
    const persistFailToast = useCallback(
        (msg: string) => {
            showToast(
                msg,
                <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
            );
        },
        [showToast],
    );
    // ── 기본/품새(제공) 재생목록 도장별 커스터마이즈 — 오버라이드 엔드포인트(마스터/기본 원본 무손상) ──
    // 제공 영상 재생 숨김/해제(목록엔 유지). 성공 시 목록 재조회로 hidden 확정.
    const toggleHiddenDefault = useCallback(
        async (plId: string, videoId: string, hidden: boolean) => {
            if (!dojang) return;
            await fetch(`/api/dojangs/${dojang.id}/playlists/${plId}/hidden`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ videoId, hidden }),
            }).catch(() => null);
            await refreshPlaylists(dojang.id);
        },
        [dojang, refreshPlaylists],
    );
    // 기본/품새 재생목록에 내 영상 덧붙이기 / 덧붙인 영상 빼기.
    // 여러 개를 받는다 — 항목 API 가 1개씩만 받아 POST 는 순차로 돌지만, 목록 재조회는 끝나고 한 번만.
    const addToDefault = useCallback(
        async (plId: string, videoIds: string[]) => {
            if (!dojang || videoIds.length === 0) return false;
            let ok = true;
            for (const videoId of videoIds) {
                const res = await fetch(
                    `/api/dojangs/${dojang.id}/playlists/${plId}/items`,
                    {
                        method: "POST",
                        headers: { "Content-Type": "application/json" },
                        body: JSON.stringify({ videoId }),
                    },
                ).catch(() => null);
                if (!res?.ok) ok = false;
            }
            await refreshPlaylists(dojang.id);
            return ok;
        },
        [dojang, refreshPlaylists],
    );
    const removeFromDefault = useCallback(
        async (plId: string, videoId: string) => {
            if (!dojang) return;
            await fetch(
                `/api/dojangs/${dojang.id}/playlists/${plId}/items?videoId=${encodeURIComponent(videoId)}`,
                {
                    method: "DELETE",
                },
            ).catch(() => null);
            await refreshPlaylists(dojang.id);
        },
        [dojang, refreshPlaylists],
    );
    // 기본/품새 재생목록 도장별 재생 순서 저장(제공+추가 통합).
    const reorderDefault = useCallback(
        async (plId: string, videoIds: string[]) => {
            if (!dojang) return;
            await fetch(`/api/dojangs/${dojang.id}/playlists/${plId}/order`, {
                method: "PUT",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ videoIds }),
            }).catch(() => null);
            await refreshPlaylists(dojang.id);
        },
        [dojang, refreshPlaylists],
    );
    // 제어 화면 재생목록 드래그 정렬 — 디바이스 세션에만 적용(REORDER_QUEUE), DB 미저장.
    // 재생목록을 다시 불러오면 저장된 순서로 돌아간다. 실패(오프라인 등)는 cmd 의 ack 토스트가 알림.
    const reorderQueue = useCallback(
        (items: string[]) => {
            cmd("REORDER_QUEUE", items);
            showToast(tp("toast.reorderDone"));
        },
        [cmd, showToast],
    );
    // 제어 화면 큐에서 영상별 반복 횟수 변경 — 저장 후 재생 중인 디바이스에 무중단 재동기화
    // 영상별 반복 — 세션 한정(재생목록에 저장 안 함). 디바이스에 명령만 보내고, 종료/새 재생 시 자동 초기화.
    const setQueueRepeat = useCallback(
        (videoId: string, count: number) => {
            cmd("SET_ITEM_REPEAT", `${videoId},${count}`);
            showToast(
                count > 1 ? tp("toast.repeatN", { count }) : tp("toast.repeatOff"),
                <I.repeat
                    size={16}
                    style={{
                        color: count > 1 ? "var(--accent)" : "var(--text-3)",
                    }}
                />,
            );
        },
        [cmd, showToast],
    );
    const renamePlaylist = (pl: PlaylistView) =>
        setModal({
            kind: "prompt",
            title: tp("dialog.renamePlaylistTitle"),
            label: tp("field.name"),
            initial: pl.name,
            placeholder: tp("ph.playlistName"),
            onSubmit: async (name) => {
                await fetch(`/api/playlists/${pl.id}`, {
                    method: "PUT",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({
                        name,
                        items: pl.items.map((videoId, position) => ({
                            videoId,
                            position,
                            repeat: pl.repeats[videoId] ?? 1,
                        })),
                    }),
                });
                if (dojang) refreshPlaylists(dojang.id);
                showToast(tp("toast.renamed"));
            },
        });
    const deletePlaylist = (pl: PlaylistView, after?: () => void) =>
        setModal({
            kind: "confirm",
            title: tp("dialog.deletePlaylistTitle"),
            message: tp("dialog.deletePlaylistMsg", { name: pl.name }),
            onConfirm: async () => {
                await fetch(`/api/playlists/${pl.id}`, { method: "DELETE" });
                if (dojang) refreshPlaylists(dojang.id);
                after?.();
                showToast(tp("toast.deleted"));
            },
        });
    // 재생목록 상세 ⋯ — '이 목록 받아두기'가 2026-08-27 여기로 들어왔다(사용자: "너무 지저분하니까").
    // 🛑 제공 목록(isDefault)엔 이름변경·삭제가 없어 예전엔 ⋯ 자체를 안 띄웠다. 지금은 받아두기 하나
    //    때문에 띄운다 — 정작 제일 많이 받아둘 게 품새 목록이라 여기가 막히면 기능이 닿을 데가 없다.
    // ⋯ 안의 '이 목록 받아두기' 한 줄 — 재생목록 상세와 품새 화면이 같은 것을 쓴다(2026-08-28).
    const offlineAction = (offline?: { items: SetItem[]; plan: SetPlan | null }): ModalAction[] => {
        const plan = offline?.plan;
        if (!plan || !offline?.items.length) return [];
        const mb = Math.max(1, Math.round(plan.setBytes / 1048576));
        return [
            plan.missing === 0
                // 이미 다 있는 경우 — 눌러도 되게 둔다(다시 누르면 '오늘 쓸 한 벌'로 표시돼 LRU 뒤로 밀린다).
                ? {
                      label: tMedia("offline.ready", { count: plan.total, mb }),
                      icon: <I.check size={19} />,
                      dim: true,
                      onClick: () => void prefetchSet(offline.items),
                  }
                : {
                      label: tMedia("offline.save", { count: plan.total, mb }),
                      icon: <I.download size={19} />,
                      // 예산을 넘는 목록이면 왜 다 안 남는지 여기서 미리 말해 준다.
                      desc: plan.fits ? undefined : tMedia("offline.tooBig", { mb, cap: Math.max(1, Math.round(plan.cap / 1048576)) }),
                      onClick: () => void prefetchSet(offline.items),
                  },
        ];
    };

    // 품새 화면(FormDrillScreen) ⋯ — 지금은 받아두기 한 줄뿐이다(이름변경·삭제는 제공 품새에 없다).
    const formMenu = (name: string, count: number, offline: { items: SetItem[]; plan: SetPlan | null }) => {
        const actions = offlineAction(offline);
        if (!actions.length) return;
        setModal({ kind: "action", title: name, subtitle: tp("misc.videosCount", { count }), actions });
    };

    const playlistMenu = (pl: PlaylistView, offline?: { items: SetItem[]; plan: SetPlan | null }) => {
        const actions: ModalAction[] = offlineAction(offline);
        if (!pl.isDefault) {
            actions.push(
                {
                    label: tp("action.rename"),
                    icon: <I.settings size={19} />,
                    onClick: () => renamePlaylist(pl),
                },
                {
                    label: tc("common.delete"),
                    icon: <I.trash size={19} />,
                    danger: true,
                    onClick: () => deletePlaylist(pl, () => setScreen("main")),
                },
            );
        }
        if (!actions.length) return; // 열어 봐야 빈 시트 — 아예 안 연다(⋯ 도 안 뜬다)
        setModal({
            kind: "action",
            title: pl.name,
            subtitle: tp("misc.videosCount", { count: pl.items.length }),
            actions,
        });
    };

    // 내 이름 등록/변경 — 가입 시 이름을 받지 않으므로 여기서 better-auth 사용자 name 을 갱신
    const renameOwner = () =>
        setModal({
            kind: "prompt",
            title: tp("dialog.renameOwnerTitle"),
            label: tp("field.name"),
            initial: owner.name,
            placeholder: tp("ph.enterName"),
            onSubmit: async (name) => {
                const { error } = await authClient.updateUser({
                    name: name.trim(),
                });
                if (error) {
                    showToast(tp("toast.renameFailed"));
                    return;
                }
                setOwner((o) => ({ ...o, name: name.trim() }));
                showToast(tp("toast.renamed"));
            },
        });
    // '영상 제목 계속 표시' — 도장 단위. 끄면(기본) 새 영상마다 잠깐, 켜면 재생 내내 작은 카드로.
    // 예전 '재생 화면에 제목 표시'(cinemaOverlay 켜기/끄기) 줄을 이걸로 바꿨다(09-21 — 꺼 둔 도장이 0곳이었다).
    // 저장은 PATCH, 켜져 있는 기기엔 SET_TITLE_STAY 로 바로 반영(동작 자막 토글과 같은 길).
    const toggleTitleStay = async () => {
        if (!dojang) return;
        const next = !(dojang.titleStay === true);
        const apply = (v: boolean) => {
            setDojang((d) => (d ? { ...d, titleStay: v } : d));
            setDojangs((ds) => ds.map((d) => (d.id === dojang.id ? { ...d, titleStay: v } : d)));
        };
        apply(next);
        try {
            const r = await fetch(`/api/dojangs/${dojang.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ titleStay: next }),
            });
            if (!r.ok) throw new Error("patch failed");
            for (const d of devices) {
                if (d.online) sockRef.current?.emit("command", { action: "SET_TITLE_STAY", deviceId: d.id, on: next } satisfies Command, () => {});
            }
        } catch {
            apply(!next); // 롤백
            showToast(tp("toast.settingsSaveFail"));
        }
    };
    // 동작 자막(학생 화면·미러) on/off — 도장 단위. 저장은 PATCH, 켜져 있는 기기엔 SET_CAPTIONS 로 바로 반영
    // (안 그러면 기기가 다음에 붙을 때까지 옛 설정으로 튼다).
    const toggleCaptions = async () => {
        if (!dojang) return;
        const next = !(dojang.captions === true);
        const apply = (v: boolean) => {
            setDojang((d) => (d ? { ...d, captions: v } : d));
            setDojangs((ds) => ds.map((d) => (d.id === dojang.id ? { ...d, captions: v } : d)));
        };
        apply(next);
        try {
            const r = await fetch(`/api/dojangs/${dojang.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ captions: next }),
            });
            if (!r.ok) throw new Error("patch failed");
            for (const d of devices) {
                if (d.online) sockRef.current?.emit("command", { action: "SET_CAPTIONS", deviceId: d.id, on: next, lang: dojang.captionLang ?? "en" } satisfies Command, () => {});
            }
        } catch {
            apply(!next); // 롤백
            showToast(tp("toast.settingsSaveFail"));
        }
    };
    // 자막 언어(영문/독일어) — 도장 단위. 저장은 PATCH, 켜져 있는 기기엔 SET_CAPTIONS(lang) 로 바로 반영.
    const setCaptionLang = async (lang: "en" | "de") => {
        if (!dojang || (dojang.captionLang ?? "en") === lang) return;
        const prev = dojang.captionLang ?? "en";
        const apply = (v: string) => {
            setDojang((d) => (d ? { ...d, captionLang: v } : d));
            setDojangs((ds) => ds.map((d) => (d.id === dojang.id ? { ...d, captionLang: v } : d)));
        };
        apply(lang);
        try {
            const r = await fetch(`/api/dojangs/${dojang.id}`, {
                method: "PATCH",
                headers: { "Content-Type": "application/json" },
                body: JSON.stringify({ captionLang: lang }),
            });
            if (!r.ok) throw new Error("patch failed");
            for (const d of devices) {
                if (d.online) sockRef.current?.emit("command", { action: "SET_CAPTIONS", deviceId: d.id, on: dojang.captions === true, lang } satisfies Command, () => {});
            }
        } catch {
            apply(prev); // 롤백
            showToast(tp("toast.settingsSaveFail"));
        }
    };

    const renameDojang = (dj: DojangView) =>
        setModal({
            kind: "prompt",
            title: tp("dialog.renameDojangTitle"),
            label: tp("field.dojangName"),
            initial: dj.name,
            placeholder: tp("ph.dojangName"),
            onSubmit: async (name) => {
                const updated = await fetch(`/api/dojangs/${dj.id}`, {
                    method: "PATCH",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ name }),
                }).then((r) => r.json());
                if (updated?.id) {
                    setDojang((d) => (d ? { ...d, name: updated.name } : d));
                    await loadMe();
                }
                showToast(tp("toast.renamed"));
            },
        });
    const deleteDojang = (dj: DojangView) =>
        setModal({
            kind: "confirm",
            title: tp("dialog.deleteDojangTitle"),
            message: tp("dialog.deleteDojangMsg", { name: dj.name }),
            onConfirm: async () => {
                const r = await fetch(`/api/dojangs/${dj.id}`, {
                    method: "DELETE",
                });
                if (r.ok) {
                    setDojang(null);
                    setScreen("dojang");
                    await loadMe();
                    showToast(tp("toast.dojangDeleted"));
                } else {
                    const e = await r.json().catch(() => ({}));
                    console.warn(
                        "dojang delete failed:",
                        e.error?.message ?? r.status,
                    );
                    showToast(
                        tp("toast.dojangDeleteFail"),
                        <I.wifiOff
                            size={16}
                            style={{ color: "var(--danger)" }}
                        />,
                    );
                }
            },
        });
    const createDojang = () =>
        setModal({
            kind: "prompt",
            title: tp("dialog.newDojangTitle"),
            label: tp("field.dojangName"),
            placeholder: tp("ph.dojangExample"),
            confirmLabel: tp("action.create"),
            onSubmit: async (name) => {
                const dj = await fetch("/api/dojangs", {
                    method: "POST",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ name }),
                }).then((r) => r.json());
                await loadMe();
                if (dj?.id)
                    enterDojang({
                        id: dj.id,
                        name: dj.name,
                        role: "OWNER",
                        deviceCount: 0,
                    });
                showToast(
                    tp("toast.dojangCreated"),
                    <I.check size={16} style={{ color: "var(--live)" }} />,
                );
            },
        });

    const pairDevice = useCallback(
        async (code: string, name: string, atHome = false, rankFormId: string | null = null, studentId: string | null = null, studentName: string | null = null, classId: string | null = null) => {
            if (!dojang) return;
            const r = await fetch(`/api/dojangs/${dojang.id}/devices/pair`, {
                method: "POST",
                headers: { "Content-Type": "application/json" },
                // atHome = 학생 집에 두는 화면. 서버가 이때 터치 잠금을 푼 채로 만든다.
                body: JSON.stringify({ code, name: name || tp("fallback.device"), atHome, rankFormId, studentId, studentName, classId }),
            });
            if (r.ok) {
                await devicesChanged(dojang.id);
                setScreen("main");
                setTab("devices");
                showToast(
                    tp("toast.paired", { name }),
                    <I.cast size={16} style={{ color: "var(--live)" }} />,
                );
            } else {
                const e = await r.json().catch(() => ({}));
                console.warn(
                    "device pair failed:",
                    e.error?.message ?? r.status,
                );
                showToast(
                    tp("toast.pairFailed"),
                    <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
                );
            }
        },
        [dojang, devicesChanged, showToast],
    );

    const logout = async () => {
        try {
            localStorage.removeItem("tedu-dojang");
        } catch {
            /* ignore */
        }
        await authClient.signOut();
        window.location.href = "/login";
    };
    // 2단계: 비밀번호 입력 시트 — 본인 확인 후 실제 탈퇴 요청.
    const askDeletePassword = () =>
        setModal({
            kind: "prompt",
            title: tp("dialog.deletePwTitle"),
            label: tp("dialog.deletePwLabel"),
            placeholder: tp("ph.password"),
            password: true,
            danger: true,
            confirmLabel: tp("dialog.deleteAccountConfirm"),
            onSubmit: async (password) => {
                const r = await fetch("/api/user", {
                    method: "DELETE",
                    headers: { "Content-Type": "application/json" },
                    body: JSON.stringify({ password }),
                });
                if (r.ok) {
                    await authClient.signOut().catch(() => {});
                    window.location.assign("/login");
                    return;
                }
                const e = await r.json().catch(() => ({}));
                const code = e.error?.code as string | undefined;
                if (
                    code === "INVALID_PASSWORD" ||
                    code === "PASSWORD_REQUIRED"
                ) {
                    showToast(
                        tp("toast.wrongPassword"),
                        <I.userX
                            size={16}
                            style={{ color: "var(--danger)" }}
                        />,
                        { label: tp("action.retry"), onClick: askDeletePassword },
                    );
                    return;
                }
                console.warn(
                    "account delete failed:",
                    e.error?.message ?? r.status,
                );
                showToast(
                    tp("toast.deleteAccountFailed"),
                    <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
                );
            },
        });
    // 1단계: 영구 삭제 경고 확인 → 비밀번호 입력.
    const deleteAccount = () =>
        setModal({
            kind: "confirm",
            title: tp("dialog.deleteAccountTitle"),
            message:
                tp("dialog.deleteAccountMsg"),
            confirmLabel: tp("dialog.continue"),
            onConfirm: askDeletePassword,
        });

    /* ===== render ===== */
    const interpPos = selId
        ? interpPosition(states[selId], now || (states[selId]?.ts ?? 0))
        : 0;

    // 제어판 — 폰(전체화면)과 가로 2단(오른쪽 패널) 양쪽에서 재사용. onClose만 다름.
    // 카메라 기능(📷 실시간 보기 / 🖥 내 카메라 전송) 노출 스위치 — 운영에는 안 나온다(lib/flags.ts).
    // 카메라 오버레이(WebRTC) — 보기(기기→폰)와 전송(폰→기기)은 한 번에 하나만. null=닫힘.
    // 상태를 하나로 들고 있어 둘이 같이 뜨는 게 애초에 불가능하다. 같이 뜨면 기기 스피커→기기 마이크
    // →폰 스피커→폰 마이크로 소리가 순환해 하울링이 나고, 전송 화면이 보기 PIP를 통째로 덮는다.
    // 보기(view)는 여러 대를 동시에 볼 수 있다(설계 §1 "여러 디바이스 동시 확인") → deviceIds 배열.
    // 전송(send)은 한 대만 — 같은 영상을 여러 화면에 뿌리는 건 다른 얘기이고, 하울링·프라이버시도 다르다.
    // 보기와 전송은 **각각 따로** 켠다 — 하나가 떠 있어도 카메라 버튼에서 나머지를 열 수 있다.
    // (예전엔 한 자리를 나눠 써서 하나를 켜면 다른 하나가 꺼졌다.)
    const [camView, setCamView] = useState<string[]>([]); // 실시간 보기 중인 기기들
    // 내 카메라를 전송 중인 기기들. 🔑 2026-08-28 **한 대 → 여러 대**. 관장이 여러 화면에 동시에
    // 시범을 보이고 싶다는 요청이었다. 카메라·창은 하나뿐이고 연결만 기기 수만큼 열린다
    // (components/presenter-cast.tsx 의 CastLink 주석). 폰 업로드가 하나라 상한은 대수로 나눠 쓴다.
    const [camSend, setCamSend] = useState<string[]>([]);
    // 전송 창은 닫았지만 **연결은 놓지 않고 붙들고 있는** 기기. 같은 기기에 보기가 떠 있을 때만 생긴다.
    // 🔑 기기는 연결을 1개만 유지한다(설계 §5) → 여기서 연결을 끊으면 보기 창이 제 연결을 처음부터
    //    다시 지어야 하고, 그동안 화면이 꺼졌다("전송 끄면 화면 꺼졌다 다시 붙는다" 신고).
    //    창만 접고 연결은 그대로 두면 그 끊김이 아예 없다.
    // 🛑 붙들고 있는 동안 폰 카메라·마이크는 **실제로 놓는다** — 전송을 껐는데 카메라가 켜져 있으면 안 된다.
    const [camHold, setCamHold] = useState<string[]>([]);
    const camConnIds = camSend.length ? camSend : camHold; // 지금 연결을 쥔 기기들(창이 보이든, 붙들고만 있든)
    const holdOnly = camSend.length === 0 && camHold.length > 0;
    // 붙들던 연결로 되돌아오기가 실패하면 이 값을 올려 전송 창을 **통째로 다시 띄운다**(key 가 바뀐다).
    // 그러면 연결·카메라를 처음부터 잡는 예전 방식이라, 최악이라도 이 기능을 넣기 전과 같아진다.
    const [castEpoch, setCastEpoch] = useState(0);

    // ── 관리 앱이 새로 열려도 카메라를 이어 붙인다(2026-09-18 사용자 결정) ───────────────────
    // 09-15 베트남 통화: 같은 폰·태블릿에서 관리 앱이 9번 새로 열렸고(새로고침 6·브라우저가 되살림 3),
    // 그때마다 보기·전송 창이 사라져 처음부터 다시 눌러야 했다. 대부분 끊긴 직후 '하던 걸 계속하려고'였다.
    // → 이 **탭**에 열려 있던 창을 적어 두고, 다시 열렸을 때 CAM_RESUME_MS 안이면 그대로 되살린다.
    //   sessionStorage 라 새로고침·브라우저가 탭을 되살린 경우엔 남고, 새 탭·다른 기기엔 없다.
    //   창을 **직접 닫으면** 목록이 비어 기록도 지워진다 → 스스로 끈 걸 되살리지 않는다.
    // 🛑 되살리는 건 보기·전송 창뿐이다. 기기 소리 듣기는 켜지 않는다(기본 꺼짐 — 기기 마이크를 저절로 켜지 않는다).
    //    붙들고만 있던 연결(camHold)도 안 되살린다 — 보기 창이 제 연결을 새로 지으면 된다.
    // 🛑 적는 시각(at)은 창이 열려 있는 동안 10초마다·숨겨질 때·떠날 때 갱신한다 — '마지막으로 살아 있던 때'부터 2분.
    const camResumeReadRef = useRef(false);
    useEffect(() => {
        if (camResumeReadRef.current) return; // 개발 모드 이중 실행에도 한 번만
        camResumeReadRef.current = true;
        try {
            const raw = sessionStorage.getItem(CAM_RESUME_KEY);
            if (!raw) return;
            const r = JSON.parse(raw) as { view?: unknown; send?: unknown; at?: unknown };
            const at = typeof r.at === "number" ? r.at : 0;
            if (!at || Date.now() - at > CAM_RESUME_MS) return;
            const ids = (a: unknown) => (Array.isArray(a) ? a.filter((x): x is string => typeof x === "string").slice(0, 16) : []);
            const view = ids(r.view);
            const send = ids(r.send);
            if (!view.length && !send.length) return;
            if (view.length) setCamView(view);
            if (send.length) setCamSend(send);
            perf("cam_resume", undefined, { view: view.length, send: send.length, ago: Math.round((Date.now() - at) / 1000) });
        } catch { /* 저장소를 못 쓰는 브라우저(사생활 보호 모드 등) — 되살리지 않을 뿐 */ }
    }, []);
    useEffect(() => {
        const open = camView.length + camSend.length > 0;
        const save = () => {
            try {
                if (open) sessionStorage.setItem(CAM_RESUME_KEY, JSON.stringify({ view: camView, send: camSend, at: Date.now() }));
                else sessionStorage.removeItem(CAM_RESUME_KEY);
            } catch { /* 무해 */ }
        };
        save();
        if (!open) return;
        const iv = setInterval(save, 10_000);
        window.addEventListener("pagehide", save);
        document.addEventListener("visibilitychange", save);
        return () => {
            clearInterval(iv);
            window.removeEventListener("pagehide", save);
            document.removeEventListener("visibilitychange", save);
        };
    }, [camView, camSend]);

    // 관리 앱 새 버전 자동 반영(2026-09-07) — 학생 화면(app/player)의 빌드 확인과 같은 원리.
    // 홈 화면에 둔 앱은 완전히 끄지 않으면 옛 JS 를 계속 들고 있어, 배포 뒤에도 "아직 그대로"가 된다
    // (사파리는 홈 화면 앱을 뒤로 보내도 페이지를 살려 둔다). 앱을 열 때·뒤에서 돌아올 때·15분마다
    // 서버 빌드 id 를 대조한다. /api/build 는 dev 에선 늘 "dev" 라 개발 중엔 아무 일도 없다.
    //  🛑 09-29 **보는 중엔 안 한다**(사용자 "바로바로 하지 말고 새벽에 쓰는 사람 없을 때 슬쩍"). 새 빌드를 알면 표시만 해 두고
    //  · 앱이 뒤로 갈 때(화면 가려짐) — 보는 사람이 없으니 그때 다시 읽는다. 다음에 열면 이미 새 판이다.
    //  · 새벽 03~05시에 켜져 있고 한가하면(기기 그리드·시트/선택/카메라 없음).
    //  · **앱을 다시 연 직후(1분)** 새 빌드를 알면 한가할 때 바로 — 밤에 배포하고 다음 날 열면 새 판(앱이 뒤에서 잠들어
    //    있던 동안엔 JS 가 멈춰 '뒤로 갈 때' 를 못 탔다). 막 연 참이라 쓰는 중이 아니다.
    //  안내 토스트는 없앴다(예전: 바쁘면 '새 버전' 안내 한 줄).
    // iOS 26.1+ 홈 화면 앱 버그(WebKit 301994, 26.5.2·27 베타까지 재현): 창이 상태줄 높이만큼 짧게 만들어져
    // (표본: 화면 844 · 창 797) 바닥 47px 이 창 밖 띠로 남는다. 띠 색은 tedu.css 의 body 바탕이 맡고, 여기선
    // 그 상태를 알아채 탭바의 홈 막대 여백(--tab-sab)을 0 으로 — 창이 이미 홈 막대 구역 밖에서 끝나 여백이 필요 없다.
    // 세로·홈 화면 앱·차이가 상태줄 크기(40~70px)일 때만. 키보드가 열리면 차이가 훨씬 커서 해당 없음. 회전·복귀마다 다시 잰다.
    useEffect(() => {
        const root = document.documentElement;
        const apply = () => {
            let standalone = false;
            try { standalone = window.matchMedia("(display-mode: standalone)").matches; } catch { /* noop */ }
            const gap = window.screen.height - window.innerHeight; // (파일의 Screen 타입과 이름이 겹쳐 window. 로)
            const portrait = window.innerHeight > window.innerWidth;
            const short = standalone && portrait && gap >= 20 && gap <= 120; // 상태줄 47(노치)·54~62(다이내믹 아일랜드)·34 변형까지
            if (short) root.style.setProperty("--tab-sab", "0px");
            else root.style.removeProperty("--tab-sab");
            root.classList.toggle("ios-short", short); // tedu.css: 알약 그림자를 창 안에 들어오게 줄인다(창 끝에서 잘려 단이 생겼다)
        };
        apply();
        window.addEventListener("resize", apply);
        window.addEventListener("orientationchange", apply);
        document.addEventListener("visibilitychange", apply);
        // 🛑 iOS 는 창을 뒤늦게 줄이면서 resize 를 안 쏘기도 한다(09-07 캡처: 감지 안 된 채 알약이 35px 떠 있었다)
        //    → visualViewport 신호 + 1.5초 주기로 다시 잰다(innerHeight 읽기뿐이라 비용 없음).
        window.visualViewport?.addEventListener("resize", apply);
        const iv = setInterval(apply, 1500);
        return () => {
            clearInterval(iv);
            window.visualViewport?.removeEventListener("resize", apply);
            window.removeEventListener("resize", apply);
            window.removeEventListener("orientationchange", apply);
            document.removeEventListener("visibilitychange", apply);
            root.style.removeProperty("--tab-sab");
            root.classList.remove("ios-short");
        };
    }, []);

    // 표준영상 접근표(lib/video-access.ts) — 미러본·표준영상은 이 표가 있어야 서버가 내준다. 로그인 세션으로 받는다.
    useEffect(() => startVideoAccessKeeper(), []);

    const buildBusy =
        screen !== "main" || sheet || !!modal || selecting || hwSelecting ||
        camView.length > 0 || camSend.length > 0 || camHold.length > 0;
    const buildBusyRef = useRef(buildBusy);
    buildBusyRef.current = buildBusy;
    useEffect(() => {
        const CHECK_MS = 15 * 60 * 1000;
        let mine: string | null = null; // 첫 확인 = 내가 딛고 선 빌드
        let stale = false; // 새 빌드를 봤지만 바빠서 미룬 상태
        let stopped = false;
        let openedAt = Date.now(); // 앱을 연(다시 보이게 된) 시각
        const reload = () => location.reload();
        const apply = () => {
            if (!stale || stopped) return;
            if (document.visibilityState === "hidden") { reload(); return; } // 앱이 뒤로 감 — 아무도 안 본다
            if (buildBusyRef.current) return;
            const hour = new Date().getHours();
            if (Date.now() - openedAt < 60 * 1000 || (hour >= 3 && hour < 5)) reload(); // 막 열었음·새벽
        };
        const check = async () => {
            try {
                const id = (await fetch("/api/build", { cache: "no-store" }).then((r) => r.json()))?.id;
                if (stopped || typeof id !== "string" || !id) return;
                if (mine === null) { mine = id; return; }
                if (id !== mine) stale = true;
                apply();
            } catch {
                // 서버가 잠깐 재시작 중일 수 있다 — 다음 기회에
            }
        };
        void check();
        const iv = setInterval(check, CHECK_MS);
        const onVis = () => { if (document.visibilityState === "visible") { openedAt = Date.now(); void check(); } else apply(); };
        document.addEventListener("visibilitychange", onVis);
        let jitter: ReturnType<typeof setTimeout> | undefined;
        buildCheckRef.current = () => { clearTimeout(jitter); jitter = setTimeout(check, 2000 + Math.random() * 8000); }; // 막 뜬 서버에 몰리지 않게 흩는다
        return () => { stopped = true; clearInterval(iv); clearTimeout(jitter); buildCheckRef.current = () => {}; document.removeEventListener("visibilitychange", onVis); };
    }, []);
    // 보고 있던 기기가 목록에서 사라지면(등록 해제 등) 타일도 정리 — 유령 타일이 남지 않게.
    const camViewDevices = camView
        .map((id) => devices.find((d) => d.id === id))
        .filter((d): d is DeviceView => !!d)
        .map((d) => ({ id: d.id, name: d.name }));
    // 전송 대상 — 목록에서 사라진 기기(등록 해제 등)는 빼고 이름을 붙인다(보기 쪽 camViewDevices 와 같은 규칙).
    const camCastDevices = camConnIds
        .map((id) => devices.find((d) => d.id === id))
        .filter((d): d is DeviceView => !!d)
        .map((d) => ({ id: d.id, name: d.name }));
    // 그리드 카드에 붙일 기기별 카메라 표식. 창 안에서만 말하면 창을 접는 순간 알 길이 없어진다.
    const camBy = useMemo(() => {
        const m: Record<string, "view" | "send" | "both"> = {};
        for (const id of camView) m[id] = "view";
        for (const id of camSend) m[id] = m[id] === "view" ? "both" : "send";
        return m;
    }, [camView, camSend]);
    const closeCamTile = (id: string) => {
        setCamView((cur) => cur.filter((x) => x !== id));
        setCamHold((cur) => cur.filter((x) => x !== id)); // 이 기기를 보려고 붙들던 연결 — 볼 사람이 없으면 놓는다
    };
    // 전송 창에서 '이 기기만 끄기'. 마지막 한 대를 빼면 창 자체가 닫힌다(빈 전송 창은 뜻이 없다).
    const closeCastOne = (id: string) => {
        setCamSend((cur) => cur.filter((x) => x !== id));
        if (camView.includes(id)) setCamHold((cur) => (cur.includes(id) ? cur : [...cur, id]));
    };

    // '수업 종료' 가 부르는 실제 함수 — 그 기기의 카메라 창을 닫는다(보기·전송 둘 다).
    // 창이 사라지면 각 컴포넌트 정리에서 media:hangup 이 나가 **기기 카메라**가 꺼지고,
    // 전송 창이 내려가며 **관장 폰 카메라**도 실제로 꺼진다(카메라 수명은 전송 창이 쥐고 있다).
    // 🛑 '정지'(STOP)에는 걸지 않는다 — 잠깐 멈춤이라 카메라는 그대로 두는 게 맞다.
    // 🛑 붙들고 있던 연결(camHold)도 같이 놓는다 — 안 놓으면 창은 없는데 연결만 남는다.
    useEffect(() => {
        closeCamsForRef.current = (ids: string[]) => {
            const gone = new Set(ids);
            setCamView((cur) => (cur.some((x) => gone.has(x)) ? cur.filter((x) => !gone.has(x)) : cur));
            setCamSend((cur) => (cur.some((x) => gone.has(x)) ? cur.filter((x) => !gone.has(x)) : cur));
            setCamHold((cur) => (cur.some((x) => gone.has(x)) ? cur.filter((x) => !gone.has(x)) : cur));
        };
        return () => {
            closeCamsForRef.current = null;
        };
    }, []);

    // ── 같은 기기에 보기와 전송이 **둘 다** 떠 있는 경우 ──────────────────────
    // 기기는 연결을 1개만 유지한다(설계 §5) → 둘이 각자 걸면 그 하나를 서로 빼앗아 둘 다 깨진다.
    // 그래서 전송 창이 연결을 쥐고 **양방향**으로 열어 기기 영상까지 같이 받고, 보기 창은 그 영상을
    // 빌려다 지금 쓰던 UI(줌·크기조절) 그대로 그린다. 서로 다른 기기면 그냥 각자 연결이라 문제없다.
    const [camBorrow, setCamBorrow] = useState<Record<string, { stream: MediaStream | null; live: boolean }>>({});
    // 전송과 보기가 **같은 기기에** 겹친 것들 — 그 연결만 양방향으로 연다.
    const bothIds = camConnIds.filter((id) => camView.includes(id));
    // 🛑 보기 창에 넘길 때는 **지금 겹쳐 있는 기기 것만** 걸러 넘긴다. camBorrow 는 한번 담기면 남아
    //    있는데, 그대로 넘기면 전송이 끝난 뒤에도 보기 타일이 '빌리는 중'으로 남아 **제 연결을 영영
    //    안 짓는다**(빈 화면 고정). 예전엔 camConn 이 null 이 되면서 자연히 끊겼던 자리다.
    const camBorrowed = Object.fromEntries(
        bothIds.filter((id) => camBorrow[id]).map((id) => [id, camBorrow[id]]),
    );

    // ── H.264(하드웨어 코덱) 사용 여부 ────────────────────────────────────
    // 2026-08-28 부터 **그냥 쓴다**(예전엔 설정 스위치 + 기본 꺼짐이었고, 아무도 안 켜니 실제 관장은
    // 계속 VP8 을 소프트웨어로 돌았다). 근거·수치는 lib/call-frame.ts 주석.
    // 🛑 막는 겹은 그대로 둘 것: **애플 기기가 한쪽이라도 끼면 안 쓴다** — 예전에 코덱을 강제했다가
    //    아이폰 송신이 두 번 깨졌다(아이폰은 스위치 없이도 이미 H264 라 잃는 것도 없다).
    // ⚠️ 비상구 하나만 남겼다: 이 폰 localStorage 에 "0" 이 있으면 안 쓴다(UI 없음).
    const [h264Off, setH264Off] = useState(false);
    // ⚠️ 마운트 때 한 번만 읽으면 안 된다 — 이 컴포넌트는 다시 마운트되지 않는다(설정은 같은 페이지 안의
    //    다른 화면이다). 카메라 창을 열 때마다 다시 읽는다.
    //    (렌더 중에 localStorage 를 읽으면 SSR 과 어긋난다 → 이펙트에서.)
    useEffect(() => { setH264Off(callH264Off()); }, [camSend.length, camView.length]);
    // 🛑 맥도 뺀다 — 맥으로 재생 화면을 띄우기도 하고, **아이패드 사파리가 UA 를 맥으로 위장**해
    //    여기로 들어온다(lib/device-platform.ts skipsCodecPreference).
    const meSkip = skipsCodecPreference(
        platformFromUA(typeof navigator === "undefined" ? null : navigator.userAgent),
    );
    // ⚠️ 이름을 use~ 로 시작하면 안 된다 — 훅으로 오해받아 lint 가 막는다(그냥 함수다).
    /** 이 기기와의 연결에서 H.264 를 고를까 — 양쪽 다 애플(·맥)이 아닐 때만. */
    const h264For = (deviceId: string | null | undefined) => {
        if (h264Off || meSkip || !deviceId) return false;
        return !skipsCodecPreference(devices.find((d) => d.id === deviceId)?.platform);
    };
    const onCamRemote = useCallback(
        (deviceId: string, stream: MediaStream | null, live: boolean) =>
            setCamBorrow((cur) => ({ ...cur, [deviceId]: { stream, live } })),
        [],
    );
    // 보기 창에서 '기기 소리 듣기'를 켠 기기들(2026-09-16). 제 연결을 가진 타일은 스스로 소리 자리를 열고,
    // 전송 창이 연결을 쥔 기기(bothIds)만 이 목록을 타고 PresenterCast 가 소리 자리를 sendrecv 로 연다.
    const [camListen, setCamListen] = useState<string[]>([]);
    const onCamListen = useCallback(
        (deviceId: string, on: boolean) =>
            setCamListen((cur) => (on ? (cur.includes(deviceId) ? cur : [...cur, deviceId]) : cur.filter((x) => x !== deviceId))),
        [],
    );

    // 카메라 시트 — 상단바 버튼 하나로 들어와 여기서 방향을 고른다. 설명을 한 줄씩 붙여
    // 처음 쓰는 사범도 무슨 차이인지(누구 카메라가 켜지는지) 바로 알 수 있게 한다.
    // 🔑 2026-08-28 **여러 대를 골랐을 때도 같은 시트를 띄운다.** 예전엔 한 대면 시트로 방향을 묻고,
    //    여러 대면 묻지도 않고 '실시간 보기'로 직행했다 — 같은 버튼이 고른 수에 따라 다르게 굴어
    //    배우기 어려웠고, 여러 대에 시범을 보이는 길이 아예 없었다.
    const cameraMenu = (picked: DeviceView[]) => {
        const many = picked.length > 1;
        const dev = picked[0];
        if (!dev) return;
        // '실시간 보기'는 **기기가 자기 카메라를 켜야** 한다 — 못 켜는 기기는 빼고 센다.
        const viewable = picked.filter(canViewCamera);
        setModal({
            kind: "action",
            // 🛑 제목은 **기기 이름**이다. 예전엔 제목이 "카메라", 부제가 기기 이름이었는데 —
            //    이 앱의 다른 시트는 전부 제목=이름·부제=상태다(기기·영상·재생목록). 여기만 거꾸로였고,
            //    바로 위 헤더에도 같은 이름이 떠 있어 **한 화면에 두 번** 나왔다("굳이 해야 하나").
            //    지우는 건 일반명사('카메라') 쪽이다 — 남길 건 어느 화면에 내 얼굴이 뜨는지다.
            //    무엇을 고르는 자리인지는 아래 두 줄('실시간 보기'·'내 카메라 전송')이 이미 말한다.
            title: many ? tp("camera.pickedN", { count: picked.length }) : dev.name,
            actions: [
                // '실시간 보기'는 기기가 자기 카메라를 켜야 한다 — 그게 안 되는 기기(카메라를 열려다 매달린
                // 적이 있는 기기)에서는 아예 내리지 않는다. 눌러봐야 12초 기다렸다 실패만 본다.
                // '내 카메라 전송'은 기기 카메라가 필요 없으므로(받기만 함) 그런 기기에도 그대로 남긴다.
                ...(viewable.length
                    ? [
                          {
                              label: tp("camera.liveView"),
                              desc: many
                                  ? tp("camera.liveViewDescN", { count: viewable.length })
                                  : tp("camera.liveViewDesc"),
                              icon: <I.video size={19} />,
                              // 자동 시연(코스③)이 눌러 여는 자리. '내 카메라 전송'은 폰 권한 팝업이
                              // 떠서 자동이 거기서 죽으므로 표식을 달지 않는다 — 그건 사람 몫.
                              tour: "cam-live",
                              // 전송이 이미 떠 있어도 그대로 얹는다 — 서로 끄지 않는다.
                              onClick: () => {
                                  // 카메라를 못 여는 기기가 섞여 있으면 조용히 빼고 나머지로 연다 — 그 한 대
                                  // 때문에 격자 전체가 12초씩 붙들리지 않도록. 다만 빠졌다는 건 알린다.
                                  // 🔑 **어느 기기가 빠졌는지 이름을 말한다**(2026-08-31). 예전엔 "N대는 뺐어요"
                                  //    뿐이라, 시연용 기기가 섞여 뜨는 정상 상황인지 진짜 내 기기가 빠진 건지
                                  //    구분할 수가 없었다(실제로 그 문구를 보고 고장인 줄 알았다는 신고).
                                  //    이름이 길어질 수 있으니 둘까지만 대고 나머지는 수로 줄인다.
                                  const dropped = picked.filter((d) => !canViewCamera(d));
                                  if (dropped.length) {
                                      const names = dropped.slice(0, 2).map((d) => d.name).join(", ");
                                      const rest = dropped.length - 2;
                                      showToast(
                                          rest > 0
                                              ? tp("toast.droppedNoCameraMore", { names, rest })
                                              : tp("toast.droppedNoCamera", { names }),
                                      );
                                  }
                                  const ids = viewable.map((d) => d.id);
                                  setCamView((cur) => [...cur, ...ids.filter((x) => !cur.includes(x))]);
                                  exitSelecting();
                              },
                          },
                      ]
                    : []),
                {
                    label: tp("camera.sendMine"),
                    icon: <I.monitor size={19} />,
                    // 자동 시연(코스③ 마지막)이 눌러 여는 자리. ⚠️ 폰 카메라라 **처음 한 번은**
                    // 브라우저 권한 팝업이 뜬다 → 부스 폰에서 미리 한 번 허용해 둘 것.
                    tour: "cam-send",
                    desc: many ? tp("camera.sendMineDescN", { count: picked.length }) : tp("camera.sendMineDesc"),
                    // 붙들고만 있던 기기면 그 연결을 그대로 다시 쓴다(새로 짓지 않는다).
                    onClick: () => {
                        setCamSend(picked.map((d) => d.id));
                        setCamHold([]);
                        exitSelecting();
                    },
                },
            ],
        });
    };

    // 여러 기기 동시 보기 — 그리드에서 고른 기기들(온라인만)을 한 창에 격자로 띄운다(설계 §2 스타 구조:
    // 기기 수만큼 독립 연결이 열린다). 오프라인 기기는 어차피 카메라를 켤 수 없으니 제외.
    // 그리드에서 여러 대를 고르고 카메라 버튼을 눌렀을 때 — 한 대 때와 **같은 시트**를 띄운다.
    // (예전엔 여기서 곧장 '실시간 보기'를 열었다. 위 cameraMenu 주석 참고.)
    const cameraForSelected = () => {
        const picked = devices.filter((d) => selIds.has(d.id) && d.online);
        if (!picked.length) return showToast(tp("toast.pickOnDevice"));
        cameraMenu(picked);
    };

    const renderControl = (onClose: () => void): ReactNode =>
        selDevice ? (
            <>
                <ControlScreen
                    allowBig={!!owner.appFullscreen}
                    captions={dojang?.captions === true}
                    captionLang={dojang?.captionLang}
                    device={selDevice}
                    wide={isWide}
                    position={interpPos}
                    sessionSec={sessionSecFor(selId) ?? undefined}
                    // 응원을 보낸 그 기기의 조종판에서만 — 발사 직후 다른 기기로 넘어가도 따라가지 않는다.
                    cheer={myCheer && myCheer.deviceId === selDevice.id ? myCheer : null}
                    onCheerDone={() => setMyCheer(null)}
                    onCmd={cmd}
                    onClose={onClose}
                    onToast={showToast}
                    onLoadSheet={() => setSheet(true)}
                    onMenu={() => deviceMenu(selDevice)}
                    onReorder={reorderQueue}
                    onSetRepeat={setQueueRepeat}
                    onCreate={() => createPlaylist("control")}
                    onAddVideos={() => {
                        // 제어 화면 위 오버레이로 '영상 추가' — 상세 화면으로 넘어가지 않고,
                        // 고른 영상을 현재 재생 중인 큐에 바로 추가 + 무중단 SYNC. 닫으면 제어 화면 그대로.
                        const plId = selDevice.playlistId;
                        const cur = plId ? plById[plId] : null;
                        if (plId && cur) setAddQueue({ plId, items: cur.items.slice() });
                    }}
                    recentCasts={recentCasts}
                    onPickRecent={(r) => replayRecent(r, false)}
                    onPickPlay={(plId) => {
                        cmd("LOAD_PLAYLIST", plId);
                        pushRecent({
                            kind: "playlist",
                            id: plId,
                            label: plById[plId]?.name ?? tp("fallback.playlist"),
                        });
                    }}
                    onPlayAt={(plId, index) => {
                        cmd("LOAD_PLAYLIST_AT", `${plId},${index},1`);
                        pushRecent({
                            kind: "playlist",
                            id: plId,
                            label: plById[plId]?.name ?? tp("fallback.playlist"),
                        });
                    }}
                    onLaunchPlayerApp={() => {
                        void launchPlayerApp(selDevice);
                    }}
                    remoteLaunchable={
                        remoteLaunchCopy(
                            selDevice.platform,
                            selDevice.hasPushToken,
                            selDevice.fromStore,
                        ).canLaunch
                    }
                    remoteLaunchNote={(() => {
                        const k = remoteLaunchCopy(
                            selDevice.platform,
                            selDevice.hasPushToken,
                            selDevice.fromStore,
                        ).noteKey;
                        return k ? tScreen(k) : undefined;
                    })()}
                    onCamera={
                        owner.camera ? () => cameraMenu([selDevice]) : undefined
                    }
                    masterForms={masterForms}
                    onPlayMaster={(v) => {
                        loadMaster(v, false);
                        pushRecent({
                            kind: "master",
                            video: v,
                            label: v.title,
                        });
                    }}
                    onPlaySetMaster={(plId) => {
                        playMasterSet(plId, false);
                        pushRecent({
                            kind: "playlist",
                            id: plId,
                            label: plById[plId]?.name ?? tp("fallback.set"),
                        });
                    }}
                    onPlayMachiSetMaster={(videos, title, index, practice) => {
                        loadMasterSet(videos, title, index, false, practice);
                        pushRecent({
                            kind: "masterset",
                            videos,
                            title,
                            index: index ?? 0,
                            label:
                                (index ?? 0) > 0
                                    ? `${title} (${(index ?? 0) + 1}~)`
                                    : title,
                        });
                    }}
                />
                {/* 카메라 창(보기·전송)은 둘 다 페이지 레벨에서 그린다(아래) — 기기를 바꿔가며 조작해도,
                    그리드로 나갔다 와도 창이 유지돼야 한다. 전송창이 여기 있던 시절엔 그리드로 나가는
                    순간 언마운트되며 통화가 끊겼다(보기와 같이 쓰면 보기 쪽이 멈춰 버린다). */}
            </>
        ) : null;

    let content: ReactNode = null;

    if (isPending || !session || (bootRestoring && screen === "dojang")) {
        content = (
            <div
                className="col"
                style={{
                    height: "100%",
                    alignItems: "center",
                    justifyContent: "center",
                }}
            >
                <div className="spin" style={{ color: "var(--accent)" }}>
                    <I.spinner size={36} sw={2} />
                </div>
            </div>
        );
    } else if (screen === "dojang") {
        content = (
            <DojangScreen
                dojangs={dojangs}
                owner={owner}
                onPick={enterDojang}
                onLogout={logout}
                onCreate={createDojang}
            />
        );
    } else if (!isWide && screen === "control" && selDevice) {
        content = renderControl(() => setScreen("main"));
    } else if (screen === "upload" && dojang) {
        content = (
            <UploadScreen
                dojangId={dojang.id}
                onToast={showToast}
                onActive={setActiveUpload}
                returnLabel={uploadReturnPl ? tp("misc.backToPlaylist") : undefined}
                onUploaded={async (newVideoId) => {
                    await refreshVideos(dojang.id);
                    // 재생목록에서 업로드한 경우 — 새 영상을 그 목록 끝에 자동 추가
                    if (uploadReturnPl && newVideoId) {
                        const pl = plById[uploadReturnPl];
                        if (pl && !pl.items.includes(newVideoId))
                            await persistPlaylist(uploadReturnPl, [
                                ...pl.items,
                                newVideoId,
                            ]);
                    }
                }}
                onClose={closeUpload}
            />
        );
    } else if (screen === "pairing") {
        content = (
            <PairingScreen
                // 도장 화면 등록만 — 홈 트레이닝 기기는 수련생 링크로만 등록한다(10-29).
                onClose={() => setScreen("main")}
                onToast={showToast}
                onPaired={pairDevice}
            />
        );
    } else if (screen === "homeLink" && dojang) {
        content = (
            <HomeLinkScreen
                onClose={() => setScreen("main")}
                onToast={showToast}
                onCreate={createStudentLink}
                classes={classes}
                defaultClassId={pairClassId}
                onAddClass={addClassInline}
                dojangName={dojang.name}
                forms={masterForms.filter((f) => f.category !== "기본동작").map((f) => ({ id: f.id, name: f.name }))}
            />
        );
    } else if (screen === "homeDevices" && dojang) {
        content = (
            <HomeDevicesScreen
                devices={devices}
                onClose={() => setScreen("main")}
                onOpenDevice={homeDeviceManageMenu}
                onPair={() => { setPairClassId(null); setScreen("homeLink"); }}
                classCount={classes.length}
                onOpenClasses={() => { setClassesFrom("homeDevices"); setScreen("classes"); }}
            />
        );
    } else if (screen === "classes" && dojang) {
        content = (
            <ClassesScreen
                classes={classes}
                countOf={classCount}
                onClose={() => setScreen(classesFrom)}
                onAdd={addClass}
                onOpen={classMenu}
            />
        );
    } else if (screen === "training" && dojang) {
        content = (
            <TrainingHistoryScreen
                dojangId={dojang.id}
                onClose={() => setScreen("main")}
            />
        );
    } else if (screen === "members" && dojang && canManage) {
        content = (
            <MembersScreen
                dojangId={dojang.id}
                onClose={() => setScreen("main")}
                onToast={showToast}
                onSendMessage={sendStaffMessage}
            />
        );
    } else if (screen === "account" && dojang) {
        content = (
            <AccountScreen
                owner={owner}
                dojang={dojang}
                onClose={() => setScreen("main")}
                onRenameOwner={renameOwner}
                onEditPhone={() => setModal({ kind: "phone", initial: owner.phone })}
                onLogout={logout}
                onDeleteAccount={deleteAccount}
            />
        );
    } else if (screen === "formdrill" && drillForm) {
        content = (
            <FormDrillScreen
                form={drillForm}
                onBack={() => setScreen("main")}
                onPlay={previewMaster}
                onPlaySet={loadToDevice}
                onPlayMachiSet={previewMasterSet}
                onMenu={(offline) => formMenu(tkdForm(drillForm.name, locale), drillForm.videos.length, offline)}
                onToggleHidden={
                    drillForm.playlistId
                        ? (videoId, hidden) => {
                              toggleHiddenDefault(
                                  drillForm.playlistId!,
                                  videoId,
                                  hidden,
                              );
                              showToast(
                                  hidden
                                      ? tp("toast.hiddenFromPlay")
                                      : tp("toast.backInPlay"),
                                  hidden ? (
                                      <I.eyeOff
                                          size={16}
                                          style={{ color: "var(--accent)" }}
                                      />
                                  ) : (
                                      <I.eye
                                          size={16}
                                          style={{ color: "var(--text-3)" }}
                                      />
                                  ),
                              );
                          }
                        : undefined
                }
                onAddDefault={
                    drillForm.playlistId
                        ? async (videoIds) => {
                              const ok = await addToDefault(
                                  drillForm.playlistId!,
                                  videoIds,
                              );
                              showToast(
                                  ok ? tp("toast.addedMyVideos", { count: videoIds.length }) : tp("toast.addFailed"),
                                  ok ? (
                                      <I.check
                                          size={16}
                                          style={{ color: "var(--live)" }}
                                      />
                                  ) : (
                                      <I.wifiOff
                                          size={16}
                                          style={{ color: "var(--danger)" }}
                                      />
                                  ),
                              );
                          }
                        : undefined
                }
                onRemoveDefault={
                    drillForm.playlistId
                        ? (videoId) => {
                              removeFromDefault(drillForm.playlistId!, videoId);
                              showToast(tp("toast.videoRemoved"));
                          }
                        : undefined
                }
                onReorderDefault={
                    drillForm.playlistId
                        ? (videoIds) =>
                              reorderDefault(drillForm.playlistId!, videoIds)
                        : undefined
                }
            />
        );
    } else if (screen === "pldetail" && selPl) {
        content = (
            <PlaylistDetail
                key={selPl.id}
                playlist={selPl}
                startPicking={plAutoPick}
                onClose={closePlDetail}
                onMenu={(offline) => playlistMenu(selPl, offline)}
                onPersist={async (items) => {
                    const ok = await persistPlaylist(selPl.id, items);
                    if (!ok)
                        persistFailToast(tp("toast.saveFailedRetry"));
                    return ok;
                }}
                onToggleHidden={(videoId, hidden) => {
                    toggleHiddenDefault(selPl.id, videoId, hidden);
                    showToast(
                        hidden ? tp("toast.hiddenFromPlay") : tp("toast.backInPlay"),
                        hidden ? (
                            <I.eyeOff
                                size={16}
                                style={{ color: "var(--accent)" }}
                            />
                        ) : (
                            <I.eye
                                size={16}
                                style={{ color: "var(--text-3)" }}
                            />
                        ),
                    );
                }}
                onAddDefault={async (videoIds) => {
                    const ok = await addToDefault(selPl.id, videoIds);
                    showToast(
                        ok ? tp("toast.addedMyVideos", { count: videoIds.length }) : tp("toast.addFailed"),
                        ok ? (
                            <I.check
                                size={16}
                                style={{ color: "var(--live)" }}
                            />
                        ) : (
                            <I.wifiOff
                                size={16}
                                style={{ color: "var(--danger)" }}
                            />
                        ),
                    );
                }}
                onRemoveDefault={(videoId) => {
                    removeFromDefault(selPl.id, videoId);
                    showToast(tp("toast.videoRemoved"));
                }}
                onReorderDefault={(videoIds) =>
                    reorderDefault(selPl.id, videoIds)
                }
                onLoadToDevice={() => {
                    // 기기에서 진입한 흐름이면 기기 선택 팝업 없이 그 기기로 바로 반영 + 제어화면 복귀 (뒤로가기와 동일 동작을 명시 CTA로).
                    if (plReturn === "control" && selDevice) {
                        if (selPl.items.length > 0)
                            cmd(
                                selDevice.playlistId === selPl.id
                                    ? "SYNC_PLAYLIST"
                                    : "LOAD_PLAYLIST",
                                selPl.id,
                            );
                        setScreen("control");
                        setPlReturn("main");
                    } else loadToDevice(selPl.id);
                }}
                onUpload={() => {
                    setUploadReturnPl(selPl.id);
                    setScreen("upload");
                }}
                onPlayItem={(ids, i) =>
                    setLocalPlay({
                        ids,
                        idx: i,
                        title: selPl.name,
                        onSend: () => {
                            setLocalPlay(null);
                            loadToDevice(selPl.id);
                        },
                    })
                }
                returnDeviceName={
                    plReturn === "control" ? (selDevice?.name ?? null) : null
                }
            />
        );
    } else if (
        isWide &&
        dojang &&
        tab === "devices" &&
        (screen === "main" || screen === "control")
    ) {
        // 가로 2단 — 왼쪽: 기기 멀티뷰 그리드 + 탭바 / 오른쪽: 선택 기기 제어판
        content = (
            <div className="tedu-panes">
                <aside className="tedu-rail tabhost">
                    <div
                        style={{
                            flex: 1,
                            minHeight: 0,
                            display: "flex",
                            flexDirection: "column",
                        }}
                    >
                        <DashboardScreen
                            dojang={dojang}
                            devices={devices}
                            loading={!devicesLoaded}
                            now={now}
                            onOpen={(d) => withTakeover(d, () => openDevice(d))}
                            onToggle={(d) => withTakeover(d, () => toggleDevice(d))}
                            onLoadDevice={(d) =>
                                withTakeover(d, () => {
                                    setSelId(d.id);
                                    setSheet(true);
                                })
                            }
                            onPair={() => setScreen("pairing")}
                            onSwitchDojang={() => setScreen("dojang")}
                            onEndAll={endAllClasses}
                            onPauseAll={pauseAllClasses}
                            onVoice={owner.voice ? () => setVoiceOpen(true) : undefined}
                            onResumeAll={resumeAllClasses}
                            selecting={selecting}
                            selIds={selIds}
                            onExitSelecting={exitSelecting}
                            onSelectDevice={toggleSelectDevice}
                            onLongPress={longPressDevice}
                            onSelectAll={selectAllOnline}
                            onBatch={onBatch}
                            onBatchView={owner.camera ? cameraForSelected : undefined}
                            camBy={camBy}
                            onBatchLoad={() => setBatchSheet(true)}
                            controlBy={canManage ? controlBy : undefined}
                            onLowerHand={lowerHand}
                        />
                    </div>
                    {!selecting && !hwSelecting && <TabBar tab={tab} onTab={setTab} showHomework={!!owner.homeDevice} />}
                </aside>
                <main className="tedu-detail">
                    {selDevice ? (
                        renderControl(() => setSelId(null))
                    ) : (
                        <EmptyDetail />
                    )}
                </main>
            </div>
        );
    } else if (dojang) {
        let tabView: ReactNode;
        if (tab === "devices")
            tabView = (
                <DashboardScreen
                    dojang={dojang}
                    devices={devices}
                    loading={!devicesLoaded}
                    now={now}
                    onOpen={(d) => withTakeover(d, () => openDevice(d))}
                    onToggle={(d) => withTakeover(d, () => toggleDevice(d))}
                    onLoadDevice={(d) =>
                        withTakeover(d, () => {
                            setSelId(d.id);
                            setSheet(true);
                        })
                    }
                    onPair={() => setScreen("pairing")}
                    onSwitchDojang={() => setScreen("dojang")}
                    onEndAll={endAllClasses}
                    onPauseAll={pauseAllClasses}
                    onVoice={owner.voice ? () => setVoiceOpen(true) : undefined}
                    onResumeAll={resumeAllClasses}
                    selecting={selecting}
                    selIds={selIds}
                    onExitSelecting={exitSelecting}
                    onSelectDevice={toggleSelectDevice}
                    onLongPress={longPressDevice}
                    onSelectAll={selectAllOnline}
                    onBatch={onBatch}
                    // 폰 세로에도 넘긴다 — 여러 기기 카메라를 '폰 한 화면에서' 보는 게 이 기능의 본래 목적인데,
                    // 태블릿 2단 분기에만 넘어가 있어 정작 폰에선 버튼이 안 떴다(2026-07-14 수정).
                    onBatchView={owner.camera ? cameraForSelected : undefined}
                    camBy={camBy}
                    onBatchLoad={() => setBatchSheet(true)}
                    controlBy={canManage ? controlBy : undefined}
                    onLowerHand={lowerHand}
                />
            );
        else if (tab === "library")
            tabView = (
                <LibraryScreen
                    onOpen={(pl) => {
                        setSelPlId(pl.id);
                        setPlReturn("main");
                        setPlAutoPick(false);
                        setScreen("pldetail");
                    }}
                    onOpenForm={(f) => {
                        setDrillForm(f);
                        setScreen("formdrill");
                    }}
                    onCreate={() => createPlaylist("main")}
                    onDelete={(pl) => deletePlaylist(pl)}
                    onUpload={() => {
                        setUploadReturnPl(null);
                        setScreen("upload");
                    }}
                    onMenu={videoMenu}
                    onPlay={(ids, i, t) =>
                        setLocalPlay({
                            ids,
                            idx: i,
                            title: t,
                            onAddToPlaylist: (index) =>
                                setAddToPlVideo(ids[index]),
                        })
                    }
                    uploading={activeUpload}
                    masterForms={masterForms}
                    backRef={libBackRef}
                    seg={libSeg}
                    onSeg={setLibSeg}
                    group={libGroup}
                    onGroup={setLibGroup}
                />
            );
        else if (tab === "homework" && owner.homeDevice)
            tabView = (
                <HomeworkScreen
                    devices={devices}
                    timeZone={dojang.timeZone}
                    onAssign={homeworkMenu}
                    onOpen={deviceMenu}
                    // 홈 트레이닝 등록은 링크 한 길(10-29 사용자 결정 — 6자리 코드 방식·설정 토글 폐지).
                    onPair={(classId) => { setPairClassId(classId); setScreen("homeLink"); }}
                    classes={classes}
                    onMoveClass={moveClassMenu}
                    onOpenClasses={() => { setClassesFrom("main"); setScreen("classes"); }}
                    linkStudents={students}
                    onOpenLinkStudent={linkMenu}
                    onSelecting={setHwSelecting}
                />
            );
        else
            tabView = (
                <SettingsScreen
                    dojang={dojang}
                    owner={owner}
                    theme={theme}
                    onToggleTheme={toggleTheme}
                    onToggleTitleStay={toggleTitleStay}
                    onToggleCaptions={toggleCaptions}
                    onCaptionLang={setCaptionLang}
                    onSwitchDojang={() => setScreen("dojang")}
                    onRenameDojang={() => renameDojang(dojang)}
                    onDeleteDojang={() => deleteDojang(dojang)}
                    onOpenTraining={() => setScreen("training")}
                    onOpenHomeDevices={owner.homeDevice ? () => setScreen("homeDevices") : undefined}
                    onOpenMembers={() => setScreen("members")}
                    onGoHome={() => { window.location.href = "/"; }}
                    onOpenAccount={() => setScreen("account")}
                    // 사용법 둘러보기 — 2026-08-27 전체 공개(lib/flags tourAllowed 기본 "*").
                    // 킬스위치(TOUR_EMAILS)로 다시 좁히면 아예 안 넘겨 설정 행조차 안 뜬다.
                    onStartTour={
                        owner.tour
                            ? () => {
                                  // 첫 칸이 기기 카드라 기기 탭에서 출발시킨다 — 설정 탭에 선 채로 열면
                                  // 첫 칸 대상이 화면 밖이라 안내가 허공을 가리킨다.
                                  setTab("devices");
                                  setTourOn(true);
                              }
                            : undefined
                    }
                    // 자동 시연 코스 — 🛑 둘러보기와 **다른 플래그**(demoAllowed). 부스 전용으로 남긴다:
                    // 실제 관장 폰에서 눌리면 앱이 혼자 화면들을 조작한다(수업 중이면 사고).
                    // 기기가 모자라면 시작을 막는다(볼 게 없다).
                    demoCourses={owner.demo ? DEMO_COURSES : undefined}
                    onStartDemo={(c) => {
                        const onlineN = devices.filter((d) => d.online).length;
                        if (onlineN < c.needDevices) {
                            showToast(
                                `이 시연은 켜져 있는 화면이 ${c.needDevices}대 필요해요 (지금 ${onlineN}대)`,
                                <I.wifiOff size={16} style={{ color: "var(--danger)" }} />,
                            );
                            return;
                        }
                        startDemoCourse(c);
                    }}
                />
            );
        content = (
            <div className="col tabhost" style={{ height: "100%" }}>
                <div
                    style={{
                        flex: 1,
                        minHeight: 0,
                        display: "flex",
                        flexDirection: "column",
                    }}
                >
                    {tabView}
                </div>
                {!selecting && !hwSelecting && <TabBar tab={tab} onTab={setTab} showHomework={!!owner.homeDevice} />}
            </div>
        );
    } else {
        content = (
            <DojangScreen
                dojangs={dojangs}
                owner={owner}
                onPick={enterDojang}
                onLogout={logout}
                onCreate={createDojang}
            />
        );
    }

    return (
        <div className={`tedu-root${theme === "light" ? " theme-light" : ""}`}>
            {/* 사용법 둘러보기 — 화면 맨 위에 뜨는 안내막. 어떤 화면에 있든 따라다녀야 해서 루트에 둔다. */}
            {/* 마무리 인사는 말풍선이 아니라 따로 띄운다 — 마지막 칸에서 영상을 갈아타면 안내막이
                사라지는데, 그 한마디를 말풍선에 같이 적어 두면 정작 영상이 바뀌는 순간 통째로 없어져
                못 읽는다. 끝까지 간 경우에만(onDone) 뜬다 — '그만보기'로 나간 사람에겐 안 띄운다. */}
            <TourOverlay steps={CONTROL_TOUR} open={tourOn && !!owner.tour} onClose={() => setTourOn(false)}
                onDone={() => setTourEnd(true)} />
            {/* 자동 시연 — 같은 엔진에 auto 만 켠 것이다(칸 목록은 app/control/demo-courses.ts).
                🛑 끝나면 반드시 원상복구한다. 부스는 하루 종일 돌고 다음 사람이 곧바로 잡는데,
                   0.75배로 느려지고 구간반복이 걸린 채 남아 있으면 그게 앱의 첫인상이 된다.
                   '그만보기'로 중간에 나가도 마찬가지라 onClose 쪽에 둔다(onDone 아님). */}
            {demo && (
                <TourOverlay
                    steps={demo.steps}
                    open
                    auto
                    // 🛑 사람이 '그만보기'로 나가면 남은 코스도 버린다. 자동으로 끝난 경우에는
                    //    바로 아래 onDone 이 먼저 돌아 demoChaining 을 세워 두므로 큐가 살아남는다
                    //    (TourOverlay 는 마지막 칸에서 onDone → onClose 순으로 둘 다 부른다).
                    onClose={() => {
                        // 사람이 '그만보기'를 눌렀으면 되풀이도 '화면 돌아오면 다시'도 전부 없앤다.
                        if (!demoChaining.current) {
                            demoQueue.current = [];
                            demoLoopChain.current = null;
                            demoLoopCourse.current = null;
                            demoResumeAt.current = null;
                        }
                        stopDemo();
                    }}
                    onDone={() => {
                        let next = demoQueue.current.shift();
                        // ⭐ '계속 반복'이면 한 바퀴가 끝난 자리에서 큐를 다시 세우고 첫 코스로 돌아간다
                        //    (2026-08-19 사용자: 부스에 그냥 틀어 두려고). 마무리 한마디는 띄우지 않는다 —
                        //    매 바퀴 "여기까지…"가 뜨면 끝난 줄 알고 사람이 앱을 놓는다.
                        if (!next && demoLoopChain.current?.length) {
                            const lap = demoLoopChain.current;
                            demoQueue.current = lap.slice(1);
                            next = lap[0];
                        }
                        // 마지막 코스까지 끝났으면 평소대로 마무리 한마디.
                        if (!next) { setTourEnd(true); return; }
                        // ⚠️ 곧바로 이어 붙이지 않는다 — stopDemo 가 배속·구간반복을 되돌리고
                        //    화면이 정리될 참을 준다. 이 뒤 beginCourse 가 다시 0.4초를 더 쉰다.
                        demoChaining.current = true;
                        setTimeout(() => { demoChaining.current = false; beginCourse(next); }, 900);
                    }}
                />
            )}
            {tourEnd && (
                <div
                    style={{
                        // 화면 한가운데 — 위쪽에 뒀더니 상단바에 묻혀 눈에 안 들어왔다(2026-08-06).
                        position: "fixed", inset: 0, zIndex: 10050,
                        display: "flex", alignItems: "center", justifyContent: "center", pointerEvents: "none",
                        animation: "etd-tourend .28s ease-out both",
                    }}
                    role="status"
                >
                    <style>{`@keyframes etd-tourend{from{opacity:0;transform:scale(.94)}to{opacity:1;transform:none}}`}</style>
                    <div style={{
                        padding: "18px 30px", borderRadius: 999, maxWidth: "calc(100% - 32px)",
                        background: "var(--surface-1)", border: "1px solid var(--border)", boxShadow: "var(--shadow-pop)",
                        fontSize: 21, fontWeight: 800, letterSpacing: "-.025em", color: "var(--text-1)", textAlign: "center",
                    }}>
                        {tc("tour.done")}
                    </div>
                </div>
            )}
            <DataProvider
                value={{ videos, playlists, byId: byIdAug, plById: plByIdAug, breakdown, techMoves, sbs, practice: !!owner.practice, lead: !!owner.lead }}
            >
                <div className="tedu-app">
                    <div
                        style={{ flex: 1, minHeight: 0, position: "relative" }}
                    >
                        {content}
                        {localPlay && (
                            <LocalPlayer
                                videoIds={localPlay.ids}
                                videos={localPlay.videos}
                                startIndex={localPlay.idx}
                                title={tkdLabel(localPlay.title, locale)}
                                onSend={localPlay.onSend}
                                onAddToPlaylist={localPlay.onAddToPlaylist}
                                allowBig={!!owner.appFullscreen}
                                onClose={() => setLocalPlay(null)}
                            />
                        )}
                        {/* 숙제 내기 — '무엇을 틀까요?' 시트 그대로(title·currentId·clearRow 만 다르다) */}
                        {hwPickDevs && (
                            <LoadSheet
                                title={tp("dialog.homeworkTitle")}
                                headerSub={
                                    hwPickDevs.length > 1
                                        ? tp("dialog.homeworkSubN", { count: hwPickDevs.length })
                                        : hwPickDevs[0].name
                                }
                                // 모두 같은 숙제일 때만 체크 — 다르면 어느 것도 '지금 것'이 아니다(""=아무 줄에도 안 붙음).
                                currentId={
                                    hwPickDevs.every((d) => d.homePlaylistId === hwPickDevs[0].homePlaylistId)
                                        ? (hwPickDevs[0].homePlaylistId ?? null)
                                        : ""
                                }
                                status={(() => {
                                    const same = hwPickDevs.every((d) => d.homePlaylistId === hwPickDevs[0].homePlaylistId);
                                    const cur = same ? (hwPickDevs[0].homePlaylistId ?? null) : "";
                                    const text = !same
                                        ? tp("dialog.homeworkNowMixed")
                                        : cur == null
                                          ? tp("dialog.homeworkNowNone")
                                          : tp("dialog.homeworkNow", { name: plById[cur]?.name ?? tp("fallback.playlist") });
                                    // 지울 게 있을 때만 버튼(없는데 '지우기'가 있으면 뭘 지우나 싶다). 학생마다 다를 때도 한 번에 없음으로.
                                    const clearable = !same || cur != null;
                                    return {
                                        text,
                                        clearLabel: clearable ? tp("action.homeworkClear") : undefined,
                                        onClear: clearable ? () => { const devs = hwPickDevs; setHwPickDevs(null); void setHomework(devs, null); } : undefined,
                                    };
                                })()}
                                onPick={(id) => { const devs = hwPickDevs; setHwPickDevs(null); void setHomework(devs, id); }}
                                onClose={() => setHwPickDevs(null)}
                                onCreate={() => { setHwPickDevs(null); createPlaylist("main"); }}
                            />
                        )}
                        {/* 자습 범위 — 같은 얼개(RangeSheet) */}
                        {rankPickDev && (
                            <RangeSheet
                                studentName={rankPickDev.name}
                                forms={masterForms}
                                current={rankPickDev.rankFormId ?? null}
                                onPick={(id) => { const dev = rankPickDev; setRankPickDev(null); void setRankForm(dev, id); }}
                                onClose={() => setRankPickDev(null)}
                            />
                        )}
                        {/* 재생목록에 담기 시트 — LocalPlayer(z:45) 위에 떠야 하므로 z:60 래퍼로 감쌈 */}
                        {addToPlVideo && (
                            <div
                                style={{
                                    position: "absolute",
                                    inset: 0,
                                    zIndex: 60,
                                }}
                            >
                                <PlaylistPickerSheet
                                    videoId={addToPlVideo}
                                    onClose={() => setAddToPlVideo(null)}
                                    onAdd={async (plId) => {
                                        const vidId = addToPlVideo;
                                        const pl = playlists.find(
                                            (p) => p.id === plId,
                                        );
                                        if (!pl || !vidId) return;
                                        setAddToPlVideo(null);
                                        // 기본/품새(제공) 재생목록은 도장별 오버라이드로 덧붙임; 내 재생목록은 기존 PUT.
                                        const ok = pl.isDefault
                                            ? await addToDefault(plId, [vidId])
                                            : await persistPlaylist(plId, [
                                                  ...pl.items,
                                                  vidId,
                                              ]);
                                        showToast(
                                            ok
                                                ? tp("toast.addedTo", { name: pl.name })
                                                : tp("toast.addFailed"),
                                            ok ? (
                                                <I.check
                                                    size={16}
                                                    style={{
                                                        color: "var(--live)",
                                                    }}
                                                />
                                            ) : (
                                                <I.wifiOff
                                                    size={16}
                                                    style={{
                                                        color: "var(--danger)",
                                                    }}
                                                />
                                            ),
                                        );
                                    }}
                                    onCreateAndAdd={async (name) => {
                                        const vidId = addToPlVideo;
                                        if (!dojang || !vidId) return;
                                        setAddToPlVideo(null);
                                        const pl: PlaylistRaw | null =
                                            await fetch(
                                                `/api/dojangs/${dojang.id}/playlists`,
                                                {
                                                    method: "POST",
                                                    headers: {
                                                        "Content-Type":
                                                            "application/json",
                                                    },
                                                    body: JSON.stringify({
                                                        name,
                                                        items: [],
                                                    }),
                                                },
                                            )
                                                .then((r) => r.json())
                                                .catch(() => null);
                                        if (pl?.id)
                                            await persistPlaylist(pl.id, [
                                                vidId,
                                            ]);
                                        else if (dojang)
                                            await refreshPlaylists(dojang.id);
                                        showToast(
                                            pl?.id
                                                ? tp("toast.addedTo", { name })
                                                : tp("toast.createFailed"),
                                            pl?.id ? (
                                                <I.check
                                                    size={16}
                                                    style={{
                                                        color: "var(--live)",
                                                    }}
                                                />
                                            ) : (
                                                <I.wifiOff
                                                    size={16}
                                                    style={{
                                                        color: "var(--danger)",
                                                    }}
                                                />
                                            ),
                                        );
                                    }}
                                />
                            </div>
                        )}
                        {/* 제어 화면 '영상 추가' 오버레이 — 현재 큐(재생 중 재생목록)에 바로 추가 + 무중단 SYNC.
                            LocalPlayer(z:45) 위에 떠야 하므로 z:60 래퍼. 닫아도 제어 화면 유지(상세로 안 넘어감). */}
                        {addQueue && selDevice && plById[addQueue.plId] && (() => {
                            const cur = plById[addQueue.plId]!;
                            const isDef = cur.isDefault;
                            const draft = addQueue.items;
                            // 품·낱기술(breakdown)은 이 시트에서만 담을 수 있다 — 내 재생목록일 때만(기본 목록엔 제공 영상 못 덧붙임).
                            const avail = [...videos, ...(isDef ? [] : breakdown)].filter(
                                (v) =>
                                    !draft.includes(v.id) &&
                                    (!isDef || !isReferenceVideo(v)),
                            );
                            const addMany = async (ids: string[]) => {
                                if (!ids.length) return;
                                const next = [...draft, ...ids];
                                setAddQueue({ plId: cur.id, items: next }); // 낙관적 — 다음 avail 에서 제외
                                const ok = isDef
                                    ? await addToDefault(cur.id, ids)
                                    : await persistPlaylist(cur.id, next);
                                if (ok) {
                                    cmd("SYNC_PLAYLIST", cur.id); // 재생 중인 큐에 무중단 반영
                                    showToast(
                                        tp("toast.addedToPlaylistN", { count: ids.length }),
                                        <I.check
                                            size={16}
                                            style={{ color: "var(--live)" }}
                                        />,
                                    );
                                } else {
                                    setAddQueue((s) =>
                                        s
                                            ? {
                                                  ...s,
                                                  items: s.items.filter(
                                                      (x) => !ids.includes(x),
                                                  ),
                                              }
                                            : s,
                                    ); // 실패 롤백
                                    persistFailToast(
                                        tp("toast.addFailedRetry"),
                                    );
                                }
                            };
                            return (
                                <div
                                    style={{
                                        position: "absolute",
                                        inset: 0,
                                        zIndex: 60,
                                    }}
                                >
                                    <AddVideoSheet
                                        avail={avail}
                                        onAddMany={addMany}
                                        mineOnly={isDef}
                                        onClose={() => setAddQueue(null)}
                                    />
                                </div>
                            );
                        })()}
                        {sheet && selDevice && (
                            <LoadSheet
                                device={selDevice}
                                onPick={(id) => {
                                    cmd("LOAD_PLAYLIST", id);
                                    pushRecent({
                                        kind: "playlist",
                                        id,
                                        label: plById[id]?.name ?? tp("fallback.playlist"),
                                    });
                                }}
                                onClose={() => setSheet(false)}
                                onCreate={() => { setSheet(false); createPlaylist("control"); }}
                                masterForms={masterForms}
                                onPlayMaster={(v) => {
                                    loadMaster(v, false);
                                    pushRecent({
                                        kind: "master",
                                        video: v,
                                        label: v.title,
                                    });
                                    setSheet(false);
                                }}
                                onPlaySetMaster={(plId) => {
                                    playMasterSet(plId, false);
                                    pushRecent({
                                        kind: "playlist",
                                        id: plId,
                                        label: plById[plId]?.name ?? tp("fallback.set"),
                                    });
                                    setSheet(false);
                                }}
                                onPlayMachiSetMaster={(
                                    videos,
                                    title,
                                    index,
                                    practice,
                                ) => {
                                    loadMasterSet(videos, title, index, false, practice);
                                    pushRecent({
                                        kind: "masterset",
                                        videos,
                                        title,
                                        index: index ?? 0,
                                        label:
                                            (index ?? 0) > 0
                                                ? `${title} (${(index ?? 0) + 1}~)`
                                                : title,
                                    });
                                    setSheet(false);
                                }}
                                onPickVideo={(v) => {
                                    const mv = toMasterPayload(v);
                                    loadMaster(mv, false);
                                    pushRecent({
                                        kind: "master",
                                        video: mv,
                                        label: v.title,
                                    });
                                    setSheet(false);
                                }}
                                recentCasts={recentCasts}
                                onPickRecent={(r) => {
                                    replayRecent(r, false);
                                    setSheet(false);
                                }}
                            />
                        )}
                        {voiceOpen && (
                            <VoiceSheet onRun={runVoice} onClose={() => setVoiceOpen(false)} dojangId={dojangIdRef.current} />
                        )}
                        {batchSheet && (
                            <LoadSheet
                                headerSub={tp("misc.selectedN", { count: selIds.size })}
                                onPick={(id) => {
                                    batchLoad(id);
                                    pushRecent({
                                        kind: "playlist",
                                        id,
                                        label: plById[id]?.name ?? tp("fallback.playlist"),
                                    });
                                }}
                                onClose={() => setBatchSheet(false)}
                                onCreate={() => { setBatchSheet(false); createPlaylist("control"); }}
                                masterForms={masterForms}
                                onPlayMaster={(v) => {
                                    loadMaster(v, true);
                                    pushRecent({
                                        kind: "master",
                                        video: v,
                                        label: v.title,
                                    });
                                    setBatchSheet(false);
                                }}
                                onPlaySetMaster={(plId) => {
                                    playMasterSet(plId, true);
                                    pushRecent({
                                        kind: "playlist",
                                        id: plId,
                                        label: plById[plId]?.name ?? tp("fallback.set"),
                                    });
                                    setBatchSheet(false);
                                }}
                                onPlayMachiSetMaster={(
                                    videos,
                                    title,
                                    index,
                                    practice,
                                ) => {
                                    loadMasterSet(videos, title, index, true, practice);
                                    pushRecent({
                                        kind: "masterset",
                                        videos,
                                        title,
                                        index: index ?? 0,
                                        label:
                                            (index ?? 0) > 0
                                                ? `${title} (${(index ?? 0) + 1}~)`
                                                : title,
                                    });
                                    setBatchSheet(false);
                                }}
                                onPickVideo={(v) => {
                                    const mv = toMasterPayload(v);
                                    loadMaster(mv, true);
                                    pushRecent({
                                        kind: "master",
                                        video: mv,
                                        label: v.title,
                                    });
                                    setBatchSheet(false);
                                }}
                                recentCasts={recentCasts}
                                onPickRecent={(r) => {
                                    replayRecent(r, true);
                                    setBatchSheet(false);
                                }}
                            />
                        )}
                        {modal?.kind === "action" && (
                            <ActionSheet
                                title={modal.title}
                                subtitle={modal.subtitle}
                                actions={modal.actions}
                                sections={modal.sections}
                                onClose={closeModal}
                            />
                        )}
                        {modal?.kind === "notice" && (
                            <NoticeSheet
                                title={modal.title}
                                message={modal.message}
                                confirmLabel={tp("action.ok")}
                                onClose={closeModal}
                            />
                        )}
                        {modal?.kind === "prompt" && (
                            <PromptSheet
                                title={modal.title}
                                label={modal.label}
                                placeholder={modal.placeholder}
                                initial={modal.initial}
                                confirmLabel={modal.confirmLabel}
                                password={modal.password}
                                danger={modal.danger}
                                onSubmit={modal.onSubmit}
                                onClose={closeModal}
                            />
                        )}
                        {modal?.kind === "confirm" && (
                            <ConfirmSheet
                                title={modal.title}
                                message={modal.message}
                                confirmLabel={modal.confirmLabel}
                                danger={modal.danger}
                                onConfirm={modal.onConfirm}
                                onClose={closeModal}
                            />
                        )}
                        {modal?.kind === "phone" && (
                            <PhoneSheet
                                initial={modal.initial}
                                onSaved={(phone) => {
                                    setOwner((o) => ({ ...o, phone }));
                                    showToast(phone ? tp("toast.contactSaved") : tp("toast.contactRemoved"));
                                }}
                                onClose={closeModal}
                            />
                        )}
                        {toast && (
                            <Toast
                                icon={toast.icon}
                                leaving={toast.leaving}
                                alert={toast.sticky}
                                title={toast.title}
                                // sticky(관장 쪽지)는 배경 탭으로 안 닫히고 '확인'으로만 닫힌다
                                onDismiss={toast.sticky ? undefined : dismissToast}
                                action={
                                    toast.action
                                        ? {
                                              label: toast.action.label,
                                              onClick: () => {
                                                  toast.action!.onClick();
                                                  dismissToast();
                                              },
                                          }
                                        : undefined
                                }
                            >
                                {toast.msg}
                            </Toast>
                        )}
                        {/* 내 카메라 전송 — 보기와 **동시에** 뜰 수 있다. 같은 기기면 이 연결 하나가
                            양방향으로 열려 보기 창이 여기서 영상을 빌려 간다(기기는 연결 1개만 유지). */}
                        {owner.camera && camConnIds.length > 0 && (
                            <PresenterCast
                                // 🛑 key 에 기기 목록을 넣지 않는다 — 한 대를 빼거나 더할 때마다 창이 통째로
                                //    다시 마운트돼 **카메라가 껐다 켜진다**(안드로이드는 그때 안 열리곤 한다).
                                //    창은 그대로 두고 자식(연결)만 늘고 주는 게 맞다.
                                key={castEpoch}
                                sock={sockRef.current}
                                devices={camCastDevices}
                                alsoReceiveIds={bothIds}
                                listenIds={camListen}
                                // 창은 접혔고 연결만 붙들고 있는 상태 — 화면도 카메라도 없다.
                                holdOnly={holdOnly}
                                // 여러 대에 보낼 땐 하나라도 애플이면 안 쓴다(안전 쪽으로) — 보기 창과 같은 규칙.
                                h264={camConnIds.every((id) => h264For(id))}
                                onRemote={onCamRemote}
                                onNeedRemount={() => { setCamHold([]); setCastEpoch((e) => e + 1); }}
                                onCloseOne={closeCastOne}
                                onClose={() => {
                                    // 같은 기기에 보기가 떠 있으면 그 연결은 붙들어 둔다(보기가 다시 짓느라 꺼지지 않게).
                                    setCamHold(camSend.filter((id) => camView.includes(id)));
                                    setCamSend([]);
                                }}
                            />
                        )}
                        {/* 실시간 보기 — 한 대든 여러 대든 여기서 한 번만 그린다(그리드·제어화면 어디서 열든 유지) */}
                        {owner.camera && camViewDevices.length > 0 && (
                            <CameraViewer
                                sock={sockRef.current}
                                devices={camViewDevices}
                                // 여러 대를 볼 땐 하나라도 애플이면 안 쓴다(안전 쪽으로).
                                h264={camViewDevices.every((d) => h264For(d.id))}
                                // 전송 창이 같이 떠 있으면 보기 창이 그 위로 비켜준다(겹치면 자세를 못 본다)
                                castOpen={camSend.length > 0}
                                // 같은 기기에 전송도 떠 있으면 그쪽 연결에서 영상을 빌려 온다(기기는 연결 1개만).
                                borrowed={camBorrowed}
                                onListen={onCamListen}
                                onClose={() => { setCamView([]); setCamHold([]); }}
                                onCloseOne={closeCamTile}
                            />
                        )}
                        <DiagOverlay />
                    </div>
                </div>
            </DataProvider>
        </div>
    );
}

/* 가로 2단에서 기기 미선택 시 오른쪽 패널 — Mirror의 !np 빈 상태(control-screen.tsx)와 톤 통일 */
function EmptyDetail() {
    const tp = useTranslations("control.page");
    return (
        <div
            className="screen col"
            style={{
                height: "100%",
                alignItems: "center",
                justifyContent: "center",
                gap: 14,
                color: "var(--text-3)",
            }}
        >
            <I.monitor size={48} sw={1.5} />
            <div style={{ fontSize: 15, fontWeight: 600 }}>
                {tp("empty.selectDevice")}
            </div>
            <div className="muted" style={{ fontSize: 13 }}>
                {tp("empty.selectDeviceDesc")}
            </div>
        </div>
    );
}
